import React, { useState, useEffect, useRef } from 'react';
import { io as socketIo } from 'socket.io-client';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Paper,
  Divider,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  Alert,
  RadioGroup,
  Radio,
  FormLabel,
  LinearProgress,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Menu,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  AccessTime,
  PlayArrow,
  Label as LabelIcon,
  GridView,
  List as ListIcon,
  Settings as SettingsIcon,
  ContentCopy,
  MusicNote as MusicNoteIcon,
  Radio as RadioIcon,
  MoreHoriz as MoreHorizIcon,
  Headphones as HeadphonesIcon,
  QueueMusic as QueueMusicIcon,
  ExpandMore,
  ExpandLess,
  DonutLarge as DonutLargeIcon,
  Schedule as ScheduleIcon,
  WarningAmber,
  DragIndicator,
  Repeat as RepeatIcon,
  MoreVert as MoreVertIcon,
  BookmarkAdd as BookmarkAddIcon,
} from '@mui/icons-material';
import PageHeader from '../components/PageHeader';
import Collapse from '@mui/material/Collapse';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import MixblockBuilder from '../components/MixblockBuilder';
import ClockWheel from '../components/ClockWheel';
import AutoGenerationSchedules from '../components/AutoGenerationSchedules';
import ExternalSchedulerSettings from '../components/ExternalSchedulerSettings';
import {
  GRID_DAYPARTS,
  areAllGridSlotsSelected,
  mergeGridSlots,
  slotsInRectangle,
  toggleGridSlotGroup,
} from '../utils/scheduleGridSelection';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;
const formatLocalDate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const parseJsonObject = (value, fallback = {}) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value !== 'string' || !value.trim()) return fallback;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
};

export default function Clocks() {
  const { user, isSuperAdmin, isAdmin, hasSystemFeature, getModulePermission } = useAuth();
  const canManageExternalScheduler = typeof isAdmin === 'function'
    ? isAdmin()
    : (user?.role === 'admin' || user?.role === 'superadmin');
  const canEditLogs = isSuperAdmin
    || user?.role === 'admin'
    || getModulePermission('clocks')?.can_edit === true
    || getModulePermission('playlists')?.can_create === true;
  const [tabValue, setTabValue] = useState(0); // 0: Clocks, 1: Categories, 2: Scheduling Settings, 3: Mixblocks
  const [beatmixEnabled, setBeatmixEnabled] = useState(false);
  
  // Clock-related state
  const [clockTemplates, setClockTemplates] = useState([]);
  const [clockView, setClockView] = useState('list'); // 'list' or 'grid'
  const [selectedStation, setSelectedStation] = useState('');
  const [clockEditorOpen, setClockEditorOpen] = useState(false);
  const [editingClock, setEditingClock] = useState(null);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [clockToClone, setClockToClone] = useState(null);
  const [cloneClockName, setCloneClockName] = useState('');
  const [clockFormData, setClockFormData] = useState({
    name: '',
    description: '',
    color: '#1976D2',
    station_id: null,
    use_default_rule_set: true,
    custom_rule_set_id: null,
    category_goals: {}, // { category_id: { min: 0, max: 10, target: 5 } }
    autofade_enabled: false,
    auto_mode: false,
    live_assist: false,
    voicetrack_collaboration_enabled: true,
  });
  const [clockItems, setClockItems] = useState([]);
  const [ruleSets, setRuleSets] = useState([]);
  const [schedulingRules, setSchedulingRules] = useState([]);
  const [ruleTypes, setRuleTypes] = useState([]);
  const [selectedRuleSet, setSelectedRuleSet] = useState(null);
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [schedulingSettingsOpen, setSchedulingSettingsOpen] = useState(false);
  const [ruleSetDialogOpen, setRuleSetDialogOpen] = useState(false);
  const [editingRuleSet, setEditingRuleSet] = useState(null);
  const [ruleSetFormData, setRuleSetFormData] = useState({
    name: '',
    description: '',
    station_id: null,
  });
  const [stations, setStations] = useState([]);
  const [contributionAccounts, setContributionAccounts] = useState([]);
  const [playlistDialogOpen, setPlaylistDialogOpen] = useState(false);
  const [playlistFormData, setPlaylistFormData] = useState({
    station_id: '',
    date: formatLocalDate(),
    end_date: formatLocalDate(),
    start_hour: 0,
    end_hour: 23,
    generate_range: false,
  });
  const [playlistGenerating, setPlaylistGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState({ percent: 0, message: '' });
  const [playlistOverwriteWarningOpen, setPlaylistOverwriteWarningOpen] = useState(false);
  const [playlistOverwriteWarning, setPlaylistOverwriteWarning] = useState(null);
  const [pendingPlaylistGeneration, setPendingPlaylistGeneration] = useState(null);
  const [autoPlaylistSchedules, setAutoPlaylistSchedules] = useState([]);
  const [autoScheduleWarningOpen, setAutoScheduleWarningOpen] = useState(false);
  const autoPlaylistSchedulesRequestRef = useRef(null);
  const autoPlaylistSchedulesLoadedRef = useRef(false);
  const socketRef = useRef(null);
  const clockItemsLoadRef = useRef(0);
  const editingClockIdRef = useRef(null);
  const [schedulingSettings, setSchedulingSettings] = useState({
    default_rule_set_id: null,
    power_intro_enabled: false,
  });
  const [stationPowerIntroSettings, setStationPowerIntroSettings] = useState({});
  const [ruleFormData, setRuleFormData] = useState({
    rule_set_id: null,
    rule_type: '',
    rule_name: '',
    enabled: true,
    breakable: false,
    scope: 'all',
    scope_value: null,
    settings: {
      // Separation rules
      days: 0,
      hours: 0,
      minutes: 0,
      // Rotation rules
      unique_hours: 0,
      unique_dayparts: 0,
      // Flow/Adjacent rules
      value1: '',
      value2: '',
      max_in_row: 0,
      min_in_row: 0,
      // Yesterday same hour
      threshold_hours: 0,
      threshold_minutes: 0,
      use_exact_time: false,
      // BPM restriction
      min_bpm: '',
      max_bpm: '',
      days_of_week: [],
      start_time: '',
      end_time: '',
      require_detected_bpm: true,
    },
  });
  const [assets, setAssets] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const [commercialBreaks, setCommercialBreaks] = useState([]);
  const [clockDialogTab, setClockDialogTab] = useState(0); // 0: Basic Info, 1: Clock Items
  const [clockItemsView, setClockItemsView] = useState('list'); // 'list' | 'wheel'
  const [editingClockItem, setEditingClockItem] = useState(null);
  const [clockItemDialogOpen, setClockItemDialogOpen] = useState(false);
  const [clockItemFormData, setClockItemFormData] = useState({
    type: 'category',
    selection_mode: 'random', // 'specific' or 'random'
    asset_id: null,
    category_id: null,
    commercial_break_id: null,
    mixblock_id: null,
    duration: null,
    play_as: 'normal',
    droppable: true,
    rule_set_id: null,
    use_category_goals: false,
    // Soft Time Marker fields
    soft_minute: null,
    soft_second: null,
    soft_boundary_seconds: null,
  });
  const [mixblockDefinitions, setMixblockDefinitions] = useState([]);
  
  // Grid view state
  const [selectedSlots, setSelectedSlots] = useState([]); // [{day_of_week, hour}, ...] where day_of_week is 0-6 (Sunday-Saturday)
  const [schedules, setSchedules] = useState([]); // Existing schedules
  const [assignClockDialogOpen, setAssignClockDialogOpen] = useState(false);
  const [selectedClockForAssign, setSelectedClockForAssign] = useState('');
  const [assignMode, setAssignMode] = useState('clock'); // 'clock' | 'repeat'
  const [repeatFromDay, setRepeatFromDay] = useState(1);
  const [repeatFromHour, setRepeatFromHour] = useState(10);
  const [gridDensity, setGridDensity] = useState('readable');
  const [gridSelecting, setGridSelecting] = useState(false);
  const selectedSlotsRef = useRef([]);
  const gridDragRef = useRef(null);
  const gridSelectHelpersRef = useRef({ isFranchiseSlotAllowed: () => true });
  selectedSlotsRef.current = selectedSlots;
  const [scheduleGrids, setScheduleGrids] = useState([]);
  const [scheduleGridMenuAnchor, setScheduleGridMenuAnchor] = useState(null);
  const [scheduleGridDialog, setScheduleGridDialog] = useState(null);
  const scheduleGridsRequestRef = useRef(0);
  
  // Regional schedule state
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(''); // '' = master view, number = region id
  const [regionalSchedules, setRegionalSchedules] = useState([]); // Regional overrides
  const [assignFollowMaster, setAssignFollowMaster] = useState(true); // For region assign dialog
  const [syndicationLinks, setSyndicationLinks] = useState([]);
  const [syndicationOverrides, setSyndicationOverrides] = useState([]);
  const [syndicatedClockIds, setSyndicatedClockIds] = useState([]);
  const [syndicationRolloutStage, setSyndicationRolloutStage] = useState(0);
  
  // Category-related state
  const [categories, setCategories] = useState([]);
  const [selectedCategoryStation, setSelectedCategoryStation] = useState('');
  const [categoryViewMode, setCategoryViewMode] = useState('grid'); // 'grid' | 'list'
  const [categoryOrganizeMode, setCategoryOrganizeMode] = useState('all'); // 'all' | 'by_type'
  const [categoryTypeFilter, setCategoryTypeFilter] = useState(''); // '' | 'music' | 'station_id' | 'music_bed' | 'other'
  const [clockItemCategoryTypeFilter, setClockItemCategoryTypeFilter] = useState(''); // for category picker in clock item dialog
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryError, setCategoryError] = useState('');
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    color: '#1976D2',
    description: '',
    station_id: null,
    station_ids: [],
    ai_insights_enabled: false,
    metadata_enabled: true,
    artwork_enabled: false,
    category_type: 'other',
    parent_category_id: null,
  });
  // Music Beds subcategory management
  const [expandedMusicBeds, setExpandedMusicBeds] = useState(false);
  const [musicBedSubcategories, setMusicBedSubcategories] = useState([]);
  
  // AI Voicetrack state (for clock item form)
  const [aiVoices, setAiVoices] = useState([]);
  const [aiPrompts, setAiPrompts] = useState([]);
  const [subCategoryDialogOpen, setSubCategoryDialogOpen] = useState(false);
  const [editingSubCategory, setEditingSubCategory] = useState(null);
  const [subCategoryFormData, setSubCategoryFormData] = useState({
    name: '',
    color: '#ec4899',
    description: '',
  });

  // Socket.io connection for generation progress events
  useEffect(() => {
    const socket = socketIo(API_URL);
    socketRef.current = socket;

    socket.on('generation:progress', (data) => {
      if (data.type === 'playlist') {
        setGenerationProgress({ percent: data.percent || 0, message: data.message || '' });
      }
    });

    return () => {
      socket.close();
      socketRef.current = null;
    };
  }, []);

  const fetchMixblockDefinitions = async (stationId = null) => {
    try {
      const effectiveStation = stationId || selectedStation;
      const params = effectiveStation ? { station_id: effectiveStation } : {};
      const res = await axios.get(`${API_URL}/api/beatmix/mixblocks`, { params, withCredentials: true });
      setMixblockDefinitions(res.data || []);
    } catch (err) {
      // Mixblocks may not be enabled
    }
  };

  useEffect(() => {
    fetchClockTemplates();
    fetchCategories();
    fetchStations();
    fetchRuleSets();
    fetchAssets();
    fetchCommercialBreaks();
    fetchRuleTypes();
    fetchSchedulingSettings();
    fetchAiVoices();
    fetchAiPrompts();
    fetchAutoPlaylistSchedules();
    axios.get(`${API_URL}/api/webrtc-contributors/options`, { withCredentials: true })
      .then((res) => setContributionAccounts(Array.isArray(res.data) ? res.data : []))
      .catch(() => setContributionAccounts([]));
  }, []);

  useEffect(() => {
    if (user?.stationId) {
      fetchRuleSets(user.stationId);
      fetchAssets(user.stationId);
      fetchCommercialBreaks(user.stationId);
    }
  }, [user?.stationId]);

  useEffect(() => {
    fetchMixblockDefinitions();
  }, [selectedStation]);

  useEffect(() => {
    if (stations.length > 0) {
      if (selectedStation) {
        const station = stations.find(s => String(s.id) === String(selectedStation));
        setBeatmixEnabled(station?.beatmix_enabled || false);
      } else {
        setBeatmixEnabled(stations.some(s => s.beatmix_enabled));
      }
    } else {
      setBeatmixEnabled(false);
    }
  }, [stations, selectedStation]);

  useEffect(() => {
    setScheduleGridDialog(null);
    setScheduleGridMenuAnchor(null);
    if (selectedStation) {
      setScheduleGrids([]);
      fetchSchedules();
      if (clockView === 'grid') {
        fetchScheduleGrids();
      }
    } else {
      setSchedules([]);
      setScheduleGrids([]);
    }
  }, [selectedStation, clockView]);

  // Station change filters the clock list; close an open clock that no longer belongs to the selection.
  useEffect(() => {
    if (!clockEditorOpen) return;
    if (!editingClock) {
      setClockFormData((prev) => {
        const nextStationId = selectedStation || null;
        return String(prev.station_id || '') === String(nextStationId || '')
          ? prev
          : { ...prev, station_id: nextStationId };
      });
      return;
    }
    if (selectedStation && String(editingClock.station_id || '') !== String(selectedStation)) {
      clockItemsLoadRef.current += 1;
      editingClockIdRef.current = null;
      setClockEditorOpen(false);
      setEditingClock(null);
      setClockItems([]);
      setClockDialogTab(0);
    }
  }, [selectedStation]);

  useEffect(() => {
    if (selectedStation && isSuperAdmin()) {
      fetchSyndicationState();
    } else {
      setSyndicationLinks([]);
      setSyndicationOverrides([]);
      setSyndicatedClockIds([]);
      setSyndicationRolloutStage(0);
    }
  }, [selectedStation]);

  // Fetch regions when station changes
  useEffect(() => {
    if (selectedStation) {
      fetchRegions();
    } else {
      setRegions([]);
      setSelectedRegion('');
    }
  }, [selectedStation]);

  // Fetch regional schedules when region selection changes
  useEffect(() => {
    if (selectedRegion && selectedStation) {
      fetchRegionalSchedules();
    } else {
      setRegionalSchedules([]);
    }
  }, [selectedRegion, selectedStation]);

  useEffect(() => {
    setSelectedClockForAssign('');
    setAssignFollowMaster(true);
  }, [selectedStation, selectedRegion]);

  useEffect(() => {
    if (!categories.length) {
      setMusicBedSubcategories([]);
      return;
    }
    fetchMusicBedSubcategories(categories);
  }, [selectedStation, categories]);

  // When opening Generate Playlist dialog, pre-fill station: single mode = the one station; multistation = header selection
  useEffect(() => {
    if (!playlistDialogOpen || !stations.length) return;
    if (stations.length === 1) {
      setPlaylistFormData((prev) => ({ ...prev, station_id: stations[0].id }));
    } else if (selectedStation) {
      setPlaylistFormData((prev) => ({ ...prev, station_id: selectedStation }));
    }
  }, [playlistDialogOpen, stations, selectedStation]);

  const fetchClockTemplates = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/clocks`, {
        withCredentials: true
      });
      const list = res.data || [];
      setClockTemplates(list);
      return list;
    } catch (error) {
      console.error('Error fetching clock templates:', error);
      setClockTemplates([]);
      return [];
    }
  };

  const fetchSchedules = async () => {
    if (!selectedStation) return;
    
    try {
      // Fetch weekly schedules (day_of_week based)
      const res = await axios.get(`${API_URL}/api/schedules/weekly`, {
        params: {
          station_id: selectedStation
        },
        withCredentials: true
      });
      
      setSchedules(res.data || []);
    } catch (error) {
      console.error('Error fetching schedules:', error);
      setSchedules([]);
    }
  };

  const fetchScheduleGrids = async () => {
    if (!selectedStation) {
      setScheduleGrids([]);
      return [];
    }
    const stationId = selectedStation;
    const requestId = ++scheduleGridsRequestRef.current;
    try {
      const res = await axios.get(`${API_URL}/api/schedules/weekly/grids`, {
        params: { station_id: stationId },
        withCredentials: true,
      });
      if (requestId !== scheduleGridsRequestRef.current) return [];
      const grids = res.data || [];
      setScheduleGrids(grids);
      return grids;
    } catch (error) {
      if (requestId !== scheduleGridsRequestRef.current) return [];
      console.error('Error fetching saved grids:', error);
      setScheduleGrids([]);
      return [];
    }
  };

  const activeScheduleGrid = scheduleGrids.find((grid) => (
    grid.is_active && String(grid.station_id) === String(selectedStation)
  )) || null;
  const gridBelongsToSelectedStation = (grid) => (
    grid && selectedStation && String(grid.station_id) === String(selectedStation)
  );

  const closeScheduleGridMenu = () => setScheduleGridMenuAnchor(null);

  const handleSelectScheduleGrid = async (gridId) => {
    if (!gridId || String(gridId) === String(activeScheduleGrid?.id)) return;
    const nextGrid = scheduleGrids.find((grid) => String(grid.id) === String(gridId));
    if (!gridBelongsToSelectedStation(nextGrid)) return;
    if (!window.confirm(`Load "${nextGrid.name}"? Playlist generation will use this layout until you switch again.`)) {
      return;
    }
    try {
      await axios.post(
        `${API_URL}/api/schedules/weekly/grids/${gridId}/activate`,
        { station_id: selectedStation },
        { withCredentials: true }
      );
      await Promise.all([fetchScheduleGrids(), fetchSchedules(), fetchRegionalSchedules()]);
    } catch (error) {
      console.error('Error loading grid:', error);
      alert('Failed to load grid: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleOpenScheduleGridDialog = (mode) => {
    closeScheduleGridMenu();
    const currentName = activeScheduleGrid?.name || 'Default';
    const defaultName = mode === 'rename'
      ? currentName
      : mode === 'empty'
        ? 'New grid'
        : `Copy of ${currentName}`;
    setScheduleGridDialog({ mode, name: defaultName });
  };

  const handleSubmitScheduleGridDialog = async () => {
    if (!scheduleGridDialog || !selectedStation) return;
    const name = String(scheduleGridDialog.name || '').trim();
    if (!name) {
      alert('Please enter a grid name');
      return;
    }
    try {
      if (scheduleGridDialog.mode === 'rename') {
        if (!gridBelongsToSelectedStation(activeScheduleGrid)) return;
        await axios.patch(
          `${API_URL}/api/schedules/weekly/grids/${activeScheduleGrid.id}`,
          { name, station_id: selectedStation },
          { withCredentials: true }
        );
      } else {
        if (scheduleGridDialog.mode === 'empty') {
          if (!window.confirm('Start a blank grid? This replaces the live weekly schedule until you load another saved grid.')) {
            return;
          }
        }
        await axios.post(
          `${API_URL}/api/schedules/weekly/grids`,
          {
            station_id: selectedStation,
            name,
            empty: scheduleGridDialog.mode === 'empty',
            activate: true,
          },
          { withCredentials: true }
        );
      }
      setScheduleGridDialog(null);
      await Promise.all([fetchScheduleGrids(), fetchSchedules(), fetchRegionalSchedules()]);
    } catch (error) {
      console.error('Error saving grid:', error);
      alert('Failed to save grid: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteScheduleGrid = async () => {
    closeScheduleGridMenu();
    const otherGrid = scheduleGrids.find((grid) => !grid.is_active && gridBelongsToSelectedStation(grid));
    if (!gridBelongsToSelectedStation(activeScheduleGrid) || !otherGrid) {
      alert('Keep at least one saved grid for this station.');
      return;
    }
    if (!window.confirm(`Delete "${activeScheduleGrid.name}"? "${otherGrid.name}" will become the live grid.`)) {
      return;
    }
    try {
      await axios.post(
        `${API_URL}/api/schedules/weekly/grids/${otherGrid.id}/activate`,
        { station_id: selectedStation },
        { withCredentials: true }
      );
      await axios.delete(
        `${API_URL}/api/schedules/weekly/grids/${activeScheduleGrid.id}`,
        { data: { station_id: selectedStation }, withCredentials: true }
      );
      await Promise.all([fetchScheduleGrids(), fetchSchedules(), fetchRegionalSchedules()]);
    } catch (error) {
      console.error('Error deleting grid:', error);
      alert('Failed to delete grid: ' + (error.response?.data?.message || error.message));
    }
  };

  const fetchSyndicationState = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/syndication/stations/${selectedStation}/ownership`,
        { withCredentials: true }
      );
      setSyndicationRolloutStage(Number(response.data?.rolloutStage || 0));
      if (!response.data?.linked) {
        setSyndicationLinks([]);
        setSyndicationOverrides([]);
        setSyndicatedClockIds([]);
        return;
      }
      setSyndicationLinks([response.data.link]);
      setSyndicationOverrides(response.data.overrides || []);
      setSyndicatedClockIds((response.data.clockIds || []).map(String));
    } catch (error) {
      if (![403, 404].includes(error.response?.status)) {
        console.error('Error fetching franchise syndication state:', error);
      }
      setSyndicationLinks([]);
      setSyndicationOverrides([]);
      setSyndicatedClockIds([]);
      setSyndicationRolloutStage(0);
    }
  };

  const fetchRegions = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/regions`, {
        params: { station_id: selectedStation },
        withCredentials: true
      });
      setRegions((res.data || []).filter(r => r.is_active));
    } catch (error) {
      console.error('Error fetching regions:', error);
      setRegions([]);
    }
  };

  const fetchRegionalSchedules = async () => {
    if (!selectedStation || !selectedRegion) return;
    try {
      const res = await axios.get(`${API_URL}/api/schedules/weekly/regional`, {
        params: { station_id: selectedStation, region_id: selectedRegion },
        withCredentials: true
      });
      setRegionalSchedules(res.data || []);
    } catch (error) {
      console.error('Error fetching regional schedules:', error);
      setRegionalSchedules([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/categories`, {
        params: { top_level_only: 'true' },
        withCredentials: true
      });
      setCategories(res.data);
      // Also fetch music bed subcategories
      fetchMusicBedSubcategories(res.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchMusicBedSubcategories = async (cats) => {
    const allCats = cats || categories;
    const musicBedsCat = allCats.find(c => c.name === 'Music Beds' || c.category_type === 'music_bed');
    if (!musicBedsCat) return;
    try {
      const res = await axios.get(`${API_URL}/api/categories/${musicBedsCat.id}/subcategories`, {
        params: selectedStation ? { station_id: selectedStation } : {},
        withCredentials: true
      });
      setMusicBedSubcategories(res.data || []);
    } catch (error) {
      console.error('Error fetching music bed subcategories:', error);
    }
  };

  const fetchAutoPlaylistSchedules = () => {
    if (autoPlaylistSchedulesRequestRef.current) {
      return autoPlaylistSchedulesRequestRef.current;
    }
    const request = axios
      .get(`${API_URL}/api/auto-generation-schedules`, {
        params: { job_type: 'playlist' },
        withCredentials: true,
      })
      .then((res) => {
        const list = Array.isArray(res.data?.schedules) ? res.data.schedules : [];
        setAutoPlaylistSchedules(list);
        autoPlaylistSchedulesLoadedRef.current = true;
        return list;
      })
      .catch((error) => {
        console.error('Error fetching auto-generation schedules:', error);
        autoPlaylistSchedulesLoadedRef.current = true;
        return null;
      })
      .finally(() => {
        autoPlaylistSchedulesRequestRef.current = null;
      });
    autoPlaylistSchedulesRequestRef.current = request;
    return request;
  };

  const handleAutoPlaylistSchedulesChange = (list) => {
    setAutoPlaylistSchedules(Array.isArray(list) ? list : []);
    autoPlaylistSchedulesLoadedRef.current = true;
  };

  const fetchStations = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/stations`, {
        withCredentials: true
      });
      const list = res.data || [];
      setStations(list);
      // Populate power intro settings from each station's settings JSONB
      const powerIntroByStation = {};
      list.forEach((s) => {
        const settings = s.settings && typeof s.settings === 'object' ? s.settings : (typeof s.settings === 'string' ? (() => { try { return JSON.parse(s.settings); } catch { return {}; } })() : {});
        powerIntroByStation[s.id] = {
          power_intro_enabled: settings.power_intro_enabled || false,
          power_intro_suppression_categories: settings.power_intro_suppression_categories || [],
        };
      });
      setStationPowerIntroSettings(powerIntroByStation);
      // If only one station exists, auto-select it and keep selection hidden in UI
      if (list.length === 1) {
        setSelectedStation(list[0].id);
      } else if (!selectedStation && user?.stationId) {
        setSelectedStation(user.stationId);
      }
    } catch (error) {
      console.error('Error fetching stations:', error);
    }
  };

  const fetchRuleSets = async (stationId = null) => {
    try {
      const params = stationId ? { station_id: stationId } : {};
      const res = await axios.get(`${API_URL}/api/rule-sets`, {
        params,
        withCredentials: true
      });
      setRuleSets(res.data || []);
    } catch (error) {
      console.error('Error fetching rule sets:', error);
      setRuleSets([]);
    }
  };

  const fetchAssets = async (stationId = null) => {
    try {
      const params = stationId ? { station_id: stationId, playout_only: true } : { playout_only: true };
      const res = await axios.get(`${API_URL}/api/assets`, {
        params,
        withCredentials: true
      });
      setAssets(res.data || []);
    } catch (error) {
      console.error('Error fetching assets:', error);
      setAssets([]);
    }
  };

  const fetchCommercialBreaks = async (stationId = null) => {
    try {
      const params = stationId ? { station_id: stationId } : {};
      const res = await axios.get(`${API_URL}/api/advertising/breaks`, {
        params,
        withCredentials: true
      });
      setCommercialBreaks(res.data || []);
    } catch (error) {
      console.error('Error fetching commercial breaks:', error);
      setCommercialBreaks([]);
    }
  };

  const fetchAiVoices = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai/voices`, { withCredentials: true });
      setAiVoices(res.data?.voices || res.data || []);
    } catch (err) {
      console.error('Error fetching AI voices:', err);
    }
  };

  const fetchAiPrompts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai/voices/prompts`, { withCredentials: true });
      setAiPrompts(res.data || []);
    } catch (err) {
      console.error('Error fetching AI prompts:', err);
    }
  };

  const fetchRuleTypes = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/scheduling-rules/types`, {
        withCredentials: true
      });
      setRuleTypes(res.data || []);
    } catch (error) {
      console.error('Error fetching rule types:', error);
      setRuleTypes([]);
    }
  };

  const fetchSchedulingRules = async (ruleSetId) => {
    if (!ruleSetId) {
      setSchedulingRules([]);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/scheduling-rules`, {
        params: { rule_set_id: ruleSetId },
        withCredentials: true
      });
      setSchedulingRules(res.data || []);
    } catch (error) {
      console.error('Error fetching scheduling rules:', error);
      setSchedulingRules([]);
    }
  };

  const handleOpenRuleDialog = (rule = null, ruleSetId = null) => {
    if (rule) {
      setEditingRule(rule);
      // Parse settings if it's a string
      let parsedSettings = {};
      if (rule.settings) {
        if (typeof rule.settings === 'string') {
          try {
            parsedSettings = JSON.parse(rule.settings);
          } catch (e) {
            parsedSettings = {};
          }
        } else {
          parsedSettings = rule.settings;
        }
      }
      
      setRuleFormData({
        rule_set_id: rule.rule_set_id,
        rule_type: rule.rule_type,
        rule_name: rule.rule_name,
        enabled: rule.enabled !== false,
        breakable: rule.breakable === true,
        scope: rule.scope || 'all',
        scope_value: rule.scope_value || null,
        settings: {
          days: parsedSettings.days || 0,
          hours: parsedSettings.hours || 0,
          minutes: parsedSettings.minutes || 0,
          unique_hours: parsedSettings.unique_hours || 0,
          unique_dayparts: parsedSettings.unique_dayparts || 0,
          value1: parsedSettings.value1 || '',
          value2: parsedSettings.value2 || '',
          max_in_row: parsedSettings.max_in_row || 0,
          min_in_row: parsedSettings.min_in_row || 0,
          threshold_hours: parsedSettings.threshold_hours || 0,
          threshold_minutes: parsedSettings.threshold_minutes || 0,
          use_exact_time: parsedSettings.use_exact_time || false,
          min_bpm: parsedSettings.min_bpm ?? '',
          max_bpm: parsedSettings.max_bpm ?? '',
          days_of_week: Array.isArray(parsedSettings.days_of_week) ? parsedSettings.days_of_week : [],
          start_time: parsedSettings.start_time || '',
          end_time: parsedSettings.end_time || '',
          require_detected_bpm: parsedSettings.require_detected_bpm !== false,
        },
      });
    } else {
      setEditingRule(null);
      setRuleFormData({
        rule_set_id: ruleSetId || null,
        rule_type: '',
        rule_name: '',
        enabled: true,
        breakable: false,
        scope: 'all',
        scope_value: null,
        settings: {
          days: 0,
          hours: 0,
          minutes: 0,
          unique_hours: 0,
          unique_dayparts: 0,
          value1: '',
          value2: '',
          max_in_row: 0,
          min_in_row: 0,
          threshold_hours: 0,
          threshold_minutes: 0,
          use_exact_time: false,
          min_bpm: '',
          max_bpm: '',
          days_of_week: [],
          start_time: '',
          end_time: '',
          require_detected_bpm: true,
        },
      });
    }
    setRuleDialogOpen(true);
  };

  const handleCloseRuleDialog = () => {
    setRuleDialogOpen(false);
    setEditingRule(null);
  };

  const handleSaveRule = async () => {
    try {
      if (!ruleFormData.rule_set_id || !ruleFormData.rule_type || !ruleFormData.rule_name) {
        alert('Rule Set, Rule Type, and Rule Name are required');
        return;
      }

      // Ensure settings are properly formatted
      const payload = {
        ...ruleFormData,
        settings: ruleFormData.settings || {}
      };

      if (editingRule) {
        await axios.put(
          `${API_URL}/api/scheduling-rules/${editingRule.id}`,
          payload,
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${API_URL}/api/scheduling-rules`,
          payload,
          { withCredentials: true }
        );
      }

      handleCloseRuleDialog();
      if (selectedRuleSet) {
        fetchSchedulingRules(selectedRuleSet.id);
      }
    } catch (error) {
      console.error('Error saving rule:', error);
      alert('Failed to save rule: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/scheduling-rules/${ruleId}`, {
        withCredentials: true
      });
      if (selectedRuleSet) {
        fetchSchedulingRules(selectedRuleSet.id);
      }
    } catch (error) {
      console.error('Error deleting rule:', error);
      alert('Failed to delete rule: ' + (error.response?.data?.message || error.message));
    }
  };

  const fetchSchedulingSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/system-settings`, {
        withCredentials: true
      });
      setSchedulingSettings({
        default_rule_set_id: res.data.default_rule_set_id || null,
      });
    } catch (error) {
      console.error('Error fetching scheduling settings:', error);
    }
  };

  const handleSaveSchedulingSettings = async () => {
    try {
      await axios.post(
        `${API_URL}/api/system-settings`,
        {
          key: 'default_rule_set_id',
          value: schedulingSettings.default_rule_set_id || null,
          description: 'Default rule set to assign to new clocks'
        },
        { withCredentials: true }
      );
      setSchedulingSettingsOpen(false);
      alert('Scheduling settings saved successfully');
    } catch (error) {
      console.error('Error saving scheduling settings:', error);
      alert('Failed to save settings: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleSavePowerIntroSettings = async () => {
    const sid = selectedStation || (stations.length === 1 ? stations[0]?.id : null);
    if (!sid) {
      alert('Please select a station');
      return;
    }
    try {
      const current = stationPowerIntroSettings[sid] || {};
      const res = await axios.get(`${API_URL}/api/stations/${sid}`, { withCredentials: true });
      const existingSettings = res.data?.settings && typeof res.data.settings === 'object'
        ? res.data.settings
        : (typeof res.data?.settings === 'string' ? (() => { try { return JSON.parse(res.data.settings); } catch { return {}; } })() : {});
      const merged = { ...existingSettings, power_intro_enabled: current.power_intro_enabled || false, power_intro_suppression_categories: current.power_intro_suppression_categories || [] };
      await axios.put(`${API_URL}/api/stations/${sid}`, { settings: merged }, { withCredentials: true });
      fetchStations();
      alert('Power intro settings saved');
    } catch (error) {
      console.error('Error saving power intro settings:', error);
      alert('Failed to save: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleOpenRuleSetDialog = (ruleSet = null) => {
    if (ruleSet) {
      setEditingRuleSet(ruleSet);
      setRuleSetFormData({
        name: ruleSet.name || '',
        description: ruleSet.description || '',
        station_id: ruleSet.station_id || null,
      });
    } else {
      setEditingRuleSet(null);
      setRuleSetFormData({
        name: '',
        description: '',
        station_id: user?.stationId || null,
      });
    }
    setRuleSetDialogOpen(true);
  };

  const handleCloseRuleSetDialog = () => {
    setRuleSetDialogOpen(false);
    setEditingRuleSet(null);
  };

  const handleSaveRuleSet = async () => {
    try {
      if (!ruleSetFormData.name) {
        alert('Rule set name is required');
        return;
      }

      if (editingRuleSet) {
        await axios.put(
          `${API_URL}/api/rule-sets/${editingRuleSet.id}`,
          ruleSetFormData,
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${API_URL}/api/rule-sets`,
          ruleSetFormData,
          { withCredentials: true }
        );
      }

      handleCloseRuleSetDialog();
      fetchRuleSets();
      // If we edited the selected rule set, refresh it
      if (selectedRuleSet && editingRuleSet && selectedRuleSet.id === editingRuleSet.id) {
        const updated = await axios.get(`${API_URL}/api/rule-sets/${selectedRuleSet.id}`, { withCredentials: true });
        setSelectedRuleSet(updated.data);
      }
    } catch (error) {
      console.error('Error saving rule set:', error);
      alert('Failed to save rule set: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleOpenCategoryDialog = async (category = null) => {
    if (category) {
      // Fetch fresh category data from server to ensure we have the latest values
      try {
        console.log('[Clocks] Opening category dialog, fetching fresh data for category:', category.id);
        const freshCategoryRes = await axios.get(`${API_URL}/api/categories/${category.id}`, {
          withCredentials: true
        });
        const freshCategory = freshCategoryRes.data;
        
        console.log('[Clocks] Fresh category data from server:', {
          id: freshCategory.id,
          name: freshCategory.name,
          artwork_enabled: freshCategory.artwork_enabled,
          artwork_enabled_type: typeof freshCategory.artwork_enabled
        });
        
        setEditingCategory(freshCategory);
        // Ensure boolean values are properly converted (handle null, undefined, string 'true'/'false', etc.)
        const artworkEnabled = freshCategory.artwork_enabled === true || freshCategory.artwork_enabled === 'true' || freshCategory.artwork_enabled === 1 || freshCategory.artwork_enabled === 't';
        const aiInsightsEnabled = freshCategory.ai_insights_enabled === true || freshCategory.ai_insights_enabled === 'true' || freshCategory.ai_insights_enabled === 1 || freshCategory.ai_insights_enabled === 't';
        const metadataEnabled = freshCategory.metadata_enabled !== false && freshCategory.metadata_enabled !== 'false' && freshCategory.metadata_enabled !== 0 && freshCategory.metadata_enabled !== 'f';
        
        console.log('[Clocks] Loading category for edit (converted):', {
          id: freshCategory.id,
          name: freshCategory.name,
          artwork_enabled_raw: freshCategory.artwork_enabled,
          artwork_enabled_type: typeof freshCategory.artwork_enabled,
          artworkEnabled_converted: artworkEnabled
        });
        
        setCategoryFormData({
          name: freshCategory.name || '',
          color: freshCategory.color || '#1976D2',
          description: freshCategory.description || '',
          station_id: freshCategory.station_id || null,
          station_ids: Array.isArray(freshCategory.station_ids)
            ? freshCategory.station_ids.map((id) => String(id))
            : freshCategory.station_id
              ? [String(freshCategory.station_id)]
              : [],
          ai_insights_enabled: aiInsightsEnabled,
          metadata_enabled: metadataEnabled,
          artwork_enabled: artworkEnabled,
          category_type: freshCategory.category_type || 'other',
        });
      } catch (error) {
        console.error('[Clocks] Error fetching fresh category data, using provided category:', error);
        // Fallback to using the provided category if fetch fails
        setEditingCategory(category);
        const artworkEnabled = category.artwork_enabled === true || category.artwork_enabled === 'true' || category.artwork_enabled === 1 || category.artwork_enabled === 't';
        const aiInsightsEnabled = category.ai_insights_enabled === true || category.ai_insights_enabled === 'true' || category.ai_insights_enabled === 1 || category.ai_insights_enabled === 't';
        const metadataEnabled = category.metadata_enabled !== false && category.metadata_enabled !== 'false' && category.metadata_enabled !== 0 && category.metadata_enabled !== 'f';
        
        setCategoryFormData({
          name: category.name || '',
          color: category.color || '#1976D2',
          description: category.description || '',
          station_id: category.station_id || null,
          station_ids: Array.isArray(category.station_ids)
            ? category.station_ids.map((id) => String(id))
            : category.station_id
              ? [String(category.station_id)]
              : [],
          ai_insights_enabled: aiInsightsEnabled,
          metadata_enabled: metadataEnabled,
          artwork_enabled: artworkEnabled,
          category_type: category.category_type || 'other',
        });
      }
    } else {
      setEditingCategory(null);
      setCategoryFormData({
        name: '',
        color: '#1976D2',
        description: '',
        station_id: stations.length === 1 ? stations[0].id : null,
        station_ids: stations.length === 1 ? [String(stations[0].id)] : [],
        ai_insights_enabled: false,
        metadata_enabled: true,
        artwork_enabled: false,
        category_type: 'other',
      });
    }
    setCategoryDialogOpen(true);
    setCategoryError('');
  };

  const handleCloseCategoryDialog = () => {
    setCategoryDialogOpen(false);
    setEditingCategory(null);
    setCategoryError('');
  };

  const handleSaveCategory = async () => {
    try {
      setCategoryError('');
      
      if (!categoryFormData.name) {
        setCategoryError('Category name is required');
        return;
      }

      // Ensure boolean values are explicitly set (not undefined)
      const updateData = {
        ...categoryFormData,
        station_ids: Array.isArray(categoryFormData.station_ids)
          ? categoryFormData.station_ids.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0)
          : [],
        station_id: Array.isArray(categoryFormData.station_ids) && categoryFormData.station_ids.length === 1
          ? Number(categoryFormData.station_ids[0])
          : null,
        artwork_enabled: categoryFormData.artwork_enabled === true,
        ai_insights_enabled: hasSystemFeature('ai_insights')
          ? categoryFormData.ai_insights_enabled === true
          : !!editingCategory && categoryFormData.ai_insights_enabled === true,
        metadata_enabled: categoryFormData.metadata_enabled !== false,
        category_type: categoryFormData.category_type || 'other',
      };
      
      console.log('[Clocks] Saving category:', {
        id: editingCategory?.id,
        updateData,
        artwork_enabled: updateData.artwork_enabled,
        artwork_enabled_type: typeof updateData.artwork_enabled,
        fullCategoryFormData: categoryFormData
      });

      if (editingCategory) {
        try {
          const response = await axios.put(
            `${API_URL}/api/categories/${editingCategory.id}`,
            updateData,
            { withCredentials: true }
          );
          console.log('[Clocks] Category update response:', response.data);
          
          // Update the category in the local state immediately
          if (response.data) {
            setCategories(prevCategories => 
              prevCategories.map(cat => 
                cat.id === editingCategory.id ? response.data : cat
              )
            );
          }
          
          handleCloseCategoryDialog();
          // Still fetch to ensure we have the latest data
          await fetchCategories();
        } catch (putError) {
          console.error('[Clocks] PUT request error:', putError);
          console.error('[Clocks] Error response:', putError.response);
          console.error('[Clocks] Error response data:', putError.response?.data);
          console.error('[Clocks] Error response status:', putError.response?.status);
          
          // Build detailed error message
          let errorMessage = 'Failed to update category';
          if (putError.response?.data) {
            const errorData = putError.response.data;
            if (errorData.message) {
              errorMessage = errorData.message;
            }
            if (errorData.detail) {
              errorMessage += `: ${errorData.detail}`;
            }
            if (errorData.error) {
              errorMessage += ` (${errorData.error})`;
            }
          } else if (putError.message) {
            errorMessage = putError.message;
          }
          
          console.error('[Clocks] Setting error message:', errorMessage);
          setCategoryError(errorMessage);
          return; // Don't throw, just show error and return
        }
      } else {
        await axios.post(
          `${API_URL}/api/categories`,
          updateData,
          { withCredentials: true }
        );
        handleCloseCategoryDialog();
        await fetchCategories();
      }
    } catch (error) {
      console.error('[Clocks] Error saving category (outer catch):', error);
      console.error('[Clocks] Error details:', {
        message: error.message,
        response: error.response,
        responseData: error.response?.data,
        responseStatus: error.response?.status,
        responseHeaders: error.response?.headers
      });
      
      // Build detailed error message
      let errorMessage = 'Failed to save category';
      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.message) {
          errorMessage = errorData.message;
        }
        if (errorData.detail) {
          errorMessage += `: ${errorData.detail}`;
        }
        if (errorData.error) {
          errorMessage += ` (${errorData.error})`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      console.error('[Clocks] Setting error message:', errorMessage);
      setCategoryError(errorMessage);
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    // Prevent deletion of fixed categories (frontend check)
    const fixedCategories = ['Voicetrack', 'Commercial Spots', 'Music Beds'];
    if (fixedCategories.includes(categoryName)) {
      alert('Fixed categories cannot be deleted');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/categories/${categoryId}`, {
        withCredentials: true
      });
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete category';
      if (errorMessage.includes('Fixed categories cannot be deleted')) {
        alert('This category is protected and cannot be deleted');
      } else {
        alert(errorMessage);
      }
    }
  };

  // Music Beds subcategory handlers
  const handleOpenSubCategoryDialog = (subCategory = null) => {
    if (subCategory) {
      setEditingSubCategory(subCategory);
      setSubCategoryFormData({
        name: subCategory.name || '',
        color: subCategory.color || '#ec4899',
        description: subCategory.description || '',
      });
    } else {
      setEditingSubCategory(null);
      setSubCategoryFormData({
        name: '',
        color: '#ec4899',
        description: '',
      });
    }
    setSubCategoryDialogOpen(true);
  };

  const handleCloseSubCategoryDialog = () => {
    setSubCategoryDialogOpen(false);
    setEditingSubCategory(null);
  };

  const handleSaveSubCategory = async () => {
    try {
      if (!subCategoryFormData.name) {
        alert('Sub-category name is required');
        return;
      }
      const musicBedsCat = categories.find(c => c.name === 'Music Beds' || c.category_type === 'music_bed');
      if (!musicBedsCat) {
        alert('Music Beds category not found');
        return;
      }

      if (editingSubCategory) {
        await axios.put(
          `${API_URL}/api/categories/${editingSubCategory.id}`,
          { ...subCategoryFormData, parent_category_id: musicBedsCat.id, category_type: 'music_bed' },
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${API_URL}/api/categories`,
          { ...subCategoryFormData, parent_category_id: musicBedsCat.id, category_type: 'music_bed' },
          { withCredentials: true }
        );
      }

      handleCloseSubCategoryDialog();
      fetchCategories();
    } catch (error) {
      console.error('Error saving subcategory:', error);
      alert('Failed to save subcategory: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteSubCategory = async (subCategoryId) => {
    if (!window.confirm('Are you sure you want to delete this sub-category?')) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/categories/${subCategoryId}`, {
        withCredentials: true
      });
      fetchCategories();
    } catch (error) {
      console.error('Error deleting subcategory:', error);
      alert('Failed to delete subcategory: ' + (error.response?.data?.message || error.message));
    }
  };

  const applyClockItems = (clockId, items) => {
    const nextItems = items || [];
    if (clockId == null || String(editingClockIdRef.current) === String(clockId)) {
      setClockItems(nextItems);
    }
    if (!clockId) return;
    setClockTemplates((prev) => prev.map((c) => (
      String(c.id) === String(clockId) ? { ...c, item_count: nextItems.length } : c
    )));
  };

  const handleOpenClockDialog = async (clock = null, options = {}) => {
    if (clock && editingClock && clockEditorOpen && String(editingClock.id) === String(clock.id)) {
      return;
    }
    if (!clock && clockEditorOpen && !editingClock) {
      return;
    }
    const loadId = ++clockItemsLoadRef.current;
    editingClockIdRef.current = clock ? clock.id : null;
    setClockItems([]);
    setClockDialogTab(options.tab ?? (clock ? 1 : 0));
    setClockEditorOpen(true);
    setClockView('list');
    if (clock) {
      setEditingClock(clock);
      const settings = parseJsonObject(clock.settings);
      setClockFormData({
        name: clock.name || '',
        description: clock.description || '',
        color: clock.color || '#1976D2',
        station_id: clock.station_id || null,
        use_default_rule_set: clock.use_default_rule_set !== false,
        custom_rule_set_id: clock.custom_rule_set_id || null,
        category_goals: clock.category_goals || {},
        autofade_enabled: settings.autofade_enabled || false,
        auto_mode: settings.auto_mode || false,
        live_assist: settings.live_assist || false,
        // Inherit from the app-wide switch unless the clock explicitly opts out.
        voicetrack_collaboration_enabled: settings.voicetrack_collaboration_enabled !== false,
      });
      try {
        const res = await axios.get(`${API_URL}/api/clocks/${clock.id}/items`, {
          withCredentials: true
        });
        if (loadId !== clockItemsLoadRef.current) return;
        applyClockItems(clock.id, res.data || []);
      } catch (error) {
        console.error('Error fetching clock items:', error);
        if (loadId !== clockItemsLoadRef.current) return;
        applyClockItems(clock.id, []);
      }
    } else {
      setEditingClock(null);
      setClockFormData({
        name: '',
        description: '',
        color: '#1976D2',
        station_id: selectedStation || null,
        use_default_rule_set: true,
        custom_rule_set_id: null,
        category_goals: {},
        autofade_enabled: false,
        auto_mode: false,
        live_assist: false,
        voicetrack_collaboration_enabled: true,
      });
    }
  };

  const handleCloseClockDialog = () => {
    clockItemsLoadRef.current += 1;
    editingClockIdRef.current = null;
    setClockEditorOpen(false);
    setEditingClock(null);
    setClockItems([]);
    setClockDialogTab(0);
  };

  // Clock Items functions
  const handleOpenClockItemDialog = async (item = null) => {
    if (editingClock && syndicatedClockIds.includes(String(editingClock.id))) return;
    // Fetch commercial breaks for the clock's station
    const dialogStationId = (editingClock && editingClock.station_id) || clockFormData.station_id || selectedStation || null;
    if (editingClock && editingClock.station_id) {
      await fetchCommercialBreaks(editingClock.station_id);
    } else if (clockFormData.station_id) {
      await fetchCommercialBreaks(clockFormData.station_id);
    }

    // Refresh mixblock definitions for the clock's station so newly created
    // mixblocks are available in the Mixblock dropdown.
    fetchMixblockDefinitions(dialogStationId);

    // Always fetch AI voices and prompts (needed for ai_voicetrack type)
    fetchAiVoices();
    fetchAiPrompts();
    
    if (item) {
      setEditingClockItem(item);
      const itemSettings = parseJsonObject(item.settings);
      setClockItemFormData({
        type: item.type || 'category',
        selection_mode: item.selection_mode || 'random',
        asset_id: item.asset_id || null,
        category_id: item.category_id || null,
        commercial_break_id: item.commercial_break_id || null,
        mixblock_id: item.mixblock_id || null,
        mixblock_harmonic: itemSettings.mixblock_harmonic !== false,
        rule_set_id: item.rule_set_id || null,
        use_category_goals: item.use_category_goals === true,
        duration: item.duration || null,
        play_as: item.play_as || 'normal',
        droppable: item.type === 'soft_time_marker' || item.type === 'hard_fixed_timer'
          ? false
          : (itemSettings.droppable !== false),
        stream_relay_url: itemSettings.relayUrl || '',
        stream_relay_display_name: itemSettings.relayDisplayName || '',
        webrtc_contributor_username: itemSettings.webrtcContributorUsername || '',
        webrtc_display_name: itemSettings.webrtcDisplayName || '',
        webrtc_preconnect_seconds: itemSettings.webrtcPreconnectSeconds != null ? itemSettings.webrtcPreconnectSeconds : 600,
        // AI Voicetrack fields
        ai_prompt_id: itemSettings.ai_prompt_id || null,
        ai_voice_id: itemSettings.ai_voice_id || null,
        sweep_over_intro: item.sweep_over_intro || false,
        music_bed_enabled: item.music_bed_enabled || false,
        music_bed_selection_mode: (item.music_bed_selection_mode === 'specific_asset' || item.music_bed_selection_mode === 'specific')
          ? 'specific'
          : (item.music_bed_selection_mode || 'random_from_category'),
        music_bed_category_id: item.music_bed_category_id || null,
        music_bed_asset_id: item.music_bed_asset_id || null,
        music_bed_volume_db: item.music_bed_volume_db != null ? item.music_bed_volume_db : -12.0,
        // Hard Fixed Timer fields
        fixed_minute: itemSettings.fixed_minute != null ? itemSettings.fixed_minute : null,
        fixed_second: itemSettings.fixed_second != null ? itemSettings.fixed_second : null,
        // Soft Time Marker fields
        soft_minute: itemSettings.soft_minute != null ? itemSettings.soft_minute : null,
        soft_second: itemSettings.soft_second != null ? itemSettings.soft_second : null,
        soft_boundary_seconds: itemSettings.soft_boundary_seconds != null ? itemSettings.soft_boundary_seconds : null,
      });
    } else {
      setEditingClockItem(null);
      setClockItemFormData({
        type: 'category',
        selection_mode: 'random',
        asset_id: null,
        category_id: null,
        commercial_break_id: null,
        mixblock_id: null,
        mixblock_harmonic: true,
        rule_set_id: null,
        use_category_goals: false,
        duration: null,
        play_as: 'normal',
        droppable: true,
        stream_relay_url: '',
        stream_relay_display_name: '',
        webrtc_contributor_username: '',
        webrtc_display_name: '',
        webrtc_preconnect_seconds: 600,
        // AI Voicetrack defaults
        ai_prompt_id: null,
        ai_voice_id: null,
        sweep_over_intro: false,
        music_bed_enabled: false,
        music_bed_selection_mode: 'random_from_category',
        music_bed_category_id: null,
        music_bed_asset_id: null,
        music_bed_volume_db: -12.0,
        // Hard Fixed Timer defaults
        fixed_minute: null,
        fixed_second: null,
        // Soft Time Marker defaults
        soft_minute: null,
        soft_second: null,
        soft_boundary_seconds: null,
      });
    }
    setClockItemDialogOpen(true);
  };

  const handleCloseClockItemDialog = () => {
    setClockItemDialogOpen(false);
    setEditingClockItem(null);
    setClockItemCategoryTypeFilter('');
  };

  const handleSaveClockItem = async () => {
    if (!editingClock) {
      alert('Please save the clock first before adding items');
      return;
    }
    if (clockItemFormData.type === 'stream_relay' && !(clockItemFormData.stream_relay_url || '').trim()) {
      alert('Please enter the rebroadcast URL for this Stream Relay item.');
      return;
    }
    if (clockItemFormData.type === 'webrtc_receiver' && !(clockItemFormData.webrtc_contributor_username || '').trim()) {
      alert('Please choose a contributor for this Remote Contribution item.');
      return;
    }
    if (clockItemFormData.type === 'webrtc_receiver') {
      const durationSeconds = Number(clockItemFormData.duration);
      if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
        alert('Please set a fixed duration (seconds) greater than 0 for this Remote Contribution item.');
        return;
      }
    }
    if (clockItemFormData.type === 'mixblock' && !clockItemFormData.mixblock_id) {
      alert('Please select a Mixblock for this item.');
      return;
    }
    if (clockItemFormData.type === 'ai_voicetrack') {
      if (!clockItemFormData.ai_prompt_id) {
        alert('Please select an AI Prompt for this AI Voicetrack item.');
        return;
      }
      if (!clockItemFormData.ai_voice_id) {
        alert('Please select an AI Voice for this AI Voicetrack item.');
        return;
      }
    }
    if ((clockItemFormData.type === 'voicetrack' || clockItemFormData.type === 'ai_voicetrack') && clockItemFormData.music_bed_enabled) {
      const mode = (clockItemFormData.music_bed_selection_mode === 'specific_asset' || clockItemFormData.music_bed_selection_mode === 'specific')
        ? 'specific'
        : (clockItemFormData.music_bed_selection_mode || 'random_from_category');
      if (mode === 'random_from_category' && !clockItemFormData.music_bed_category_id) {
        alert('Please select a Music Bed Category.');
        return;
      }
      if (mode === 'specific' && !clockItemFormData.music_bed_asset_id) {
        alert('Please select a specific Music Bed asset.');
        return;
      }
    }
    if (clockItemFormData.type === 'hard_fixed_timer') {
      const m = clockItemFormData.fixed_minute;
      const s = clockItemFormData.fixed_second;
      if (m == null || s == null || m === '' || s === '') {
        alert('Please set both Minute and Second for the Hard Fixed Timer.');
        return;
      }
      if (m < 0 || m > 59 || s < 0 || s > 59) {
        alert('Minute must be 0-59 and Second must be 0-59.');
        return;
      }
    }
    if (clockItemFormData.type === 'soft_time_marker') {
      const m = clockItemFormData.soft_minute;
      const s = clockItemFormData.soft_second;
      const b = clockItemFormData.soft_boundary_seconds;
      if (m == null || s == null || m === '' || s === '') {
        alert('Please set both Minute and Second for the Soft Time Marker.');
        return;
      }
      if (m < 0 || m > 59 || s < 0 || s > 59) {
        alert('Minute must be 0-59 and Second must be 0-59.');
        return;
      }
      if (b != null && b !== '' && (!Number.isInteger(Number(b)) || Number(b) < 0)) {
        alert('Soft marker boundary must be a whole number of seconds (0 or greater).');
        return;
      }
    }

    try {
      // Determine position based on current order; new items go to end
      let positionValue = 0;
      if (editingClockItem) {
        const idx = clockItems.findIndex(ci => ci.id === editingClockItem.id);
        positionValue = idx >= 0 ? idx : 0;
      } else {
        positionValue = clockItems.length;
      }

      const settings = {
        droppable: clockItemFormData.type === 'hard_fixed_timer' || clockItemFormData.type === 'soft_time_marker'
          ? false
          : (clockItemFormData.droppable !== false),
      };
      if (clockItemFormData.type === 'mixblock') {
        settings.mixblock_harmonic = clockItemFormData.mixblock_harmonic !== false;
      }
      if (clockItemFormData.type === 'stream_relay') {
        if (clockItemFormData.stream_relay_url) settings.relayUrl = clockItemFormData.stream_relay_url.trim();
        if (clockItemFormData.stream_relay_display_name != null) settings.relayDisplayName = String(clockItemFormData.stream_relay_display_name).trim();
      }
      if (clockItemFormData.type === 'webrtc_receiver') {
        settings.webrtcContributorUsername = String(clockItemFormData.webrtc_contributor_username || '').trim();
        if (clockItemFormData.webrtc_display_name != null) {
          settings.webrtcDisplayName = String(clockItemFormData.webrtc_display_name).trim();
        }
        if (clockItemFormData.webrtc_preconnect_seconds != null && clockItemFormData.webrtc_preconnect_seconds !== '') {
          const preconnect = parseInt(clockItemFormData.webrtc_preconnect_seconds, 10);
          if (!Number.isNaN(preconnect) && preconnect >= 0) {
            settings.webrtcPreconnectSeconds = preconnect;
          }
        }
      }
      if (clockItemFormData.type === 'ai_voicetrack') {
        if (clockItemFormData.ai_prompt_id) settings.ai_prompt_id = parseInt(clockItemFormData.ai_prompt_id);
        if (clockItemFormData.ai_voice_id) settings.ai_voice_id = parseInt(clockItemFormData.ai_voice_id);
      }
      if (clockItemFormData.type === 'hard_fixed_timer') {
        settings.fixed_minute = clockItemFormData.fixed_minute != null ? parseInt(clockItemFormData.fixed_minute) : 0;
        settings.fixed_second = clockItemFormData.fixed_second != null ? parseInt(clockItemFormData.fixed_second) : 0;
        settings.is_hard_fixed_timer = true;
      }
      if (clockItemFormData.type === 'soft_time_marker') {
        settings.soft_minute = clockItemFormData.soft_minute != null ? parseInt(clockItemFormData.soft_minute) : 0;
        settings.soft_second = clockItemFormData.soft_second != null ? parseInt(clockItemFormData.soft_second) : 0;
        if (clockItemFormData.soft_boundary_seconds != null && clockItemFormData.soft_boundary_seconds !== '') {
          const boundary = parseInt(clockItemFormData.soft_boundary_seconds, 10);
          if (!Number.isNaN(boundary) && boundary > 0) {
            settings.soft_boundary_seconds = boundary;
          }
        }
        settings.is_soft_time_marker = true;
      }
      const data = {
        ...clockItemFormData,
        position: positionValue,
        settings,
      };
      data.music_bed_selection_mode = (data.music_bed_selection_mode === 'specific_asset' || data.music_bed_selection_mode === 'specific')
        ? 'specific'
        : (data.music_bed_selection_mode || 'random_from_category');
      if (!data.music_bed_enabled) {
        data.music_bed_selection_mode = 'random_from_category';
        data.music_bed_category_id = null;
        data.music_bed_asset_id = null;
        data.music_bed_volume_db = -12.0;
      } else if (data.music_bed_selection_mode === 'random_from_category') {
        data.music_bed_asset_id = null;
      } else if (data.music_bed_selection_mode === 'specific') {
        data.music_bed_category_id = null;
      }
      // Hard fixed timer has 0 duration and specific play_as
      if (clockItemFormData.type === 'hard_fixed_timer') {
        data.duration = 0;
        data.play_as = 'normal';
      }
      // Soft time marker has 0 duration
      if (clockItemFormData.type === 'soft_time_marker') {
        data.duration = 0;
        data.play_as = 'normal';
      }
      // Remove fields that go in settings, not top-level
      delete data.droppable;
      delete data.stream_relay_url;
      delete data.stream_relay_display_name;
      delete data.webrtc_contributor_username;
      delete data.webrtc_display_name;
      delete data.webrtc_preconnect_seconds;
      delete data.fixed_minute;
      delete data.fixed_second;
      delete data.soft_minute;
      delete data.soft_second;
      delete data.soft_boundary_seconds;
      delete data.ai_prompt_id;
      delete data.ai_voice_id;

      if (editingClockItem) {
        await axios.put(
          `${API_URL}/api/clocks/${editingClock.id}/items/${editingClockItem.id}`,
          data,
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${API_URL}/api/clocks/${editingClock.id}/items`,
          data,
          { withCredentials: true }
        );
      }

      handleCloseClockItemDialog();
      // Refresh clock items
      const res = await axios.get(`${API_URL}/api/clocks/${editingClock.id}/items`, {
        withCredentials: true
      });
      applyClockItems(editingClock.id, res.data || []);
    } catch (error) {
      console.error('Error saving clock item:', error);
      alert('Failed to save clock item: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteClockItem = async (itemId) => {
    if (!editingClock) return;
    if (syndicatedClockIds.includes(String(editingClock.id))) return;
    if (!window.confirm('Are you sure you want to delete this clock item?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/clocks/${editingClock.id}/items/${itemId}`, {
        withCredentials: true
      });
      // Refresh clock items
      const res = await axios.get(`${API_URL}/api/clocks/${editingClock.id}/items`, {
        withCredentials: true
      });
      applyClockItems(editingClock.id, res.data || []);
    } catch (error) {
      console.error('Error deleting clock item:', error);
      alert('Failed to delete clock item: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDuplicateClockItem = async (item) => {
    if (!editingClock) return;
    if (syndicatedClockIds.includes(String(editingClock.id))) return;

    try {
      const itemSettings = parseJsonObject(item.settings);
      
      const data = {
        type: item.type,
        selection_mode: item.selection_mode,
        asset_id: item.asset_id || null,
        category_id: item.category_id || null,
        commercial_break_id: item.commercial_break_id || null,
        mixblock_id: item.mixblock_id || null,
        rule_set_id: item.rule_set_id || null,
        use_category_goals: item.use_category_goals === true,
        duration: item.duration || null,
        play_as: item.play_as || 'normal',
        sweep_over_intro: item.sweep_over_intro || false,
        music_bed_enabled: item.music_bed_enabled || false,
        music_bed_selection_mode: (item.music_bed_selection_mode === 'specific_asset' || item.music_bed_selection_mode === 'specific')
          ? 'specific'
          : (item.music_bed_selection_mode || 'random_from_category'),
        music_bed_category_id: item.music_bed_category_id || null,
        music_bed_asset_id: item.music_bed_asset_id || null,
        music_bed_volume_db: item.music_bed_volume_db != null ? item.music_bed_volume_db : -12.0,
        settings: {
          droppable: item.type === 'soft_time_marker' || item.type === 'hard_fixed_timer' ? false : (itemSettings.droppable !== false),
          ...(item.type === 'mixblock' ? { mixblock_harmonic: itemSettings.mixblock_harmonic !== false } : {}),
          ...(item.type === 'stream_relay' && itemSettings.relayUrl ? { relayUrl: itemSettings.relayUrl } : {}),
          ...(item.type === 'stream_relay' && itemSettings.relayDisplayName != null ? { relayDisplayName: itemSettings.relayDisplayName } : {}),
          ...(item.type === 'webrtc_receiver' && itemSettings.webrtcContributorUsername ? { webrtcContributorUsername: itemSettings.webrtcContributorUsername } : {}),
          ...(item.type === 'webrtc_receiver' && itemSettings.webrtcDisplayName != null ? { webrtcDisplayName: itemSettings.webrtcDisplayName } : {}),
          ...(item.type === 'webrtc_receiver' && itemSettings.webrtcPreconnectSeconds != null ? { webrtcPreconnectSeconds: itemSettings.webrtcPreconnectSeconds } : {}),
          ...(item.type === 'ai_voicetrack' && itemSettings.ai_prompt_id ? { ai_prompt_id: itemSettings.ai_prompt_id } : {}),
          ...(item.type === 'ai_voicetrack' && itemSettings.ai_voice_id ? { ai_voice_id: itemSettings.ai_voice_id } : {}),
          ...(item.type === 'hard_fixed_timer' ? { is_hard_fixed_timer: true, fixed_minute: itemSettings.fixed_minute || 0, fixed_second: itemSettings.fixed_second || 0 } : {}),
          ...(item.type === 'soft_time_marker'
            ? {
                is_soft_time_marker: true,
                soft_minute: itemSettings.soft_minute || 0,
                soft_second: itemSettings.soft_second || 0,
                ...(itemSettings.soft_boundary_seconds != null && Number(itemSettings.soft_boundary_seconds) > 0
                  ? { soft_boundary_seconds: parseInt(itemSettings.soft_boundary_seconds, 10) }
                  : {}),
              }
            : {}),
        },
      };

      await axios.post(
        `${API_URL}/api/clocks/${editingClock.id}/items`,
        data,
        { withCredentials: true }
      );

      // Refresh clock items
      const res = await axios.get(`${API_URL}/api/clocks/${editingClock.id}/items`, {
        withCredentials: true
      });
      applyClockItems(editingClock.id, res.data || []);
    } catch (error) {
      console.error('Error duplicating clock item:', error);
      alert('Failed to duplicate clock item: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleClockItemDragStart = (index) => {
    setDragIndex(index);
  };

  const handleClockItemDragEnd = () => {
    setDragIndex(null);
  };

  const handleClockItemDrop = async (toIndex) => {
    if (editingClock && syndicatedClockIds.includes(String(editingClock.id))) {
      setDragIndex(null);
      return;
    }
    if (dragIndex === null || dragIndex === toIndex) {
      setDragIndex(null);
      return;
    }

    const updated = [...clockItems].sort((a, b) => (a.position || 0) - (b.position || 0));
    const [moved] = updated.splice(dragIndex, 1);
    updated.splice(toIndex, 0, moved);

    const reordered = updated.map((item, idx) => ({ ...item, position: idx }));
    setClockItems(reordered);
    setDragIndex(null);

    if (editingClock) {
      try {
        // Use bulk reorder endpoint for atomic position update
        const itemOrder = reordered
          .filter(item => item.id)
          .map((item, idx) => ({ id: item.id, position: idx }));
        const res = await axios.put(
          `${API_URL}/api/clocks/${editingClock.id}/reorder`,
          { itemOrder },
          { withCredentials: true }
        );
        // Update state with server-confirmed order
        if (res.data && Array.isArray(res.data)) {
          applyClockItems(editingClock.id, res.data);
        }
      } catch (error) {
        console.error('Error reordering clock items:', error);
        alert('Failed to reorder items: ' + (error.response?.data?.message || error.message));
        // Refetch to get the actual server state
        try {
          const refreshRes = await axios.get(`${API_URL}/api/clocks/${editingClock.id}/items`, {
            withCredentials: true
          });
          applyClockItems(editingClock.id, refreshRes.data || []);
        } catch (e) { /* ignore */ }
      }
    }
  };

  const filteredClocks = selectedStation
    ? clockTemplates.filter((c) => String(c.station_id || '') === String(selectedStation))
    : clockTemplates;
  const selectedSyndicationLink = syndicationLinks.find((link) =>
    link.role === 'child'
    && link.status === 'active'
    && String(link.destination_station_id) === String(selectedStation));

  // Hours-per-week each clock is assigned in the selected station's weekly schedule
  const scheduledHoursByClock = {};
  if (selectedStation) {
    schedules.forEach((s) => {
      if (s.clock_id != null) {
        scheduledHoursByClock[s.clock_id] = (scheduledHoursByClock[s.clock_id] || 0) + 1;
      }
    });
  }

  const assignableClocks = selectedStation
    ? filteredClocks.filter((clock) => !syndicatedClockIds.includes(String(clock.id)))
    : [];

  const isCategoryVisibleForStation = (category, stationValue) => {
    if (stationValue === null || stationValue === undefined || stationValue === '') return true;

    const selected = String(stationValue);
    const categoryStationId = category.station_id == null ? '' : String(category.station_id);
    const categoryStationIds = Array.isArray(category.station_ids)
      ? category.station_ids.map((id) => String(id))
      : [];
    const isMappedCategory = categoryStationIds.length > 0;
    const isGlobalCategory = categoryStationId === '' && !isMappedCategory;

    return isGlobalCategory || categoryStationId === selected || categoryStationIds.includes(selected);
  };

  const filteredCategories = selectedCategoryStation
    ? categories.filter((category) => isCategoryVisibleForStation(category, selectedCategoryStation))
    : categories;

  const getCategoryType = (category) => {
    if (!category?.category_type || category.category_type === 'other') return 'other';
    return category.category_type;
  };

  const getCategoryTypeMeta = (categoryType) => {
    switch (categoryType) {
      case 'music':
        return { label: 'Music', icon: <MusicNoteIcon sx={{ fontSize: '0.85rem' }} />, titleColor: '#22c55e' };
      case 'station_id':
        return { label: 'Station / ID', icon: <RadioIcon sx={{ fontSize: '0.85rem' }} />, titleColor: '#f59e0b' };
      case 'music_bed':
        return { label: 'Music Bed', icon: <HeadphonesIcon sx={{ fontSize: '0.85rem' }} />, titleColor: '#ec4899' };
      default:
        return { label: 'Other', icon: <MoreHorizIcon sx={{ fontSize: '0.85rem' }} />, titleColor: '#9e9e9e' };
    }
  };

  const categoryTypeOrder = ['music', 'station_id', 'music_bed', 'other'];
  const categoriesByType = filteredCategories.reduce((acc, category) => {
    const categoryType = getCategoryType(category);
    if (!acc[categoryType]) acc[categoryType] = [];
    acc[categoryType].push(category);
    return acc;
  }, {});

  const orderedCategoryGroups = [
    ...categoryTypeOrder.filter((type) => categoriesByType[type]),
    ...Object.keys(categoriesByType).filter((type) => !categoryTypeOrder.includes(type)),
  ].map((type) => ({ type, categories: categoriesByType[type] }));

  const categoriesWithTypeFilter = categoryTypeFilter
    ? filteredCategories.filter((c) => getCategoryType(c) === categoryTypeFilter)
    : filteredCategories;

  const categoriesWithTypeFilterByType = categoriesWithTypeFilter.reduce((acc, category) => {
    const categoryType = getCategoryType(category);
    if (!acc[categoryType]) acc[categoryType] = [];
    acc[categoryType].push(category);
    return acc;
  }, {});

  const orderedFilteredCategoryGroups = [
    ...categoryTypeOrder.filter((type) => categoriesWithTypeFilterByType[type]),
    ...Object.keys(categoriesWithTypeFilterByType).filter((type) => !categoryTypeOrder.includes(type)),
  ].map((type) => ({ type, categories: categoriesWithTypeFilterByType[type] }));

  const renderClocksCategoryCard = (category, isList) => {
    const isFixed = category.name === 'Voicetrack' || category.name === 'Commercial Spots' || category.name === 'Music Beds';
    const isMusicBeds = category.category_type === 'music_bed' || category.name === 'Music Beds';
    const renderTypeChip = () => {
      if (category.category_type === 'music') return <Chip icon={<MusicNoteIcon sx={{ fontSize: '0.85rem' }} />} label="Music" size="small" sx={{ bgcolor: '#22c55e20', color: '#22c55e', fontSize: '0.7rem' }} />;
      if (category.category_type === 'music_bed') return <Chip icon={<HeadphonesIcon sx={{ fontSize: '0.85rem' }} />} label="Music Bed" size="small" sx={{ bgcolor: '#ec489920', color: '#ec4899', fontSize: '0.7rem' }} />;
      if (category.category_type === 'station_id') return <Chip icon={<RadioIcon sx={{ fontSize: '0.85rem' }} />} label="Station / ID" size="small" sx={{ bgcolor: '#f59e0b20', color: '#f59e0b', fontSize: '0.7rem' }} />;
      return <Chip icon={<MoreHorizIcon sx={{ fontSize: '0.85rem' }} />} label="Other" size="small" sx={{ bgcolor: '#9e9e9e20', color: '#9e9e9e', fontSize: '0.7rem' }} />;
    };
    return (
      <Card
        key={category.id}
        sx={{
          bgcolor: 'var(--rs-paper)',
          border: isMusicBeds ? '1px solid #ec4899' : '1px solid var(--rs-border)',
          '&:hover': { borderColor: category.color || '#22d3ee' },
        }}
      >
        <CardContent sx={isList ? { py: 1.5, '&:last-child': { pb: 1.5 } } : {}}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: isList ? 'center' : 'start', mb: isList ? 0 : 2, ...(isList && { flexWrap: 'wrap', gap: 1 }) }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
              <Box sx={{ width: isList ? 18 : 24, height: isList ? 18 : 24, borderRadius: '4px', bgcolor: category.color || '#1976D2', flexShrink: 0 }} />
              <Typography variant={isList ? 'subtitle1' : 'h6'} sx={{ color: isMusicBeds ? '#ec4899' : '#22d3ee', fontWeight: isList ? 600 : undefined, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isList ? 'nowrap' : undefined }}>
                {category.name}
              </Typography>
              {isFixed && <Chip label="Fixed" size="small" sx={{ bgcolor: '#22d3ee20', color: '#22d3ee', fontSize: '0.65rem', height: '20px' }} />}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {isList && renderTypeChip()}
              {isList && hasSystemFeature('ai_insights') && category.ai_insights_enabled && <Chip label="AI Insights" size="small" sx={{ bgcolor: '#22d3ee20', color: '#22d3ee', fontSize: '0.7rem' }} />}
              {isList && !category.metadata_enabled && <Chip label="No Metadata" size="small" sx={{ bgcolor: '#666', color: '#999', fontSize: '0.7rem' }} />}
              <IconButton size="small" onClick={() => handleOpenCategoryDialog(category)} sx={{ color: '#22d3ee' }}><Edit fontSize="small" /></IconButton>
              {isMusicBeds && <IconButton size="small" onClick={() => setExpandedMusicBeds(!expandedMusicBeds)} sx={{ color: '#ec4899' }} title="Toggle sub-categories">{expandedMusicBeds ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}</IconButton>}
              <IconButton size="small" onClick={() => handleDeleteCategory(category.id, category.name)} disabled={isFixed} sx={{ color: isFixed ? '#666' : '#ff6b6b' }}><Delete fontSize="small" /></IconButton>
            </Box>
          </Box>
          {category.description && <Typography variant="body2" color="text.secondary" sx={{ mb: isList ? 0 : 1, mt: isList ? 1 : 0 }}>{category.description}</Typography>}
          {!isList && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              {renderTypeChip()}
              {hasSystemFeature('ai_insights') && category.ai_insights_enabled && <Chip label="AI Insights" size="small" sx={{ bgcolor: '#22d3ee20', color: '#22d3ee', fontSize: '0.7rem' }} />}
              {!category.metadata_enabled && <Chip label="No Metadata" size="small" sx={{ bgcolor: '#666', color: '#999', fontSize: '0.7rem' }} />}
              {isMusicBeds && musicBedSubcategories.length > 0 && <Chip icon={<QueueMusicIcon sx={{ fontSize: '0.85rem' }} />} label={`${musicBedSubcategories.length} Sub-categor${musicBedSubcategories.length === 1 ? 'y' : 'ies'}`} size="small" sx={{ bgcolor: '#ec489920', color: '#ec4899', fontSize: '0.7rem' }} />}
            </Box>
          )}
          {isMusicBeds && (
            <Collapse in={expandedMusicBeds} timeout="auto" unmountOnExit>
              <Divider sx={{ my: 2, borderColor: 'var(--rs-border)' }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ color: '#ec4899' }}>Sub-Categories</Typography>
                <Button size="small" startIcon={<Add />} onClick={() => handleOpenSubCategoryDialog()} sx={{ color: '#ec4899', fontSize: '0.75rem' }}>Add Sub-Category</Button>
              </Box>
              {musicBedSubcategories.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No sub-categories yet. Add sub-categories to organise your music beds (e.g. "Upbeat", "Mellow", "News Beds").</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {musicBedSubcategories.map((sub) => (
                    <Box key={sub.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'var(--rs-bg)', borderRadius: 1, px: 1.5, py: 0.75, border: '1px solid var(--rs-border)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 16, height: 16, borderRadius: '3px', bgcolor: sub.color || '#ec4899' }} />
                        <Typography variant="body2">{sub.name}</Typography>
                        {sub.description && <Typography variant="caption" color="text.secondary">— {sub.description}</Typography>}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => handleOpenSubCategoryDialog(sub)} sx={{ color: '#22d3ee' }}><Edit sx={{ fontSize: '0.9rem' }} /></IconButton>
                        <IconButton size="small" onClick={() => handleDeleteSubCategory(sub.id)} sx={{ color: '#ff6b6b' }}><Delete sx={{ fontSize: '0.9rem' }} /></IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Collapse>
          )}
        </CardContent>
      </Card>
    );
  };

  const isMultiStationMode = (stations?.length || 0) > 1;
  const activeClockStationId = editingClock?.station_id || clockFormData.station_id || null;

  const baseClockItemCategories = isMultiStationMode && activeClockStationId
    ? categories.filter((category) => isCategoryVisibleForStation(category, activeClockStationId))
    : categories;

  const filteredClockItemCategories = clockItemCategoryTypeFilter
    ? baseClockItemCategories.filter((c) => getCategoryType(c) === clockItemCategoryTypeFilter)
    : baseClockItemCategories;

  const filteredClockItemMusicBedCategories = baseClockItemCategories.filter(
    (c) => c.category_type === 'music_bed' || c.name === 'Music Beds'
  );

  const filteredClockItemMusicBedSubcategories = isMultiStationMode && activeClockStationId
    ? musicBedSubcategories.filter((subcategory) => isCategoryVisibleForStation(subcategory, activeClockStationId))
    : musicBedSubcategories;
  const clockItemMusicBedCategoryIds = new Set([
    ...filteredClockItemMusicBedCategories.map((c) => Number(c.id)),
    ...filteredClockItemMusicBedSubcategories.map((c) => Number(c.id)),
  ]);
  const filteredClockItemMusicBedAssets = assets.filter((asset) =>
    Array.isArray(asset.categories) &&
    asset.categories.some((cat) => clockItemMusicBedCategoryIds.has(Number(cat.id)))
  );

  const handleSaveClock = async () => {
    try {
      if (!clockFormData.name) {
        alert('Clock name is required');
        return;
      }

      const payload = {
        ...clockFormData,
        settings: {
          autofade_enabled: clockFormData.autofade_enabled || false,
          auto_mode: clockFormData.auto_mode || false,
          live_assist: clockFormData.live_assist || false,
          voicetrack_collaboration_enabled: clockFormData.voicetrack_collaboration_enabled !== false,
        },
      };

      if (editingClock) {
        const res = await axios.put(
          `${API_URL}/api/clocks/${editingClock.id}`,
          payload,
          { withCredentials: true }
        );
        const saved = res.data || {};
        setEditingClock((prev) => ({ ...prev, ...saved }));
        await fetchClockTemplates();
      } else {
        const res = await axios.post(
          `${API_URL}/api/clocks`,
          payload,
          { withCredentials: true }
        );
        await fetchClockTemplates();
        if (res.data?.id) {
          await handleOpenClockDialog(res.data, { tab: 1 });
        }
      }
    } catch (error) {
      console.error('Error saving clock:', error);
      alert('Failed to save clock: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteClock = async (clockId) => {
    if (!window.confirm('Are you sure you want to delete this clock template?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/clocks/${clockId}`, {
        withCredentials: true
      });
      if (editingClock && String(editingClock.id) === String(clockId)) {
        handleCloseClockDialog();
      }
      fetchClockTemplates();
    } catch (error) {
      console.error('Error deleting clock:', error);
      alert('Failed to delete clock');
    }
  };

  const handleOpenCloneDialog = (clock) => {
    setClockToClone(clock);
    setCloneClockName(`Copy of ${clock.name}`);
    setCloneDialogOpen(true);
  };

  const handleCloseCloneDialog = () => {
    setCloneDialogOpen(false);
    setClockToClone(null);
    setCloneClockName('');
  };

  const handleConfirmClone = async () => {
    if (!cloneClockName.trim()) {
      alert('Please enter a name for the cloned clock');
      return;
    }
    try {
      const res = await axios.post(
        `${API_URL}/api/clocks/${clockToClone.id}/clone`,
        { name: cloneClockName.trim() },
        { withCredentials: true }
      );
      handleCloseCloneDialog();
      await fetchClockTemplates();
      if (res.data) {
        await handleOpenClockDialog(res.data);
      }
    } catch (error) {
      console.error('Error cloning clock:', error);
      alert('Failed to clone clock: ' + (error.response?.data?.message || error.message));
    }
  };

  // Grid view functions
  const isFranchiseSlotAllowed = (dayOfWeek, hour) => {
    if (selectedRegion || !selectedSyndicationLink) return true;
    if (syndicationRolloutStage < 3) return false;
    if (selectedSyndicationLink.override_mode === 'all') return true;
    if (selectedSyndicationLink.override_mode !== 'selected') return false;
    return (selectedSyndicationLink.override_slots || []).some(
      (slot) => Number(slot.day_of_week) === Number(dayOfWeek) && Number(slot.hour) === Number(hour)
    );
  };
  gridSelectHelpersRef.current.isFranchiseSlotAllowed = isFranchiseSlotAllowed;

  useEffect(() => {
    const GRID_DRAG_THRESHOLD_PX = 6;

    const findGridSlotElement = (x, y) => {
      const stack = typeof document.elementsFromPoint === 'function'
        ? document.elementsFromPoint(x, y)
        : [];
      for (let i = 0; i < stack.length; i++) {
        const el = stack[i];
        if (el && el.getAttribute && el.getAttribute('data-grid-slot')) {
          return el;
        }
      }
      return null;
    };

    const applyDragRect = (day, hour) => {
      const drag = gridDragRef.current;
      if (!drag) return;
      if (day === drag.lastDay && hour === drag.lastHour && drag.moved) return;
      drag.lastDay = day;
      drag.lastHour = hour;
      drag.moved = true;
      const allowed = gridSelectHelpersRef.current.isFranchiseSlotAllowed;
      const rect = slotsInRectangle(drag.startDay, drag.startHour, day, hour, allowed);
      setSelectedSlots(mergeGridSlots(drag.base, rect));
    };

    const endGridDrag = () => {
      const drag = gridDragRef.current;
      if (!drag) return;
      if (!drag.moved) {
        const slot = { day_of_week: drag.startDay, hour: drag.startHour };
        setSelectedSlots((prev) => {
          const exists = prev.some(
            (item) => item.day_of_week === slot.day_of_week && item.hour === slot.hour
          );
          if (exists) {
            return prev.filter(
              (item) => !(item.day_of_week === slot.day_of_week && item.hour === slot.hour)
            );
          }
          return [...prev, slot];
        });
      }
      gridDragRef.current = null;
      setGridSelecting(false);
    };

    const onMove = (event) => {
      const drag = gridDragRef.current;
      if (!drag) return;
      if (event.buttons === 0) {
        endGridDrag();
        return;
      }

      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      if (!drag.moved && (dx * dx + dy * dy) > GRID_DRAG_THRESHOLD_PX * GRID_DRAG_THRESHOLD_PX) {
        applyDragRect(drag.startDay, drag.startHour);
      }

      const cell = findGridSlotElement(event.clientX, event.clientY);
      if (!cell) return;
      const day = Number(cell.getAttribute('data-day'));
      const hour = Number(cell.getAttribute('data-hour'));
      if (Number.isNaN(day) || Number.isNaN(hour)) return;
      applyDragRect(day, hour);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', endGridDrag);
    window.addEventListener('pointercancel', endGridDrag);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', endGridDrag);
      window.removeEventListener('pointercancel', endGridDrag);
    };
  }, []);

  const handleSlotPointerDown = (dayOfWeek, hour, event) => {
    if (!isFranchiseSlotAllowed(dayOfWeek, hour)) return;
    if (event.button !== 0) return;
    event.preventDefault();

    const current = selectedSlotsRef.current;
    gridDragRef.current = {
      startDay: dayOfWeek,
      startHour: hour,
      lastDay: dayOfWeek,
      lastHour: hour,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
      base: current.slice(),
    };
    setGridSelecting(true);
  };

  const handleSelectDay = (dayOfWeek) => {
    const daySlots = Array.from({ length: 24 }, (_, hour) => ({
      day_of_week: dayOfWeek,
      hour
    })).filter((slot) => isFranchiseSlotAllowed(slot.day_of_week, slot.hour));
    setSelectedSlots((prev) => toggleGridSlotGroup(prev, daySlots));
  };

  const handleSelectHour = (hour) => {
    const hourSlots = Array.from({ length: 7 }, (_, dayOfWeek) => ({
      day_of_week: dayOfWeek,
      hour
    })).filter((slot) => isFranchiseSlotAllowed(slot.day_of_week, slot.hour));
    setSelectedSlots((prev) => toggleGridSlotGroup(prev, hourSlots));
  };

  const handleSelectDaypart = (hours) => {
    const slots = [];
    for (let dayOfWeek = 0; dayOfWeek < 7; dayOfWeek++) {
      hours.forEach((hour) => {
        if (isFranchiseSlotAllowed(dayOfWeek, hour)) {
          slots.push({ day_of_week: dayOfWeek, hour });
        }
      });
    }
    setSelectedSlots((prev) => toggleGridSlotGroup(prev, slots));
  };

  const handleSelectAllHours = () => {
    const slots = [];
    for (let dayOfWeek = 0; dayOfWeek < 7; dayOfWeek++) {
      for (let hour = 0; hour < 24; hour++) {
        if (isFranchiseSlotAllowed(dayOfWeek, hour)) {
          slots.push({ day_of_week: dayOfWeek, hour });
        }
      }
    }
    setSelectedSlots(slots);
  };

  const handleClearSelection = () => {
    setSelectedSlots([]);
  };

  const handleAssignClock = async () => {
    if (selectedSlots.length === 0) {
      alert('Please select at least one time slot');
      return;
    }

    // Regional assignment
    if (selectedRegion) {
      if (assignFollowMaster) {
        // Follow master — remove overrides
        try {
          await axios.post(
            `${API_URL}/api/schedules/weekly/regional/bulk`,
            {
              station_id: selectedStation,
              region_id: parseInt(selectedRegion),
              follow_master: true,
              slots: selectedSlots
            },
            { withCredentials: true }
          );
          setSelectedSlots([]);
          setAssignClockDialogOpen(false);
          setSelectedClockForAssign('');
          setAssignFollowMaster(true);
          fetchRegionalSchedules();
        } catch (error) {
          console.error('Error assigning regional clock:', error);
          alert('Failed to assign regional clock: ' + (error.response?.data?.message || error.message));
        }
      } else {
        if (!selectedClockForAssign) {
          alert('Please select a clock or choose "Follow Master"');
          return;
        }
        if (selectedClockForAssign !== 'none') {
          const isAssignableClock = assignableClocks.some(
            (clock) => String(clock.id) === String(selectedClockForAssign)
          );
          if (!isAssignableClock) {
            alert('Please select a clock from the currently selected station');
            return;
          }
        }
        try {
          await axios.post(
            `${API_URL}/api/schedules/weekly/regional/bulk`,
            {
              station_id: selectedStation,
              region_id: parseInt(selectedRegion),
              clock_id: selectedClockForAssign === 'none' ? null : parseInt(selectedClockForAssign),
              follow_master: false,
              slots: selectedSlots
            },
            { withCredentials: true }
          );
          setSelectedSlots([]);
          setAssignClockDialogOpen(false);
          setSelectedClockForAssign('');
          setAssignFollowMaster(true);
          fetchRegionalSchedules();
        } catch (error) {
          console.error('Error assigning regional clock:', error);
          alert('Failed to assign regional clock: ' + (error.response?.data?.message || error.message));
        }
      }
      return;
    }

    // Master assignment (existing behavior)
    if (assignMode === 'repeat' && !selectedRegion) {
      const selfRepeat = selectedSlots.some(
        (slot) => Number(slot.day_of_week) === Number(repeatFromDay) && Number(slot.hour) === Number(repeatFromHour)
      );
      if (selfRepeat) {
        alert('A selected hour cannot repeat itself. Choose a different source hour.');
        return;
      }
      if (selectedSyndicationLink) {
        alert('Hour repeats are assigned on the master schedule. Franchise overrides still use a local clock.');
        return;
      }
      try {
        await axios.post(
          `${API_URL}/api/schedules/weekly/bulk`,
          {
            station_id: selectedStation,
            clock_id: null,
            repeat_from_day: parseInt(repeatFromDay, 10),
            repeat_from_hour: parseInt(repeatFromHour, 10),
            slots: selectedSlots
          },
          { withCredentials: true }
        );
        setSelectedSlots([]);
        setAssignClockDialogOpen(false);
        setSelectedClockForAssign('');
        setAssignMode('clock');
        fetchSchedules();
      } catch (error) {
        console.error('Error assigning hour repeat:', error);
        alert('Failed to assign hour repeat: ' + (error.response?.data?.message || error.message));
      }
      return;
    }

    if (!selectedClockForAssign) {
      alert('Please select a clock');
      return;
    }

    if (selectedClockForAssign !== 'none') {
      const isAssignableClock = assignableClocks.some(
        (clock) => String(clock.id) === String(selectedClockForAssign)
      );
      if (!isAssignableClock) {
        alert('Please select a clock from the currently selected station');
        return;
      }
    }

    if (selectedSyndicationLink) {
      if (syndicationRolloutStage < 3) {
        alert('Local franchise clock overrides are not enabled in this rollout stage.');
        return;
      }
      try {
        await axios.put(
          `${API_URL}/api/syndication/links/${selectedSyndicationLink.link_id}/overrides`,
          {
            slots: selectedSlots.map((slot) => ({
              ...slot,
              local_clock_id: selectedClockForAssign === 'none' ? null : parseInt(selectedClockForAssign),
              enabled: selectedClockForAssign !== 'none',
            })),
          },
          { withCredentials: true }
        );
        setSelectedSlots([]);
        setAssignClockDialogOpen(false);
        setSelectedClockForAssign('');
        await fetchSyndicationState();
      } catch (error) {
        console.error('Error assigning franchise clock override:', error);
        alert('Failed to assign franchise override: ' + (error.response?.data?.message || error.message));
      }
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/schedules/weekly/bulk`,
        {
          station_id: selectedStation,
          clock_id: selectedClockForAssign === 'none' ? null : parseInt(selectedClockForAssign),
          slots: selectedSlots
        },
        { withCredentials: true }
      );

      setSelectedSlots([]);
      setAssignClockDialogOpen(false);
      setSelectedClockForAssign('');
      setAssignMode('clock');
      fetchSchedules();
    } catch (error) {
      console.error('Error assigning clock:', error);
      alert('Failed to assign clock: ' + (error.response?.data?.message || error.message));
    }
  };

  const getScheduleForSlot = (dayOfWeek, hour) => {
    if (selectedRegion) {
      // Look for a regional override first
      const regional = regionalSchedules.find(
        s => s.day_of_week === dayOfWeek && s.hour === hour && !s.follow_master
      );
      if (regional) return regional; // Has a specific clock override
      // Fall back to master schedule
    }
    const inherited = schedules.find(s => s.day_of_week === dayOfWeek && s.hour === hour);
    if (!selectedRegion && selectedSyndicationLink) {
      const override = syndicationOverrides.find(
        (slot) => slot.day_of_week === dayOfWeek && slot.hour === hour && slot.enabled
      );
      if (override) return { ...(inherited || {}), ...override, clock_id: override.local_clock_id };
    }
    return inherited;
  };

  const isRegionalOverride = (dayOfWeek, hour) => {
    if (!selectedRegion) return false;
    return regionalSchedules.some(
      s => s.day_of_week === dayOfWeek && s.hour === hour && !s.follow_master
    );
  };

  const isFranchiseOverride = (dayOfWeek, hour) => {
    if (selectedRegion || !selectedSyndicationLink) return false;
    return syndicationOverrides.some(
      (slot) => slot.day_of_week === dayOfWeek && slot.hour === hour && slot.enabled
    );
  };

  const isSlotSelected = (dayOfWeek, hour) => {
    return selectedSlots.some(s => s.day_of_week === dayOfWeek && s.hour === hour);
  };

  const isDayRowSelected = (dayOfWeek) => {
    const hours = Array.from({ length: 24 }, (_, hour) => hour)
      .filter((hour) => isFranchiseSlotAllowed(dayOfWeek, hour));
    return hours.length > 0 && hours.every((hour) => isSlotSelected(dayOfWeek, hour));
  };

  const isHourColumnSelected = (hour) => {
    const days = Array.from({ length: 7 }, (_, dayOfWeek) => dayOfWeek)
      .filter((dayOfWeek) => isFranchiseSlotAllowed(dayOfWeek, hour));
    return days.length > 0 && days.every((dayOfWeek) => isSlotSelected(dayOfWeek, hour));
  };

  const isDaypartSelected = (hours) => {
    const slots = [];
    for (let dayOfWeek = 0; dayOfWeek < 7; dayOfWeek++) {
      hours.forEach((hour) => {
        if (isFranchiseSlotAllowed(dayOfWeek, hour)) {
          slots.push({ day_of_week: dayOfWeek, hour });
        }
      });
    }
    return areAllGridSlotsSelected(selectedSlots, slots);
  };

  const getDayName = (dayOfWeek) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayOfWeek];
  };

  const addClockItem = () => {
    if (newItem.minute < 0 || newItem.minute > 59) {
      alert('Minute must be between 0 and 59');
      return;
    }
    
    // Check if item already exists at this minute
    if (clockItems.some(item => {
      if (!item || !item.start_time) return false;
      const [, minutes] = item.start_time.split(':').map(Number);
      return minutes === newItem.minute;
    })) {
      alert('An item already exists at this minute');
      return;
    }

    if (selectedHour === null || selectedHour === undefined) return;
    
    setClockItems([...clockItems, {
      ...newItem,
      start_time: `${selectedHour.toString().padStart(2, '0')}:${newItem.minute.toString().padStart(2, '0')}:00`,
      id: `temp-${Date.now()}`
    }]);
    
    setNewItem({
      minute: 0,
      type: 'playlist',
      playlist_id: null,
      track_id: null,
    });
  };

  const removeClockItem = (index) => {
    setClockItems(clockItems.filter((_, i) => i !== index));
  };

  const saveClock = async () => {
    if (selectedHour === null || selectedHour === undefined) return;
    
    try {
      // Delete existing schedules for this hour
      const hourClock = clocks.find(c => c && c.hour === selectedHour);
      if (hourClock && hourClock.items.length > 0) {
        for (const item of hourClock.items) {
          try {
            await axios.delete(`${API_URL}/api/schedule/${item.id}`);
          } catch (error) {
            // Ignore if already deleted
          }
        }
      }

      // Create new schedules for each clock item
      for (const item of clockItems) {
        if (item.id && item.id.startsWith('temp-')) {
          // New item
          await axios.post(`${API_URL}/api/schedule`, {
            name: `${selectedHour}:${item.start_time.split(':')[1]} - ${item.type}`,
            start_time: item.start_time,
            type: item.type,
            playlist_id: item.playlist_id,
            track_id: item.track_id,
            repeat_type: 'daily',
            enabled: true,
          });
        } else {
          // Existing item - update it
          await axios.put(`${API_URL}/api/schedule/${item.id}`, {
            name: item.name,
            start_time: item.start_time,
            type: item.type,
            playlist_id: item.playlist_id,
            track_id: item.track_id,
            repeat_type: item.repeat_type || 'daily',
            enabled: item.enabled !== false,
          });
        }
      }

      setOpenDialog(false);
      fetchClocks();
    } catch (error) {
      console.error('Error saving clock:', error);
      alert('Error saving clock: ' + error.message);
    }
  };

  const formatHour = (hour) => {
    if (hour === null || hour === undefined) return '00:00';
    return `${hour.toString().padStart(2, '0')}:00`;
  };

  const buildPlaylistGenerationRequest = () => {
    const effectiveStationId = playlistFormData.station_id;
    if (!effectiveStationId || !playlistFormData.date) {
      return {
        error: 'Station and date are required'
      };
    }

    const startHour = playlistFormData.start_hour || 0;
    const endHour = playlistFormData.end_hour !== undefined ? playlistFormData.end_hour : 23;

    if (playlistFormData.generate_range) {
      if (!playlistFormData.end_date) {
        return { error: 'End date is required for date range' };
      }

      return {
        endpoint: `${API_URL}/api/playlist-generator/generate/range`,
        payload: {
          station_id: parseInt(effectiveStationId),
          start_date: playlistFormData.date,
          end_date: playlistFormData.end_date,
          start_hour: startHour,
          end_hour: endHour,
          format: 'm3u',
          overwrite_existing: true,
        },
        isRange: true,
      };
    }

    return {
      endpoint: `${API_URL}/api/playlist-generator/generate`,
      payload: {
        station_id: parseInt(effectiveStationId),
        date: playlistFormData.date,
        start_hour: startHour,
        end_hour: endHour,
        format: 'm3u',
        overwrite_existing: true,
      },
      isRange: false,
    };
  };

  const executePlaylistGeneration = async (request, confirmOverwriteVoicetracks = false) => {
    setPlaylistGenerating(true);
    setGenerationProgress({ percent: 0, message: 'Starting generation...' });

    try {
      const res = await axios.post(
        request.endpoint,
        {
          ...request.payload,
          async: true,
          confirm_overwrite_voicetracks: confirmOverwriteVoicetracks,
        },
        { withCredentials: true }
      );
      const queuePos = res?.data?.queue_position;
      alert(
        queuePos
          ? `Playlist generation queued (position #${queuePos}). It will continue in the background while you use other parts of the app.`
          : 'Playlist generation queued. It will continue in the background while you use other parts of the app.'
      );

      setPlaylistOverwriteWarningOpen(false);
      setPlaylistOverwriteWarning(null);
      setPendingPlaylistGeneration(null);
      setPlaylistDialogOpen(false);
    } catch (error) {
      console.error('Error generating playlist:', error);
      const warningPayload = error.response?.data;
      if (error.response?.status === 409 && warningPayload?.requires_confirmation) {
        setPlaylistOverwriteWarning(warningPayload);
        setPendingPlaylistGeneration(request);
        setPlaylistOverwriteWarningOpen(true);
        return;
      }

      alert('Failed to generate playlist: ' + (error.response?.data?.message || error.message));
    } finally {
      setPlaylistGenerating(false);
    }
  };

  const enabledAutoPlaylistSchedules = autoPlaylistSchedules.filter((schedule) => schedule.enabled);
  const autoScheduleEnabled = enabledAutoPlaylistSchedules.length > 0;
  const nextAutoScheduleRun = enabledAutoPlaylistSchedules
    .map((schedule) => schedule.next_run_at)
    .filter(Boolean)
    .sort()[0];

  const openPlaylistDialog = () => {
    setAutoScheduleWarningOpen(false);
    setPlaylistDialogOpen(true);
  };

  const handleGeneratePlaylistClick = async () => {
    let list = autoPlaylistSchedules;
    if (!autoPlaylistSchedulesLoadedRef.current || autoPlaylistSchedulesRequestRef.current) {
      const fresh = await fetchAutoPlaylistSchedules();
      if (Array.isArray(fresh)) list = fresh;
    }
    if (list.some((schedule) => schedule.enabled)) {
      setAutoScheduleWarningOpen(true);
      return;
    }
    openPlaylistDialog();
  };

  const handleGeneratePlaylist = async (forceProceed = false) => {
    const request = forceProceed ? pendingPlaylistGeneration : buildPlaylistGenerationRequest();
    if (!request) return;
    if (request.error) {
      alert(request.error);
      return;
    }

    await executePlaylistGeneration(request, forceProceed);
  };

  // Estimates the effective duration of a clock item when no explicit duration
  // is set, mirroring the playlist generator's fallbacks (asset duration,
  // category average, break max_duration, 30s voicetracks, 300s relays, 180s default).
  const estimateClockItemDuration = (item) => {
    if (item.duration) return { duration: Number(item.duration), estimated: false };
    if (item.type === 'hard_fixed_timer' || item.type === 'soft_time_marker') {
      return { duration: 0, estimated: false };
    }
    if (item.type === 'category' || item.type === 'asset') {
      if (item.selection_mode === 'specific' && item.asset_id) {
        const asset = assets.find(a => a.id === item.asset_id);
        if (asset && Number(asset.duration) > 0) {
          return { duration: Number(asset.duration), estimated: true };
        }
      } else if (item.category_id) {
        const catAssets = assets.filter(a =>
          Number(a.duration) > 0 && a.categories?.some(c => c.id === item.category_id)
        );
        if (catAssets.length > 0) {
          const avg = catAssets.reduce((sum, a) => sum + Number(a.duration), 0) / catAssets.length;
          return { duration: avg, estimated: true };
        }
      }
      return { duration: 180, estimated: true };
    }
    if (item.type === 'commercial_break') {
      const cb = commercialBreaks.find(b => b.id === item.commercial_break_id);
      return { duration: Number(cb?.max_duration) || 180, estimated: true };
    }
    if (item.type === 'voicetrack' || item.type === 'ai_voicetrack') {
      return { duration: 30, estimated: true };
    }
    if (item.type === 'stream_relay' || item.type === 'webrtc_receiver') {
      return { duration: 300, estimated: true };
    }
    return { duration: 180, estimated: true };
  };

  // Builds segments for the clock wheel view from the current clock items
  const buildClockWheelSegments = () => {
    return [...clockItems]
      .sort((a, b) => (a.position || 0) - (b.position || 0))
      .map((item) => {
        const s = parseJsonObject(item.settings);
        const cat = item.category_id ? categories.find(c => c.id === item.category_id) : null;
        const assetCat = item.asset_id ? assets.find(a => a.id === item.asset_id)?.categories?.[0] : null;
        const color = cat?.color
          || (item.type === 'commercial_break' ? '#ff9800' : null)
          || (item.type === 'voicetrack' || item.type === 'ai_voicetrack' ? '#9c27b0' : null)
          || (item.type === 'stream_relay' ? '#607d8b' : null)
          || (item.type === 'hard_fixed_timer' ? '#f44336' : null)
          || (item.type === 'soft_time_marker' ? '#f57c00' : null)
          || (item.type === 'mixblock' ? '#7c4dff' : null)
          || assetCat?.color
          || '#666666';
        const label =
          item.type === 'category' && item.selection_mode === 'specific' && item.asset_id
            ? `Specific: ${assets.find(a => a.id === item.asset_id)?.title || 'Unknown'}`
            : item.type === 'category' && item.selection_mode === 'random' && item.category_id
            ? `Random from: ${categories.find(c => c.id === item.category_id)?.name || 'Unknown'}`
            : item.type === 'voicetrack'
            ? 'Voicetrack'
            : item.type === 'commercial_break'
            ? 'Commercial Break'
            : item.type === 'stream_relay'
            ? 'Stream Relay'
            : item.type === 'webrtc_receiver'
            ? 'Remote Contribution'
            : item.type === 'ai_voicetrack'
            ? 'AI Voicetrack'
            : item.type === 'hard_fixed_timer'
            ? `Hard Fixed Timer @ :${String(s.fixed_minute ?? 0).padStart(2, '0')}:${String(s.fixed_second ?? 0).padStart(2, '0')}`
            : item.type === 'soft_time_marker'
            ? `Soft Time Marker @ :${String(s.soft_minute ?? 0).padStart(2, '0')}:${String(s.soft_second ?? 0).padStart(2, '0')}`
            : item.type === 'mixblock'
            ? (mixblockDefinitions.find(m => m.id === item.mixblock_id)?.name || 'Mixblock')
            : item.type;
        const isMarker = item.type === 'hard_fixed_timer' || item.type === 'soft_time_marker';
        const required = isMarker ? true : (s.droppable === false);
        const { duration, estimated } = estimateClockItemDuration(item);
        return {
          id: item.id,
          label,
          sublabel: estimated ? 'estimated' : undefined,
          color: color.startsWith('#') ? color : `#${color}`,
          duration,
          required,
          isMarker,
        };
      });
  };

  const mixblocksTab = beatmixEnabled ? 1 : -1;
  const categoriesTab = beatmixEnabled ? 2 : 1;
  const schedulingTab = beatmixEnabled ? 3 : 2;
  const clockEditorReadOnly = !!(editingClock && syndicatedClockIds.includes(String(editingClock.id)));

  return (
    <Box>
      <PageHeader
        overline="Scheduling"
        title="Clocks & Categories"
        subtitle="Design your hour templates and generate playlists"
        icon={<AccessTime />}
        actions={
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {autoScheduleEnabled && (
                <Tooltip
                  title={
                    nextAutoScheduleRun
                      ? `Next automatic run: ${new Date(nextAutoScheduleRun).toLocaleString()}`
                      : 'Automatic station log generation is enabled'
                  }
                >
                  <Chip
                    icon={<ScheduleIcon sx={{ fontSize: '16px !important' }} />}
                    label="Auto schedule on"
                    size="small"
                    sx={{
                      height: 28,
                      bgcolor: 'rgba(251,191,36,0.14)',
                      color: '#fbbf24',
                      border: '1px solid rgba(251,191,36,0.4)',
                      fontWeight: 600,
                      '& .MuiChip-icon': { color: '#fbbf24' },
                    }}
                  />
                </Tooltip>
              )}
              <Button
                variant="contained"
                startIcon={<PlayArrow />}
                onClick={handleGeneratePlaylistClick}
                sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
              >
                Generate Playlist
              </Button>
            </Box>
            {autoScheduleEnabled && (
              <Typography variant="caption" sx={{ color: '#fbbf24', maxWidth: 280, textAlign: 'right', lineHeight: 1.4 }}>
                Automatic generation is enabled. Scheduled runs may overwrite playlists you generate now.
              </Typography>
            )}
          </Box>
        }
      />

      <Tabs
        value={tabValue}
        onChange={(e, v) => setTabValue(v)}
        sx={{ mb: 3, borderBottom: '1px solid rgba(148,163,184,0.12)' }}
      >
        <Tab icon={<AccessTime />} label="Clocks" iconPosition="start" />
        {beatmixEnabled && <Tab icon={<QueueMusicIcon />} label="Mixblocks" iconPosition="start" />}
        <Tab icon={<LabelIcon />} label="Categories" iconPosition="start" />
        <Tab icon={<SettingsIcon />} label="Scheduling Settings" iconPosition="start" />
      </Tabs>

      {/* Clocks Tab */}
      {tabValue === 0 && (
        <Box>
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                {/* Station selector: hide when only one station exists */}
                {stations.length > 1 && (
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Station</InputLabel>
                    <Select
                      value={selectedStation}
                      onChange={(e) => {
                        setSelectedStation(e.target.value);
                        const station = stations.find(s => String(s.id) === String(e.target.value));
                        setBeatmixEnabled(station?.beatmix_enabled || false);
                      }}
                      label="Station"
                    >
                      <MenuItem value="">All Stations</MenuItem>
                      {stations.map((station) => (
                        <MenuItem key={station.id} value={station.id}>
                          {station.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <Box
                  sx={{
                    display: 'inline-flex',
                    p: 0.5,
                    gap: 0.5,
                    borderRadius: 999,
                    bgcolor: 'rgba(148,163,184,0.07)',
                    border: '1px solid rgba(148,163,184,0.14)',
                  }}
                >
                  {[
                    { value: 'list', label: 'Clock List', icon: <ListIcon fontSize="small" /> },
                    { value: 'grid', label: 'Schedule Grid', icon: <GridView fontSize="small" /> },
                  ].map((option) => {
                    const active = clockView === option.value;
                    return (
                      <Button
                        key={option.value}
                        size="small"
                        startIcon={option.icon}
                        onClick={() => setClockView(option.value)}
                        sx={{
                          borderRadius: 999,
                          px: 2,
                          color: active ? '#06222b' : 'text.secondary',
                          backgroundImage: active
                            ? 'linear-gradient(135deg, #67e8f9 0%, #22d3ee 60%, #0891b2 100%)'
                            : 'none',
                          '&:hover': {
                            backgroundImage: active
                              ? 'linear-gradient(135deg, #7deffc 0%, #3ddcf4 60%, #0aa2c7 100%)'
                              : 'none',
                            bgcolor: active ? undefined : 'rgba(148,163,184,0.08)',
                            color: active ? '#06222b' : 'text.primary',
                          },
                        }}
                      >
                        {option.label}
                      </Button>
                    );
                  })}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => handleOpenClockDialog()}
                  sx={{
                    color: '#06222b',
                    backgroundImage: 'linear-gradient(135deg, #67e8f9 0%, #22d3ee 45%, #0891b2 100%)',
                    '&:hover': {
                      backgroundImage: 'linear-gradient(135deg, #7deffc 0%, #3ddcf4 45%, #0aa2c7 100%)',
                      boxShadow: '0 8px 24px -10px rgba(34,211,238,0.6)',
                    },
                  }}
                >
                  New Clock
                </Button>
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Choose list editing or assign clocks directly in the weekly schedule grid.
            </Typography>
          </Box>

          {clockView === 'list' && (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'stretch',
                minHeight: { xs: 560, md: 'calc(var(--rs-vh) - 300px)' },
                flexDirection: { xs: 'column', md: 'row' },
              }}
            >
              <Paper
                sx={{
                  width: { xs: '100%', md: 340 },
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  bgcolor: 'var(--rs-paper)',
                  border: '1px solid var(--rs-border)',
                }}
              >
                <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: '1px solid rgba(148,163,184,0.12)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Clockwheels
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.45 }}>
                      Hour templates — playlist pulls, jingles, exact tracks and talk breaks.
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<Add />}
                    onClick={() => handleOpenClockDialog()}
                    sx={{
                      flexShrink: 0,
                      color: clockEditorOpen && !editingClock ? '#06222b' : '#22d3ee',
                      backgroundImage: clockEditorOpen && !editingClock
                        ? 'linear-gradient(135deg, #67e8f9 0%, #22d3ee 45%, #0891b2 100%)'
                        : 'none',
                      border: clockEditorOpen && !editingClock ? 'none' : '1px solid rgba(34,211,238,0.35)',
                    }}
                  >
                    New
                  </Button>
                </Box>
                <Box sx={{ flex: 1, overflow: 'auto', p: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {clockEditorOpen && !editingClock && (
                    <Box
                      sx={{
                        borderRadius: 2,
                        p: 1.5,
                        border: '1px solid #22d3ee',
                        bgcolor: 'rgba(34,211,238,0.10)',
                        boxShadow: 'inset 3px 0 0 #22d3ee',
                      }}
                    >
                      <Typography variant="subtitle2">New Clock Template</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Draft — save to start adding items
                      </Typography>
                    </Box>
                  )}
                  {filteredClocks.length === 0 && !(clockEditorOpen && !editingClock) ? (
                    <Box sx={{ py: 6, px: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25, textAlign: 'center' }}>
                      <AccessTime sx={{ fontSize: 28, color: '#22d3ee' }} />
                      <Typography variant="body2" color="text.secondary">
                        No clock templates yet. Create one to start scheduling the hour.
                      </Typography>
                    </Box>
                  ) : (
                    filteredClocks.map((clock) => {
                      const clockColor = clock.color || '#22d3ee';
                      const stationName = stations.find(s => String(s.id) === String(clock.station_id))?.name;
                      const hoursPerWeek = scheduledHoursByClock[clock.id] || 0;
                      const isInheritedClock = syndicatedClockIds.includes(String(clock.id));
                      const isSelected = !!(editingClock && String(editingClock.id) === String(clock.id));
                      const slotCount = Number.isFinite(Number(clock.item_count)) ? Number(clock.item_count) : 0;
                      let meta = `${slotCount} slot${slotCount === 1 ? '' : 's'}`;
                      if (isSelected && clockItems.length > 0 && String(editingClockIdRef.current) === String(clock.id)) {
                        const total = clockItems.reduce((sum, item) => sum + estimateClockItemDuration(item).duration, 0);
                        meta += ` · ≈ ${Math.round(total / 60)} min`;
                      }
                      return (
                        <Box
                          key={clock.id}
                          onClick={() => handleOpenClockDialog(clock)}
                          sx={{
                            position: 'relative',
                            borderRadius: 2,
                            p: 1.5,
                            cursor: 'pointer',
                            border: isSelected ? `1px solid ${clockColor}` : '1px solid rgba(148,163,184,0.16)',
                            bgcolor: isSelected ? `${clockColor}14` : 'transparent',
                            boxShadow: isSelected ? `inset 3px 0 0 ${clockColor}` : 'none',
                            transition: 'border-color 140ms ease, background-color 140ms ease',
                            '&:hover': {
                              borderColor: `${clockColor}99`,
                              bgcolor: isSelected ? `${clockColor}14` : 'rgba(148,163,184,0.06)',
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                            <Box
                              sx={{
                                width: 34,
                                height: 34,
                                flexShrink: 0,
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: `${clockColor}22`,
                                border: `1px solid ${clockColor}55`,
                                color: clockColor,
                              }}
                            >
                              <AccessTime sx={{ fontSize: 18 }} />
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography variant="subtitle2" noWrap title={clock.name} sx={{ lineHeight: 1.3 }}>
                                {clock.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                {meta}{stationName ? ` · ${stationName}` : ''}
                              </Typography>
                              {clock.description ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    mt: 0.35,
                                    opacity: 0.85,
                                  }}
                                >
                                  {clock.description}
                                </Typography>
                              ) : null}
                              {(selectedStation || isInheritedClock) && (
                                <Chip
                                  size="small"
                                  label={isInheritedClock
                                    ? 'Parent-owned'
                                    : (hoursPerWeek > 0 ? `${hoursPerWeek} hr${hoursPerWeek === 1 ? '' : 's'}/week` : 'Not scheduled')}
                                  sx={{
                                    mt: 0.75,
                                    height: 22,
                                    fontSize: '0.68rem',
                                    bgcolor: isInheritedClock ? 'rgba(139,92,246,0.16)' : (hoursPerWeek > 0 ? `${clockColor}1f` : 'rgba(148,163,184,0.10)'),
                                    color: isInheritedClock ? '#c4b5fd' : (hoursPerWeek > 0 ? clockColor : 'text.secondary'),
                                    border: isInheritedClock ? '1px solid rgba(139,92,246,0.45)' : (hoursPerWeek > 0 ? `1px solid ${clockColor}44` : '1px solid transparent'),
                                  }}
                                />
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
                              <Tooltip title="Clone clock">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenCloneDialog(clock)}
                                  sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
                                >
                                  <ContentCopy sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={isInheritedClock ? 'Parent-owned clocks cannot be deleted' : 'Delete clock'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteClock(clock.id)}
                                    disabled={isInheritedClock}
                                    sx={{ color: 'text.secondary', '&:hover': { color: '#ff6b6b' } }}
                                  >
                                    <Delete sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Box>
              </Paper>

              <Paper
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  bgcolor: 'var(--rs-paper)',
                  border: '1px solid var(--rs-border)',
                }}
              >
                {clockEditorOpen ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5, borderBottom: '1px solid rgba(148,163,184,0.12)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        bgcolor: `${clockFormData.color || '#22d3ee'}22`,
                        border: `1px solid ${clockFormData.color || '#22d3ee'}66`,
                        color: clockFormData.color || '#22d3ee',
                      }}
                    >
                      <AccessTime fontSize="small" />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="h6" noWrap>
                        {editingClock ? (clockFormData.name || 'Edit Clock Template') : 'New Clock Template'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                        {editingClock
                          ? (clockEditorReadOnly
                            ? 'Parent-owned — view only'
                            : `${clockItems.length} slot${clockItems.length === 1 ? '' : 's'}`)
                          : 'Save the clock to start adding items'}
                      </Typography>
                    </Box>
                  </Box>
                  <Tabs
                    value={clockDialogTab}
                    onChange={(e, v) => setClockDialogTab(v)}
                    sx={{ mt: 1.5, minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 1 } }}
                  >
                    <Tab label="Basic Info" />
                    <Tab label="Clock Items" disabled={!editingClock} />
                    <Tab label="Scheduling Settings" disabled={!editingClock} />
                  </Tabs>
                </Box>

                {clockEditorReadOnly && (
                  <Alert severity="info" sx={{ mx: 2.5, mt: 1.5, mb: 0 }}>
                    This clock is owned by the parent station and cannot be edited here.
                  </Alert>
                )}
                <Box sx={{ flex: 1, overflow: 'auto', px: 2.5, py: 2 }}>
                {clockDialogTab === 0 && (
                  <Box
                    component="fieldset"
                    disabled={clockEditorReadOnly}
                    sx={{ border: 0, m: 0, p: 0, minWidth: 0, width: '100%', display: 'block' }}
                  >
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Clock Name *"
                        value={clockFormData.name}
                        onChange={(e) => setClockFormData({ ...clockFormData, name: e.target.value })}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        multiline
                        rows={2}
                        value={clockFormData.description}
                        onChange={(e) => setClockFormData({ ...clockFormData, description: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Color"
                        type="color"
                        value={clockFormData.color}
                        onChange={(e) => setClockFormData({ ...clockFormData, color: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    {/* Only show station selector when multiple stations (not in single-station mode) */}
                    {stations.length > 1 && (
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel>Station</InputLabel>
                          <Select
                            value={clockFormData.station_id || ''}
                            onChange={(e) => setClockFormData({ ...clockFormData, station_id: e.target.value || null })}
                            label="Station"
                          >
                            <MenuItem value="">All Stations</MenuItem>
                            {stations.map((station) => (
                              <MenuItem key={station.id} value={station.id}>
                                {station.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={clockFormData.autofade_enabled || false}
                            onChange={(e) => setClockFormData({ ...clockFormData, autofade_enabled: e.target.checked, auto_mode: e.target.checked ? false : clockFormData.auto_mode })}
                            disabled={clockFormData.auto_mode}
                          />
                        }
                        label="Enable Autofade"
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 4 }}>
                        Fits items to exactly one hour. Droppable items may be removed, songs may be faded early. Commercials and their surrounding items are always protected.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={clockFormData.auto_mode || false}
                            onChange={(e) => setClockFormData({ ...clockFormData, auto_mode: e.target.checked, autofade_enabled: e.target.checked ? false : clockFormData.autofade_enabled })}
                            disabled={clockFormData.autofade_enabled}
                          />
                        }
                        label="Auto Mode"
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 4 }}>
                        Plays all items without fitting to the hour. If over, next hour starts late. If under, next hour starts early. Ideal for pre-produced shows.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={clockFormData.live_assist || false}
                            onChange={(e) => setClockFormData({ ...clockFormData, live_assist: e.target.checked })}
                          />
                        }
                        label="Live Assist (Studio) Hour"
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 4 }}>
                        Marks this hour as live for Studio Mode. Studio Mode clients sync and play out hours whose clock has this enabled and contains a Remote Contribution item bound to the studio's contribution account.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={clockFormData.voicetrack_collaboration_enabled !== false}
                            onChange={(e) => setClockFormData({
                              ...clockFormData,
                              voicetrack_collaboration_enabled: e.target.checked,
                            })}
                          />
                        }
                        label="Allow Co-presented Voicetracks"
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 4 }}>
                        Lets a presenter invite another presenter into this clock's voicetrack sessions. The app-wide
                        switch in Settings must also be on; turning it off here blocks sessions for this clock only.
                      </Typography>
                    </Grid>
                    </Grid>
                  </Box>
                )}

                {clockDialogTab === 1 && editingClock && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ color: '#22d3ee' }}>
                        Clock Items
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        <ToggleButtonGroup
                          value={clockItemsView}
                          exclusive
                          size="small"
                          onChange={(e, v) => v && setClockItemsView(v)}
                        >
                          <ToggleButton value="list" title="List view">
                            <ListIcon fontSize="small" />
                          </ToggleButton>
                          <ToggleButton value="wheel" title="Clock wheel view">
                            <DonutLargeIcon fontSize="small" />
                          </ToggleButton>
                        </ToggleButtonGroup>
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={() => handleOpenClockItemDialog()}
                          disabled={clockEditorReadOnly}
                          sx={{ bgcolor: '#22d3ee' }}
                        >
                          Add Item
                        </Button>
                      </Box>
                    </Box>

                    {/* Duration Status */}
                    {clockItems.length > 0 && (() => {
                      let hasEstimates = false;
                      const totalDuration = clockItems.reduce((sum, item) => {
                        const est = estimateClockItemDuration(item);
                        if (est.estimated) hasEstimates = true;
                        return sum + est.duration;
                      }, 0);
                      const targetDuration = 3600; // 1 hour
                      const difference = totalDuration - targetDuration;
                      const isOver = difference > 0;
                      const isUnder = difference < 0;
                      const fits = Math.abs(difference) < 1; // Within 1 second
                      const itemSettings = clockItems.map(item => {
                        const s = parseJsonObject(item.settings);
                        return {
                          ...item,
                          droppable: item.type === 'hard_fixed_timer' || item.type === 'soft_time_marker'
                            ? false
                            : (s.droppable !== false)
                        };
                      });
                      const droppableItems = itemSettings.filter(item => item.droppable);
                      const droppableDuration = droppableItems.reduce((sum, item) => sum + estimateClockItemDuration(item).duration, 0);

                      return (
                        <Alert 
                          severity={fits ? 'success' : (isOver && droppableDuration >= difference ? 'warning' : 'error')} 
                          sx={{ mb: 2 }}
                        >
                          <Typography variant="body2">
                            <strong>Total Duration:</strong> {Math.round(totalDuration)}s / {targetDuration}s
                            {hasEstimates && <> (estimated — some items have no fixed duration)</>}
                            {!fits && (
                              <>
                                <br />
                                {isOver ? (
                                  <>Over by {Math.round(difference)}s. {droppableDuration >= difference ? `${droppableItems.length} droppable items (${Math.round(droppableDuration)}s) can be removed.` : 'Not enough droppable items to fit.'}</>
                                ) : (
                                  <>Under by {Math.round(Math.abs(difference))}s. Add more items or increase durations.</>
                                )}
                              </>
                            )}
                            {clockFormData.autofade_enabled && !fits && isOver && droppableDuration >= difference && (
                              <><br /><strong>Autofade:</strong> Items will be automatically dropped/faded to fit the hour. Commercials and their surrounding items are protected.</>
                            )}
                            {clockFormData.auto_mode && !fits && (
                              <><br /><strong>Auto Mode:</strong> {isOver ? 'Next hour will start late.' : 'Next hour will start early.'} All items will play in full.</>
                            )}
                          </Typography>
                        </Alert>
                      );
                    })()}

                    {clockItems.length === 0 ? (
                      <Alert severity="info">
                        No clock items yet. Click "Add Item" to start building your clock structure.
                      </Alert>
                    ) : clockItemsView === 'wheel' ? (
                      <ClockWheel
                        segments={buildClockWheelSegments()}
                        targetDuration={3600}
                        onEdit={(id) => {
                          if (clockEditorReadOnly) return;
                          const it = clockItems.find(i => i.id === id);
                          if (it) handleOpenClockItemDialog(it);
                        }}
                        onDuplicate={(id) => {
                          if (clockEditorReadOnly) return;
                          const it = clockItems.find(i => i.id === id);
                          if (it) handleDuplicateClockItem(it);
                        }}
                        onDelete={(id) => {
                          if (clockEditorReadOnly) return;
                          handleDeleteClockItem(id);
                        }}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {clockItems
                          .slice()
                          .sort((a, b) => (a.position || 0) - (b.position || 0))
                          .map((item, index) => {
                            const cat = item.category_id ? categories.find(c => c.id === item.category_id) : null;
                            const assetCat = item.asset_id ? assets.find(a => a.id === item.asset_id)?.categories?.[0] : null;
                            const categoryColor = cat?.color
                              || (item.type === 'commercial_break' ? '#f59e0b' : null)
                              || (item.type === 'voicetrack' || item.type === 'ai_voicetrack' ? '#a855f7' : null)
                              || (item.type === 'stream_relay' ? '#607d8b' : null)
                              || (item.type === 'hard_fixed_timer' ? '#ef4444' : null)
                              || (item.type === 'soft_time_marker' ? '#f57c00' : null)
                              || (item.type === 'mixblock' ? '#7c4dff' : null)
                              || assetCat?.color
                              || '#666666';
                            const itemColor = categoryColor || '#666666';
                            const hex = itemColor.startsWith('#') ? itemColor : `#${itemColor}`;
                            const fullHex = hex.length === 4 ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}` : hex;
                            return (
                            <Paper
                              key={item.id || index}
                              draggable={!clockEditorReadOnly}
                              onDragStart={() => { if (!clockEditorReadOnly) handleClockItemDragStart(index); }}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={() => handleClockItemDrop(index)}
                              onDragEnd={handleClockItemDragEnd}
                              sx={{
                                p: 2,
                                bgcolor: `${fullHex}15`,
                                border: '1px solid',
                                borderColor: `${fullHex}60`,
                                borderLeft: `4px solid ${fullHex}`,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 1,
                                cursor: clockEditorReadOnly ? 'default' : 'grab',
                              }}
                            >
                              <DragIndicator sx={{ color: 'text.secondary', flexShrink: 0, opacity: 0.7 }} fontSize="small" />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    #{index + 1} · {
                                      item.type === 'category' && item.selection_mode === 'specific' && item.asset_id
                                        ? `Specific Item: ${assets.find(a => a.id === item.asset_id)?.title || 'Unknown'}`
                                        : item.type === 'category' && item.selection_mode === 'random' && item.category_id
                                        ? `Random from: ${categories.find(c => c.id === item.category_id)?.name || 'Unknown'}`
                                        : item.type === 'voicetrack'
                                        ? 'Voicetrack'
                                        : item.type === 'commercial_break'
                                        ? 'Commercial Break'
                                        : item.type === 'stream_relay'
                                        ? 'Stream Relay'
                                        : item.type === 'webrtc_receiver'
                                        ? 'Remote Contribution'
                                        : item.type === 'ai_voicetrack'
                                        ? 'AI Voicetrack'
                                        : item.type === 'hard_fixed_timer'
                                        ? (() => {
                                            const s = parseJsonObject(item.settings);
                                            const m = s.fixed_minute != null ? String(s.fixed_minute).padStart(2, '0') : '00';
                                            const sec = s.fixed_second != null ? String(s.fixed_second).padStart(2, '0') : '00';
                                            return `Hard Fixed Timer @ :${m}:${sec}`;
                                          })()
                                        : item.type === 'soft_time_marker'
                                        ? (() => {
                                            const s = parseJsonObject(item.settings);
                                            const m = s.soft_minute != null ? String(s.soft_minute).padStart(2, '0') : '00';
                                            const sec = s.soft_second != null ? String(s.soft_second).padStart(2, '0') : '00';
                                            const b = s.soft_boundary_seconds != null ? parseInt(s.soft_boundary_seconds, 10) : null;
                                            return `Soft Time Marker @ :${m}:${sec}${(b && b > 0) ? ` (+${b}s force)` : ''}`;
                                          })()
                                        : item.type
                                    }
                                  </Typography>
                                  {(() => {
                                    const itemSettings = parseJsonObject(item.settings);
                                    const isDroppable = item.type === 'hard_fixed_timer' || item.type === 'soft_time_marker'
                                      ? false
                                      : (itemSettings.droppable !== false);
                                    return isDroppable ? (
                                      <Chip label="Droppable" size="small" sx={{ bgcolor: '#f59e0b', color: '#0a0c11', fontSize: '0.65rem', height: '20px' }} />
                                    ) : (
                                      <Chip label="Required" size="small" sx={{ bgcolor: '#22c55e', color: '#fff', fontSize: '0.65rem', height: '20px' }} />
                                    );
                                  })()}
                                  {item.duration && (
                                    <Typography variant="caption" color="text.secondary">
                                      ({Math.round(item.duration)}s)
                                    </Typography>
                                  )}
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                  {item.type === 'category' ? 'Category' :
                                   item.type === 'voicetrack' ? 'Voicetrack' :
                                   item.type === 'commercial_break' ? 'Commercial Break' :
                                   item.type === 'stream_relay' ? 'Stream Relay' :
                                   item.type === 'webrtc_receiver' ? 'Remote Contribution' :
                                   item.type === 'ai_voicetrack' ? 'AI Voicetrack' :
                                   item.type === 'hard_fixed_timer' ? 'Hard Fixed Timer' :
                                   item.type === 'soft_time_marker' ? 'Soft Time Marker' :
                                   item.type === 'mixblock' ? 'Mixblock' :
                                   item.type}
                                  {item.commercial_break_id && ` · Break ID: ${item.commercial_break_id}`}
                                  {item.type === 'mixblock' && item.mixblock_id && (() => {
                                    const mb = mixblockDefinitions.find(m => m.id === item.mixblock_id);
                                    return mb ? ` · ${mb.name}` : ` · Mixblock #${item.mixblock_id}`;
                                  })()}
                                  {item.type === 'ai_voicetrack' && (() => {
                                    const s = parseJsonObject(item.settings);
                                    const promptName = s.ai_prompt_id ? (aiPrompts.find(p => p.id === s.ai_prompt_id)?.name || `Prompt #${s.ai_prompt_id}`) : null;
                                    const voiceName = s.ai_voice_id ? (aiVoices.find(v => v.id === s.ai_voice_id)?.name || `Voice #${s.ai_voice_id}`) : null;
                                    return (promptName || voiceName) ? ` · ${promptName || 'No Prompt'}${voiceName ? ` / ${voiceName}` : ''}` : '';
                                  })()}
                                  {(item.sweep_over_intro) && ' · Sweep'}
                                  {(item.music_bed_enabled) && ' · Music Bed'}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDuplicateClockItem(item)}
                                  disabled={clockEditorReadOnly}
                                  sx={{ color: '#9e9e9e' }}
                                  title="Duplicate"
                                >
                                  <ContentCopy fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenClockItemDialog(item)}
                                  disabled={clockEditorReadOnly}
                                  sx={{ color: '#22d3ee' }}
                                  title="Edit"
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteClockItem(item.id)}
                                  disabled={clockEditorReadOnly}
                                  sx={{ color: '#ff6b6b' }}
                                  title="Delete"
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Box>
                            </Paper>
                            );
                          })}
                      </Box>
                    )}
                  </Box>
                )}

                {clockDialogTab === 2 && editingClock && (
                  <Box
                    component="fieldset"
                    disabled={clockEditorReadOnly}
                    sx={{ border: 0, m: 0, p: 0, minWidth: 0, width: '100%', display: 'block' }}
                  >
                    <Typography variant="h6" sx={{ color: '#22d3ee', mb: 3 }}>
                      Scheduling Settings for {clockFormData.name}
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            By default, all clocks use the default rule set configured in Scheduling Settings.
                            You can opt out and use a custom rule set for this clock.
                          </Typography>
                        </Alert>
                      </Grid>

                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={clockFormData.use_default_rule_set}
                              onChange={(e) => setClockFormData({
                                ...clockFormData,
                                use_default_rule_set: e.target.checked,
                                custom_rule_set_id: e.target.checked ? null : clockFormData.custom_rule_set_id
                              })}
                            />
                          }
                          label="Use Default Rule Set"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 4 }}>
                          {clockFormData.use_default_rule_set
                            ? `Using default rule set: ${ruleSets.find(rs => rs.id === schedulingSettings.default_rule_set_id)?.name || 'None'}`
                            : 'This clock will use a custom rule set'}
                        </Typography>
                      </Grid>

                      {!clockFormData.use_default_rule_set && (
                        <Grid item xs={12}>
                          <FormControl fullWidth>
                            <InputLabel>Custom Rule Set</InputLabel>
                            <Select
                              value={clockFormData.custom_rule_set_id || ''}
                              onChange={(e) => setClockFormData({ ...clockFormData, custom_rule_set_id: e.target.value || null })}
                              label="Custom Rule Set"
                            >
                              <MenuItem value="">None</MenuItem>
                              {ruleSets.map((rs) => (
                                <MenuItem key={rs.id} value={rs.id}>
                                  {rs.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle1" sx={{ color: '#22d3ee', mb: 1 }}>
                          Category Goals
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Optional per-hour counts. Clock items with "Count toward category goals" stop filling once max or target is reached. Autofade will not drop a category below its min.
                        </Typography>
                        {(() => {
                          const usedIds = new Set(
                            clockItems
                              .map((item) => Number(item.category_id))
                              .filter((id) => Number.isInteger(id) && id > 0)
                          );
                          const goalIds = Object.keys(clockFormData.category_goals || {})
                            .map((id) => parseInt(id, 10))
                            .filter((id) => Number.isInteger(id) && id > 0);
                          const rows = baseClockItemCategories.filter((cat) => (
                            usedIds.has(Number(cat.id)) || goalIds.includes(Number(cat.id))
                          ));
                          const display = rows.length > 0 ? rows : baseClockItemCategories;
                          if (display.length === 0) {
                            return (
                              <Typography variant="body2" color="text.secondary">
                                Add category items to this clock to set goals.
                              </Typography>
                            );
                          }
                          const updateGoal = (categoryId, field, value) => {
                            const next = { ...(clockFormData.category_goals || {}) };
                            const key = String(categoryId);
                            const current = { ...(next[categoryId] || next[key] || {}) };
                            const parsed = value === '' ? 0 : parseInt(value, 10);
                            current[field] = Number.isInteger(parsed) && parsed > 0 ? parsed : 0;
                            delete next[key];
                            if (!current.min && !current.max && !current.target) {
                              delete next[categoryId];
                            } else {
                              next[categoryId] = current;
                            }
                            setClockFormData({ ...clockFormData, category_goals: next });
                          };
                          return display.map((cat) => {
                            const goal = (clockFormData.category_goals || {})[cat.id]
                              || (clockFormData.category_goals || {})[String(cat.id)]
                              || {};
                            return (
                              <Grid container spacing={1} key={cat.id} sx={{ mb: 1, alignItems: 'center' }}>
                                <Grid item xs={12} sm={3}>
                                  <Typography variant="body2">{cat.name}</Typography>
                                </Grid>
                                <Grid item xs={4} sm={3}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Min"
                                    inputProps={{ min: 0 }}
                                    value={goal.min || ''}
                                    onChange={(e) => updateGoal(cat.id, 'min', e.target.value)}
                                  />
                                </Grid>
                                <Grid item xs={4} sm={3}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Target"
                                    inputProps={{ min: 0 }}
                                    value={goal.target || ''}
                                    onChange={(e) => updateGoal(cat.id, 'target', e.target.value)}
                                  />
                                </Grid>
                                <Grid item xs={4} sm={3}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Max"
                                    inputProps={{ min: 0 }}
                                    value={goal.max || ''}
                                    onChange={(e) => updateGoal(cat.id, 'max', e.target.value)}
                                  />
                                </Grid>
                              </Grid>
                            );
                          });
                        })()}
                      </Grid>

                    </Grid>
                  </Box>
                )}
                </Box>
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    borderTop: '1px solid rgba(148,163,184,0.12)',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1,
                    flexShrink: 0,
                  }}
                >
                <Button onClick={handleCloseClockDialog}>Close</Button>
                <Button onClick={handleSaveClock} variant="contained" disabled={clockEditorReadOnly} sx={{ bgcolor: '#22d3ee' }}>
                  {editingClock ? 'Update' : 'Create'}
                </Button>
                </Box>
              </Box>
                ) : (
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, p: 4, textAlign: 'center' }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(34,211,238,0.08)',
                        border: '1px solid rgba(34,211,238,0.25)',
                      }}
                    >
                      <AccessTime sx={{ fontSize: 30, color: '#22d3ee' }} />
                    </Box>
                    <Typography variant="h6">
                      {filteredClocks.length === 0 ? 'No clock templates yet' : 'Select a clock'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
                      {filteredClocks.length === 0
                        ? 'Clock templates define the structure of each broadcast hour — music, jingles, adverts and more.'
                        : 'Click a clock on the left to edit its items and settings here, without opening a popup.'}
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={() => handleOpenClockDialog()}
                      sx={{ mt: 0.5, borderColor: 'rgba(34,211,238,0.4)', color: '#22d3ee' }}
                    >
                      {filteredClocks.length === 0 ? 'Create your first clock' : 'New Clock'}
                    </Button>
                  </Box>
                )}
              </Paper>
            </Box>
          )}

          {clockView === 'grid' && (
            <Box>
              {!selectedStation ? (
                <Alert severity="info">Please select a station to view the schedule grid.</Alert>
              ) : (
                <>
                  {selectedSyndicationLink && !selectedRegion && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      This grid follows the parent station. Parent cells are read-only; local clock overrides are
                      {selectedSyndicationLink.override_mode === 'none'
                        ? ' disabled for this franchise.'
                        : selectedSyndicationLink.override_mode === 'all'
                          ? ' allowed for all hours.'
                          : ' allowed only for the hours approved by the parent.'}
                    </Alert>
                  )}
                  {!selectedSyndicationLink && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                      <FormControl size="small" sx={{ minWidth: 240 }}>
                        <InputLabel>Saved grid</InputLabel>
                        <Select
                          value={activeScheduleGrid ? String(activeScheduleGrid.id) : ''}
                          label="Saved grid"
                          onChange={(e) => handleSelectScheduleGrid(e.target.value)}
                        >
                          {scheduleGrids.length === 0 && (
                            <MenuItem value="" disabled>Loading grids…</MenuItem>
                          )}
                          {scheduleGrids.map((grid) => (
                            <MenuItem key={grid.id} value={String(grid.id)}>
                              {grid.name}
                              {grid.is_active ? ' (active)' : ''}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<BookmarkAddIcon />}
                        onClick={() => handleOpenScheduleGridDialog('saveAs')}
                      >
                        Save as
                      </Button>
                      <IconButton
                        size="small"
                        onClick={(e) => setScheduleGridMenuAnchor(e.currentTarget)}
                        aria-label="Saved grid actions"
                      >
                        <MoreVertIcon />
                      </IconButton>
                      <Menu
                        anchorEl={scheduleGridMenuAnchor}
                        open={Boolean(scheduleGridMenuAnchor)}
                        onClose={closeScheduleGridMenu}
                      >
                        <MenuItem onClick={() => handleOpenScheduleGridDialog('rename')}>Rename</MenuItem>
                        <MenuItem onClick={() => handleOpenScheduleGridDialog('empty')}>New empty grid</MenuItem>
                        <MenuItem
                          onClick={handleDeleteScheduleGrid}
                          disabled={scheduleGrids.length <= 1}
                        >
                          Delete grid
                        </MenuItem>
                      </Menu>
                      <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 420 }}>
                        Saved per station. Hour edits stay on the active grid; save a copy when next week looks different.
                      </Typography>
                    </Box>
                  )}
                  {/* Region selector - only show when station has regions */}
                  {regions.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <FormControl sx={{ minWidth: 220 }}>
                        <InputLabel>Schedule View</InputLabel>
                        <Select
                          value={selectedRegion}
                          onChange={(e) => {
                            setSelectedRegion(e.target.value);
                            setSelectedSlots([]);
                          }}
                          label="Schedule View"
                          size="small"
                        >
                          <MenuItem value="">Master Schedule</MenuItem>
                          <Divider />
                          {regions.map((region) => (
                            <MenuItem key={region.id} value={region.id}>
                              {region.name} {region.code ? `(${region.code})` : ''}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {selectedRegion && (
                        <Chip
                          label={`Viewing: ${regions.find(r => r.id === parseInt(selectedRegion))?.name || 'Region'} — overrides shown with border`}
                          color="info"
                          variant="outlined"
                          size="small"
                        />
                      )}
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleClearSelection}
                        disabled={selectedSlots.length === 0}
                      >
                        Clear
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleSelectAllHours}
                      >
                        Select all
                      </Button>
                      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                      {GRID_DAYPARTS.map((part) => {
                        const active = isDaypartSelected(part.hours);
                        return (
                          <Button
                            key={part.id}
                            variant={active ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => handleSelectDaypart(part.hours)}
                            sx={{
                              minWidth: 0,
                              color: active ? '#06222b' : 'text.secondary',
                              bgcolor: active ? '#22d3ee' : 'transparent',
                              '&:hover': {
                                bgcolor: active ? '#3ddcf4' : 'rgba(34,211,238,0.10)',
                              },
                            }}
                          >
                            {part.label}
                          </Button>
                        );
                      })}
                      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                      <Box
                        sx={{
                          display: 'inline-flex',
                          p: 0.35,
                          gap: 0.35,
                          borderRadius: 999,
                          bgcolor: 'rgba(148,163,184,0.07)',
                          border: '1px solid rgba(148,163,184,0.14)',
                        }}
                      >
                        {['compact', 'readable'].map((density) => {
                          const active = gridDensity === density;
                          return (
                            <Button
                              key={density}
                              size="small"
                              onClick={() => setGridDensity(density)}
                              sx={{
                                minWidth: 74,
                                borderRadius: 999,
                                px: 1.5,
                                color: active ? '#06222b' : 'text.secondary',
                                backgroundImage: active
                                  ? 'linear-gradient(135deg, #67e8f9 0%, #22d3ee 60%, #0891b2 100%)'
                                  : 'none',
                                '&:hover': {
                                  bgcolor: active ? undefined : 'rgba(148,163,184,0.08)',
                                  color: active ? '#06222b' : 'text.primary',
                                },
                              }}
                            >
                              {density === 'compact' ? 'Compact' : 'Readable'}
                            </Button>
                          );
                        })}
                      </Box>
                    </Box>
                    {selectedSlots.length > 0 && (
                      <Button
                        variant="contained"
                        onClick={() => { setAssignMode('clock'); setAssignClockDialogOpen(true); }}
                        sx={{
                          color: '#06222b',
                          backgroundImage: 'linear-gradient(135deg, #67e8f9 0%, #22d3ee 45%, #0891b2 100%)',
                          '&:hover': {
                            backgroundImage: 'linear-gradient(135deg, #7deffc 0%, #3ddcf4 45%, #0aa2c7 100%)',
                          },
                        }}
                      >
                        Assign Clock ({selectedSlots.length} selected)
                      </Button>
                    )}
                  </Box>

                  <Paper
                    sx={{
                      overflow: 'auto',
                      bgcolor: 'var(--rs-paper)',
                      border: '1px solid var(--rs-border)',
                      userSelect: 'none',
                      cursor: gridSelecting ? 'cell' : 'default',
                    }}
                    onDragStart={(e) => e.preventDefault()}
                  >
                    <Box sx={{ display: 'grid', gridTemplateColumns: '120px repeat(24, minmax(42px, 1fr))', gap: gridDensity === 'compact' ? 0.35 : 0.5, p: 1 }}>
                      <Box
                        sx={{
                          p: 1,
                          fontWeight: 'bold',
                          color: '#22d3ee',
                          position: 'sticky',
                          top: 0,
                          left: 0,
                          zIndex: 3,
                          bgcolor: 'var(--rs-paper)',
                        }}
                      >
                        Day / Hour
                      </Box>
                      {Array.from({ length: 24 }, (_, hour) => {
                        const hourSelected = isHourColumnSelected(hour);
                        return (
                          <Box
                            key={hour}
                            onClick={() => handleSelectHour(hour)}
                            title={`Select all ${hour.toString().padStart(2, '0')}:00 hours`}
                            sx={{
                              p: 0.5,
                              textAlign: 'center',
                              fontSize: '0.65rem',
                              whiteSpace: 'nowrap',
                              fontWeight: hourSelected ? 700 : 500,
                              color: hourSelected ? '#06222b' : '#999',
                              bgcolor: hourSelected ? '#22d3ee' : 'var(--rs-paper)',
                              borderRadius: 1,
                              cursor: gridSelecting ? 'cell' : 'pointer',
                              pointerEvents: gridSelecting ? 'none' : 'auto',
                              position: 'sticky',
                              top: 0,
                              zIndex: 2,
                              '&:hover': {
                                bgcolor: hourSelected ? '#3ddcf4' : 'rgba(34,211,238,0.12)',
                                color: hourSelected ? '#06222b' : '#22d3ee',
                              },
                            }}
                          >
                            {hour.toString().padStart(2, '0')}:00
                          </Box>
                        );
                      })}

                      {Array.from({ length: 7 }, (_, dayOfWeek) => {
                        const dayName = getDayName(dayOfWeek);
                        const daySelected = isDayRowSelected(dayOfWeek);
                        return (
                          <React.Fragment key={dayOfWeek}>
                            <Box
                              onClick={() => handleSelectDay(dayOfWeek)}
                              title={`Select all hours on ${dayName}`}
                              sx={{
                                p: 1,
                                fontWeight: 700,
                                color: daySelected ? '#06222b' : '#22d3ee',
                                bgcolor: daySelected ? '#22d3ee' : 'var(--rs-paper)',
                                borderRight: '1px solid var(--rs-border)',
                                borderRadius: 1,
                                cursor: gridSelecting ? 'cell' : 'pointer',
                                pointerEvents: gridSelecting ? 'none' : 'auto',
                                position: 'sticky',
                                left: 0,
                                zIndex: 2,
                                '&:hover': {
                                  bgcolor: daySelected ? '#3ddcf4' : 'rgba(34,211,238,0.12)',
                                },
                              }}
                            >
                              {dayName}
                            </Box>
                            {Array.from({ length: 24 }, (_, hour) => {
                              const schedule = getScheduleForSlot(dayOfWeek, hour);
                              const isSelected = isSlotSelected(dayOfWeek, hour);
                              const clock = schedule ? clockTemplates.find(c => c.id === schedule.clock_id) : null;
                              const hasRegionalOverride = isRegionalOverride(dayOfWeek, hour);
                              const hasFranchiseOverride = isFranchiseOverride(dayOfWeek, hour);
                              const isRepeatHour = schedule
                                && schedule.repeat_from_day != null
                                && schedule.repeat_from_hour != null
                                && !hasFranchiseOverride
                                && !hasRegionalOverride;
                              const repeatText = isRepeatHour
                                ? `Rpt ${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][Number(schedule.repeat_from_day)] || ''} ${String(schedule.repeat_from_hour).padStart(2, '0')}`
                                : null;
                              const franchiseSlotAllowed = isFranchiseSlotAllowed(dayOfWeek, hour);
                              const slotLabel = hasRegionalOverride
                                ? `Regional override: ${clock?.name || 'None'}`
                                : hasFranchiseOverride
                                  ? `Local franchise override: ${clock?.name || 'None'}`
                                  : isRepeatHour
                                    ? `Repeats ${['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][Number(schedule.repeat_from_day)]} ${String(schedule.repeat_from_hour).padStart(2, '0')}:00 — ads are local to this hour`
                                  : selectedRegion && clock
                                    ? `${clock.name} (from master)`
                                    : clock
                                      ? clock.name
                                      : 'Empty hour';

                              return (
                                <Box
                                  key={`${dayOfWeek}-${hour}`}
                                  data-grid-slot="true"
                                  data-day={dayOfWeek}
                                  data-hour={hour}
                                  onPointerDown={(e) => handleSlotPointerDown(dayOfWeek, hour, e)}
                                  sx={{
                                    p: 0.5,
                                    minHeight: gridDensity === 'compact' ? '38px' : '48px',
                                    border: isSelected
                                      ? '2px solid #22d3ee'
                                      : hasRegionalOverride
                                        ? '2px solid #f59e0b'
                                        : hasFranchiseOverride
                                          ? '2px solid #8b5cf6'
                                          : isRepeatHour
                                            ? '2px dashed #a78bfa'
                                          : '1px solid var(--rs-border)',
                                    bgcolor: isSelected
                                      ? '#22d3ee20'
                                      : hasRegionalOverride
                                        ? (clock ? (clock.color || '#f59e0b') + '60' : '#f59e0b30')
                                        : hasFranchiseOverride
                                          ? (clock ? (clock.color || '#8b5cf6') + '60' : '#8b5cf630')
                                          : isRepeatHour
                                            ? (clock ? (clock.color || '#8b5cf6') + '40' : '#8b5cf630')
                                          : (clock ? (clock.color || '#1976D2') + '40' : 'var(--rs-bg)'),
                                    cursor: franchiseSlotAllowed ? (gridSelecting ? 'cell' : 'pointer') : 'not-allowed',
                                    opacity: franchiseSlotAllowed ? 1 : 0.78,
                                    touchAction: 'none',
                                    '&:hover': {
                                      bgcolor: isSelected ? '#22d3ee30' : '#22d3ee10',
                                    },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.7rem',
                                    position: 'relative',
                                  }}
                                  title={`${getDayName(dayOfWeek)} ${hour.toString().padStart(2, '0')}:00 — ${slotLabel}. Click to toggle, drag to select a block.`}
                                >
                                  {isRepeatHour && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: '#c4b5fd',
                                        textAlign: 'center',
                                        lineHeight: 1.1,
                                        fontSize: gridDensity === 'compact' ? '0.58rem' : '0.66rem',
                                        fontWeight: 700,
                                        pointerEvents: 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.25,
                                      }}
                                    >
                                      <RepeatIcon sx={{ fontSize: gridDensity === 'compact' ? 10 : 12 }} />
                                      {repeatText}
                                    </Typography>
                                  )}
                                  {clock && !isRepeatHour && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: 'var(--rs-text)',
                                        textAlign: 'center',
                                        lineHeight: gridDensity === 'compact' ? 1.05 : 1.15,
                                        fontSize: gridDensity === 'compact' ? '0.64rem' : '0.72rem',
                                        fontWeight: 600,
                                        maxWidth: '100%',
                                        display: '-webkit-box',
                                        WebkitLineClamp: gridDensity === 'compact' ? 1 : 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        wordBreak: 'break-word',
                                        px: 0.25,
                                        pointerEvents: 'none',
                                      }}
                                    >
                                      {clock.name}
                                    </Typography>
                                  )}
                                  {hasRegionalOverride && (
                                    <Typography variant="caption" sx={{ color: '#f59e0b', fontSize: '0.55rem', lineHeight: 1, pointerEvents: 'none' }}>
                                      region
                                    </Typography>
                                  )}
                                  {selectedRegion && !hasRegionalOverride && clock && (
                                    <Typography variant="caption" sx={{ color: '#666', fontSize: '0.55rem', lineHeight: 1, pointerEvents: 'none' }}>
                                      master
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </Box>
                  </Paper>

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.55 }}>
                    Click hours to add or remove them. Click and drag to select a block — no Shift or Ctrl needed.
                    Click a day name or hour header to toggle that row or column. Repeat another hour to copy a show
                    (including voicetracks) and keep this hour's ads. Save extra grids if a week needs a different
                    layout, then load the one you want before generating playlists.
                  </Typography>
                </>
              )}
            </Box>
          )}

          {/* Saved Grid Dialog */}
          <Dialog
            open={Boolean(scheduleGridDialog)}
            onClose={() => setScheduleGridDialog(null)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>
              {scheduleGridDialog?.mode === 'rename'
                ? 'Rename grid'
                : scheduleGridDialog?.mode === 'empty'
                  ? 'New empty grid'
                  : 'Save grid as'}
            </DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                fullWidth
                label="Grid name"
                value={scheduleGridDialog?.name || ''}
                onChange={(e) => setScheduleGridDialog((prev) => (prev ? { ...prev, name: e.target.value } : prev))}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSubmitScheduleGridDialog(); }}
                sx={{ mt: 2 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setScheduleGridDialog(null)}>Cancel</Button>
              <Button onClick={handleSubmitScheduleGridDialog} variant="contained" sx={{ bgcolor: '#22d3ee' }}>
                {scheduleGridDialog?.mode === 'rename' ? 'Rename' : 'Save'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Clone Clock Dialog */}
          <Dialog open={cloneDialogOpen} onClose={handleCloseCloneDialog} maxWidth="xs" fullWidth>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ContentCopy sx={{ color: '#22d3ee' }} />
                <Typography variant="h6">Clone Clock</Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                fullWidth
                label="New Clock Name"
                value={cloneClockName}
                onChange={(e) => setCloneClockName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmClone(); }}
                sx={{ mt: 2 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseCloneDialog}>Cancel</Button>
              <Button onClick={handleConfirmClone} variant="contained" sx={{ bgcolor: '#22d3ee' }}>
                Clone
              </Button>
            </DialogActions>
          </Dialog>

          {/* Clock Item Dialog */}
          <Dialog open={clockItemDialogOpen} onClose={handleCloseClockItemDialog} maxWidth="md" fullWidth>
            <DialogTitle>
              <Typography variant="h6">
                {editingClockItem ? 'Edit Clock Item' : 'New Clock Item'}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type *</InputLabel>
                    <Select
                      value={clockItemFormData.type}
                      onChange={(e) => setClockItemFormData({ ...clockItemFormData, type: e.target.value, selection_mode: e.target.value === 'category' ? 'random' : clockItemFormData.selection_mode, asset_id: null, category_id: null })}
                      label="Type *"
                    >
                      <MenuItem value="category">Category</MenuItem>
                      <MenuItem value="voicetrack">Voicetrack</MenuItem>
                      <MenuItem value="commercial_break">Commercial Break</MenuItem>
                      <MenuItem value="stream_relay">Stream Relay</MenuItem>
                      <MenuItem value="webrtc_receiver">Remote Contribution</MenuItem>
                      <MenuItem value="ai_voicetrack">AI Voicetrack</MenuItem>
                      <MenuItem value="hard_fixed_timer">Hard Fixed Timer</MenuItem>
                      <MenuItem value="soft_time_marker">Soft Time Marker</MenuItem>
                      {beatmixEnabled && <MenuItem value="mixblock">Mixblock</MenuItem>}
                    </Select>
                  </FormControl>
                </Grid>

                {clockItemFormData.type === 'category' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Selection Mode</InputLabel>
                      <Select
                        value={clockItemFormData.selection_mode}
                        onChange={(e) => setClockItemFormData({ ...clockItemFormData, selection_mode: e.target.value, asset_id: null })}
                        label="Selection Mode"
                      >
                        <MenuItem value="specific">Specific Item</MenuItem>
                        <MenuItem value="random">Random from Category</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {clockItemFormData.type === 'category' && (
                  <>
                    {baseClockItemCategories.length > 8 && (
                      <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Filter by Type</InputLabel>
                          <Select
                            value={clockItemCategoryTypeFilter}
                            onChange={(e) => { setClockItemCategoryTypeFilter(e.target.value); setClockItemFormData({ ...clockItemFormData, category_id: null, asset_id: null }); }}
                            label="Filter by Type"
                          >
                            <MenuItem value="">All Types</MenuItem>
                            <MenuItem value="music">Music</MenuItem>
                            <MenuItem value="station_id">Station / ID</MenuItem>
                            <MenuItem value="music_bed">Music Bed</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Select Category</InputLabel>
                        <Select
                          value={clockItemFormData.category_id || ''}
                          onChange={(e) => setClockItemFormData({ ...clockItemFormData, category_id: e.target.value || null, asset_id: null })}
                          label="Select Category"
                        >
                          <MenuItem value="">None</MenuItem>
                          {filteredClockItemCategories.map((category) => (
                          <MenuItem key={category.id} value={category.id}>
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  </>
                )}

                {clockItemFormData.type === 'category' && clockItemFormData.selection_mode === 'specific' && clockItemFormData.category_id && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Select Asset</InputLabel>
                      <Select
                        value={clockItemFormData.asset_id || ''}
                        onChange={(e) => setClockItemFormData({ ...clockItemFormData, asset_id: e.target.value || null })}
                        label="Select Asset"
                      >
                        <MenuItem value="">None</MenuItem>
                        {assets
                          .filter(asset => {
                            // Filter assets by selected category
                            return asset.categories && asset.categories.some(cat => cat.id === clockItemFormData.category_id);
                          })
                          .map((asset) => (
                            <MenuItem key={asset.id} value={asset.id}>
                              {asset.title} - {asset.artist || 'Unknown'}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {clockItemFormData.type === 'stream_relay' && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Rebroadcast URL *"
                        placeholder="https://stream.example.com/live"
                        value={clockItemFormData.stream_relay_url || ''}
                        onChange={(e) => setClockItemFormData({ ...clockItemFormData, stream_relay_url: e.target.value })}
                        helperText="URL of the stream to rebroadcast (e.g. Icecast, HLS, or other stream URL)"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Display name (Now Playing)"
                        placeholder="e.g. Live from Stadium"
                        value={clockItemFormData.stream_relay_display_name || ''}
                        onChange={(e) => setClockItemFormData({ ...clockItemFormData, stream_relay_display_name: e.target.value })}
                        helperText="Shown as Now Playing when the stream has no ICY metadata. Leave blank for 'Stream Relay'."
                      />
                    </Grid>
                  </>
                )}

                {clockItemFormData.type === 'webrtc_receiver' && (
                  <>
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                          Remote Contribution
                        </Typography>
                        <Typography variant="body2">
                          This item opens a fixed-duration remote contribution slot. The remote sender should authenticate and connect before airtime.
                        </Typography>
                      </Alert>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Contributor</InputLabel>
                        <Select
                          label="Contributor"
                          value={clockItemFormData.webrtc_contributor_username || ''}
                          onChange={(e) => {
                            const username = e.target.value;
                            const account = contributionAccounts.find((item) => item.username === username);
                            const previousUsername = clockItemFormData.webrtc_contributor_username || '';
                            const previous = contributionAccounts.find((item) => item.username === previousUsername);
                            const displayName = clockItemFormData.webrtc_display_name || '';
                            const autoFilled = !displayName
                              || (previousUsername && [previous?.display_name, previousUsername].includes(displayName));
                            setClockItemFormData({
                              ...clockItemFormData,
                              webrtc_contributor_username: username,
                              webrtc_display_name: autoFilled ? (account?.display_name || username) : displayName,
                            });
                          }}
                        >
                          <MenuItem value="">
                            <em>Choose a contributor</em>
                          </MenuItem>
                          {clockItemFormData.webrtc_contributor_username
                            && !contributionAccounts.some((item) => item.username === clockItemFormData.webrtc_contributor_username) && (
                            <MenuItem value={clockItemFormData.webrtc_contributor_username}>
                              {clockItemFormData.webrtc_contributor_username}
                            </MenuItem>
                          )}
                          {contributionAccounts.map((account) => (
                            <MenuItem key={account.id} value={account.username}>
                              {account.username}{account.display_name ? ` — ${account.display_name}` : ''}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography variant="caption" color="text.secondary">
                        Accounts created under Users → Contributor
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Display Name"
                        value={clockItemFormData.webrtc_display_name || ''}
                        onChange={(e) => setClockItemFormData({ ...clockItemFormData, webrtc_display_name: e.target.value })}
                        helperText="Shown in playout as now-playing label"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Pre-connect Seconds"
                        type="number"
                        inputProps={{ min: 0 }}
                        value={clockItemFormData.webrtc_preconnect_seconds != null ? clockItemFormData.webrtc_preconnect_seconds : ''}
                        onChange={(e) => setClockItemFormData({ ...clockItemFormData, webrtc_preconnect_seconds: e.target.value !== '' ? parseInt(e.target.value, 10) : 600 })}
                        helperText="Recommended early connect window"
                      />
                    </Grid>
                  </>
                )}

                {clockItemFormData.type === 'commercial_break' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Commercial Break</InputLabel>
                      <Select
                        value={clockItemFormData.commercial_break_id || ''}
                        onChange={(e) => setClockItemFormData({ ...clockItemFormData, commercial_break_id: e.target.value || null })}
                        label="Commercial Break"
                      >
                        <MenuItem value="">Select Break</MenuItem>
                        {commercialBreaks.length === 0 ? (
                          <MenuItem disabled>
                            No breaks configured for this station
                          </MenuItem>
                        ) : (
                          // Deduplicate by break_id - each break shown once; timing is resolved when that hour plays
                          [...new Map(commercialBreaks.map((cb) => [cb.break_id ?? cb.id, cb])).values()]
                            .sort((a, b) => (a.break_id || a.id) - (b.break_id || b.id))
                            .map((cb) => (
                              <MenuItem key={cb.id} value={cb.id}>
                                {cb.name || `Break ${cb.break_id ?? cb.id}`}
                              </MenuItem>
                            ))
                        )}
                      </Select>
                    </FormControl>
                    {commercialBreaks.length === 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Configure commercial breaks in the Advertising section first.
                      </Typography>
                    )}
                  </Grid>
                )}

                {/* Mixblock Configuration */}
                {clockItemFormData.type === 'mixblock' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Mixblock</InputLabel>
                      <Select
                        value={clockItemFormData.mixblock_id || ''}
                        onChange={(e) => setClockItemFormData({ ...clockItemFormData, mixblock_id: e.target.value || null })}
                        label="Mixblock"
                      >
                        <MenuItem value="">Select Mixblock</MenuItem>
                        {mixblockDefinitions.length === 0 ? (
                          <MenuItem disabled>No mixblocks configured. Create one in the Mixblocks tab.</MenuItem>
                        ) : (
                          mixblockDefinitions.map((mb) => (
                            <MenuItem key={mb.id} value={mb.id}>
                              {mb.name} {mb.description ? `— ${mb.description}` : ''}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      The mixblock will be generated automatically and inserted into the playout log.
                    </Typography>
                    <FormControlLabel
                      sx={{ mt: 1 }}
                      control={
                        <Switch
                          checked={clockItemFormData.mixblock_harmonic !== false}
                          onChange={(e) => setClockItemFormData({ ...clockItemFormData, mixblock_harmonic: e.target.checked })}
                        />
                      }
                      label="BPM / key matching"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6 }}>
                      When enabled, mixblock tracks avoid in-window tempo changes and prefer matching mix-in/out lengths, downbeats, and keys. BPM is only a preference. Turn off to select purely by rotation rules.
                    </Typography>
                  </Grid>
                )}

                {/* AI Voicetrack Configuration */}
                {clockItemFormData.type === 'ai_voicetrack' && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>AI Prompt *</InputLabel>
                        <Select
                          value={clockItemFormData.ai_prompt_id || ''}
                          onChange={(e) => setClockItemFormData({ ...clockItemFormData, ai_prompt_id: e.target.value || null })}
                          label="AI Prompt *"
                        >
                          <MenuItem value="">Select Prompt</MenuItem>
                          {aiPrompts.length === 0 ? (
                            <MenuItem disabled>No prompts available. Create them in AI Content → Prompts.</MenuItem>
                          ) : (
                            aiPrompts.map((prompt) => (
                              <MenuItem key={prompt.id} value={prompt.id}>
                                #{prompt.id} · {prompt.name} {prompt.folder_name && `(${prompt.folder_name})`}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>AI Voice *</InputLabel>
                        <Select
                          value={clockItemFormData.ai_voice_id || ''}
                          onChange={(e) => setClockItemFormData({ ...clockItemFormData, ai_voice_id: e.target.value || null })}
                          label="AI Voice *"
                        >
                          <MenuItem value="">Select Voice</MenuItem>
                          {aiVoices.length === 0 ? (
                            <MenuItem disabled>No voices available. Add them in AI Content → My Voices.</MenuItem>
                          ) : (
                            aiVoices.map((voice) => (
                              <MenuItem key={voice.id} value={voice.id}>
                                {voice.name} ({voice.voice_type === 'speechify' ? 'Speechify' : (voice.voice_type === 'elevenlabs' ? 'ElevenLabs' : 'Cloned')})
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    {clockItemFormData.ai_prompt_id && (
                      <Grid item xs={12}>
                        <Paper sx={{ p: 1.5, bgcolor: 'var(--rs-bg)', border: '1px solid var(--rs-border)' }}>
                          <Typography variant="caption" sx={{ color: '#22d3ee' }}>
                            Prompt #{clockItemFormData.ai_prompt_id}: {aiPrompts.find(p => p.id === parseInt(clockItemFormData.ai_prompt_id))?.name || 'Unknown'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.75rem' }}>
                            {aiPrompts.find(p => p.id === parseInt(clockItemFormData.ai_prompt_id))?.prompt_template || ''}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                  </>
                )}

                {/* Hard Fixed Timer Configuration */}
                {clockItemFormData.type === 'hard_fixed_timer' && (
                  <>
                    <Grid item xs={12}>
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                          Hard Fixed Timer
                        </Typography>
                        <Typography variant="body2">
                          At the specified minute and second within the hour, the playout will immediately cut to the items after this marker.
                          If the current track hasn't finished, it will be faded out and skipped.
                          If items before this marker run short, the timer is ignored (items play early).
                          This marker is always undroppable.
                        </Typography>
                      </Alert>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Minute"
                        type="number"
                        inputProps={{ min: 0, max: 59 }}
                        value={clockItemFormData.fixed_minute != null ? clockItemFormData.fixed_minute : ''}
                        onChange={(e) => setClockItemFormData({ ...clockItemFormData, fixed_minute: e.target.value !== '' ? parseInt(e.target.value) : null })}
                        helperText="0-59 — Minute within the hour"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Second"
                        type="number"
                        inputProps={{ min: 0, max: 59 }}
                        value={clockItemFormData.fixed_second != null ? clockItemFormData.fixed_second : ''}
                        onChange={(e) => setClockItemFormData({ ...clockItemFormData, fixed_second: e.target.value !== '' ? parseInt(e.target.value) : null })}
                        helperText="0-59 — Second within the minute"
                      />
                    </Grid>
                  </>
                )}

                {/* Soft Time Marker Configuration */}
                {clockItemFormData.type === 'soft_time_marker' && (
                  <>
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                          Soft Time Marker
                        </Typography>
                        <Typography variant="body2">
                          At the specified minute and second, if an item is currently playing, it will be allowed to finish naturally.
                          Once it finishes, playout skips forward to the first item after this marker.
                          Optionally set a force boundary (seconds after marker time) to hard-jump at that point if still running over.
                        </Typography>
                      </Alert>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Minute"
                        type="number"
                        inputProps={{ min: 0, max: 59 }}
                        value={clockItemFormData.soft_minute != null ? clockItemFormData.soft_minute : ''}
                        onChange={(e) => setClockItemFormData({ ...clockItemFormData, soft_minute: e.target.value !== '' ? parseInt(e.target.value) : null })}
                        helperText="0-59 — Minute within the hour"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Second"
                        type="number"
                        inputProps={{ min: 0, max: 59 }}
                        value={clockItemFormData.soft_second != null ? clockItemFormData.soft_second : ''}
                        onChange={(e) => setClockItemFormData({ ...clockItemFormData, soft_second: e.target.value !== '' ? parseInt(e.target.value) : null })}
                        helperText="0-59 — Second within the minute"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Force Boundary (seconds, optional)"
                        type="number"
                        inputProps={{ min: 0 }}
                        value={clockItemFormData.soft_boundary_seconds != null ? clockItemFormData.soft_boundary_seconds : ''}
                        onChange={(e) => setClockItemFormData({ ...clockItemFormData, soft_boundary_seconds: e.target.value !== '' ? parseInt(e.target.value, 10) : null })}
                        helperText="Optional. Example: 120 = force jump 2 minutes after marker time if still behind."
                      />
                    </Grid>
                  </>
                )}

                {/* Sweep Over Intro - for voicetrack and ai_voicetrack types */}
                {(clockItemFormData.type === 'voicetrack' || clockItemFormData.type === 'ai_voicetrack') && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={clockItemFormData.sweep_over_intro || false}
                          onChange={(e) => setClockItemFormData({ ...clockItemFormData, sweep_over_intro: e.target.checked })}
                        />
                      }
                      label="Sweep Over Intro"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 4 }}>
                      When enabled, the voicetrack will play over the intro of the next track, creating a seamless sweep transition.
                    </Typography>
                  </Grid>
                )}

                {/* Music Bed - for voicetrack and ai_voicetrack types */}
                {(clockItemFormData.type === 'voicetrack' || clockItemFormData.type === 'ai_voicetrack') && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={clockItemFormData.music_bed_enabled || false}
                            onChange={(e) => setClockItemFormData({ ...clockItemFormData, music_bed_enabled: e.target.checked })}
                          />
                        }
                        label="Music Bed"
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 4 }}>
                        Play a music bed underneath the voicetrack at a lower volume.
                      </Typography>
                    </Grid>

                    {clockItemFormData.music_bed_enabled && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel>Music Bed Source</InputLabel>
                            <Select
                              value={clockItemFormData.music_bed_selection_mode || 'random_from_category'}
                              onChange={(e) => {
                                const nextMode = (e.target.value === 'specific_asset' || e.target.value === 'specific')
                                  ? 'specific'
                                  : 'random_from_category';
                                setClockItemFormData({
                                  ...clockItemFormData,
                                  music_bed_selection_mode: nextMode,
                                  music_bed_category_id: nextMode === 'random_from_category' ? clockItemFormData.music_bed_category_id : null,
                                  music_bed_asset_id: nextMode === 'specific' ? clockItemFormData.music_bed_asset_id : null,
                                });
                              }}
                              label="Music Bed Source"
                            >
                              <MenuItem value="random_from_category">Random from Category</MenuItem>
                              <MenuItem value="specific">Specific Asset</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Music Bed Volume (dB)"
                            value={clockItemFormData.music_bed_volume_db != null ? clockItemFormData.music_bed_volume_db : -12}
                            onChange={(e) => setClockItemFormData({ ...clockItemFormData, music_bed_volume_db: parseFloat(e.target.value) })}
                            inputProps={{ min: -40, max: 0, step: 1 }}
                            helperText="Volume level for the music bed (e.g. -12 dB)"
                          />
                        </Grid>

                        {clockItemFormData.music_bed_selection_mode === 'random_from_category' && (
                          <Grid item xs={12}>
                            <FormControl fullWidth>
                              <InputLabel>Music Bed Category</InputLabel>
                              <Select
                                value={clockItemFormData.music_bed_category_id || ''}
                                onChange={(e) => setClockItemFormData({ ...clockItemFormData, music_bed_category_id: e.target.value || null })}
                                label="Music Bed Category"
                              >
                                <MenuItem value="">Select Category</MenuItem>
                                {filteredClockItemMusicBedCategories.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                                  ))}
                                {filteredClockItemMusicBedSubcategories.map((sub) => (
                                  <MenuItem key={sub.id} value={sub.id}>↳ {sub.name}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}
                        {clockItemFormData.music_bed_selection_mode === 'specific' && (
                          <Grid item xs={12}>
                            <FormControl fullWidth>
                              <InputLabel>Music Bed Asset</InputLabel>
                              <Select
                                value={clockItemFormData.music_bed_asset_id || ''}
                                onChange={(e) => setClockItemFormData({ ...clockItemFormData, music_bed_asset_id: e.target.value || null })}
                                label="Music Bed Asset"
                              >
                                <MenuItem value="">Select Asset</MenuItem>
                                {filteredClockItemMusicBedAssets.map((asset) => (
                                  <MenuItem key={asset.id} value={asset.id}>
                                    {asset.title} - {asset.artist || 'Unknown'}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}
                      </>
                    )}
                  </>
                )}

                {clockItemFormData.type !== 'hard_fixed_timer' && clockItemFormData.type !== 'soft_time_marker' && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={clockItemFormData.type === 'webrtc_receiver' ? 'Fixed Duration (seconds) *' : 'Estimated Duration (seconds)'}
                        type="number"
                        inputProps={{ min: 0 }}
                        value={clockItemFormData.duration || ''}
                        onChange={(e) => setClockItemFormData({ ...clockItemFormData, duration: e.target.value ? parseFloat(e.target.value) : null })}
                        helperText={clockItemFormData.type === 'webrtc_receiver' ? 'Required - this defines the exact contribution slot length' : "Optional - leave blank to use asset's default duration"}
                        required={clockItemFormData.type === 'webrtc_receiver'}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Play As</InputLabel>
                        <Select
                          value={clockItemFormData.play_as}
                          onChange={(e) => setClockItemFormData({ ...clockItemFormData, play_as: e.target.value })}
                          label="Play As"
                        >
                          <MenuItem value="normal">Normal</MenuItem>
                          <MenuItem value="sweeper">Sweeper</MenuItem>
                          <MenuItem value="voicetrack">Voicetrack</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={clockItemFormData.droppable !== false}
                            onChange={(e) => setClockItemFormData({ ...clockItemFormData, droppable: e.target.checked })}
                          />
                        }
                        label="Droppable"
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 4 }}>
                        When autofade is enabled, droppable items can be automatically removed to fit the hour. Commercials, items before and after ad breaks are always protected regardless of this setting.
                      </Typography>
                    </Grid>
                  </>
                )}

                {(clockItemFormData.type === 'category' || clockItemFormData.type === 'mixblock') && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" sx={{ color: '#22d3ee', mb: 1 }}>
                        Scheduling
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Rule Set Override</InputLabel>
                        <Select
                          value={clockItemFormData.rule_set_id || ''}
                          onChange={(e) => setClockItemFormData({ ...clockItemFormData, rule_set_id: e.target.value || null })}
                          label="Rule Set Override"
                        >
                          <MenuItem value="">Use clock rule set</MenuItem>
                          {ruleSets.map((rs) => (
                            <MenuItem key={rs.id} value={rs.id}>
                              {rs.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Leave as clock default unless this slot needs a different rule set.
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!clockItemFormData.use_category_goals}
                            onChange={(e) => setClockItemFormData({ ...clockItemFormData, use_category_goals: e.target.checked })}
                          />
                        }
                        label="Count toward category goals"
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 4 }}>
                        When on, this slot stops filling once the clock's max/target for its category is reached. Min goals also protect these items from autofade drops.
                      </Typography>
                    </Grid>
                  </>
                )}

              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseClockItemDialog}>Cancel</Button>
              <Button onClick={handleSaveClockItem} variant="contained" sx={{ bgcolor: '#22d3ee' }}>
                {editingClockItem ? 'Update' : 'Add'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Assign Clock Dialog */}
          <Dialog open={assignClockDialogOpen} onClose={() => { setAssignClockDialogOpen(false); setAssignFollowMaster(true); setAssignMode('clock'); }} maxWidth="sm" fullWidth>
            <DialogTitle>
              <Typography variant="h6">
                {selectedRegion
                  ? `Assign Clock — ${regions.find(r => r.id === parseInt(selectedRegion))?.name || 'Region'}`
                  : 'Assign Clock to Selected Slots'
                }
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedSlots.length} time slot(s) selected
              </Typography>
              {selectedSyndicationLink && !selectedRegion && (
                <Alert severity={selectedSyndicationLink.override_mode === 'none' ? 'warning' : 'info'} sx={{ mb: 2 }}>
                  {selectedSyndicationLink.override_mode === 'none'
                    ? 'The parent has disabled local clock overrides.'
                    : 'Choose a local clock to opt out for these hours, or choose None to return to the parent log.'}
                </Alert>
              )}

              {/* Regional mode: Follow Master toggle */}
              {selectedRegion && (
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={assignFollowMaster}
                        onChange={(e) => {
                          setAssignFollowMaster(e.target.checked);
                          if (e.target.checked) setSelectedClockForAssign('');
                        }}
                      />
                    }
                    label="Follow Master Schedule"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6 }}>
                    When enabled, this region uses the same clock as the master schedule for these slots.
                  </Typography>
                </Box>
              )}

              {!selectedRegion && (
                <FormControl component="fieldset" sx={{ mb: 2 }}>
                  <FormLabel>Assignment</FormLabel>
                  <RadioGroup
                    row
                    value={assignMode}
                    onChange={(e) => {
                      setAssignMode(e.target.value);
                      if (e.target.value === 'repeat') setSelectedClockForAssign('');
                    }}
                  >
                    <FormControlLabel value="clock" control={<Radio />} label="Clock template" />
                    <FormControlLabel value="repeat" control={<Radio />} label="Repeat another hour" />
                  </RadioGroup>
                </FormControl>
              )}

              {assignMode === 'repeat' && !selectedRegion && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Alert severity="info">
                    Copies the full source hour — music, voicetracks, mixblocks and markers.
                    Ads are scheduled for each destination hour as usual.
                  </Alert>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <FormControl sx={{ minWidth: 160, flex: 1 }}>
                      <InputLabel>Source day</InputLabel>
                      <Select
                        value={repeatFromDay}
                        label="Source day"
                        onChange={(e) => setRepeatFromDay(e.target.value)}
                      >
                        {WEEKDAY_OPTIONS.map((day) => (
                          <MenuItem key={day.value} value={day.value}>{day.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 140, flex: 1 }}>
                      <InputLabel>Source hour</InputLabel>
                      <Select
                        value={repeatFromHour}
                        label="Source hour"
                        onChange={(e) => setRepeatFromHour(e.target.value)}
                      >
                        {Array.from({ length: 24 }, (_, hour) => (
                          <MenuItem key={hour} value={hour}>
                            {hour.toString().padStart(2, '0')}:00
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              )}

              {/* Clock selector — hidden when "Follow Master" is on in regional mode or when repeating */}
              {(!selectedRegion || !assignFollowMaster) && assignMode !== 'repeat' && (
                <FormControl fullWidth>
                  <InputLabel>Select Clock</InputLabel>
                  <Select
                    value={selectedClockForAssign}
                    onChange={(e) => setSelectedClockForAssign(e.target.value)}
                    label="Select Clock"
                  >
                    <MenuItem value="none">None (Clear)</MenuItem>
                    {assignableClocks.map((clock) => (
                      <MenuItem key={clock.id} value={clock.id}>
                        {clock.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setAssignClockDialogOpen(false); setAssignFollowMaster(true); setAssignMode('clock'); }}>Cancel</Button>
              <Button
                onClick={handleAssignClock}
                variant="contained"
                disabled={Boolean(selectedSyndicationLink && !selectedRegion
                  && (syndicationRolloutStage < 3 || selectedSyndicationLink.override_mode === 'none'))}
                sx={{ bgcolor: '#22d3ee' }}
              >
                {selectedRegion && assignFollowMaster ? 'Reset to Master' : (assignMode === 'repeat' && !selectedRegion ? 'Repeat Hour' : 'Assign')}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {/* Categories Tab */}
      {tabValue === categoriesTab && (
        <>
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              {stations.length > 1 && (
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Station</InputLabel>
                  <Select
                    value={selectedCategoryStation}
                    onChange={(e) => setSelectedCategoryStation(e.target.value)}
                    label="Station"
                  >
                    <MenuItem value="">All Stations</MenuItem>
                    {stations.map((station) => (
                      <MenuItem key={station.id} value={station.id}>
                        {station.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <Typography variant="h6" sx={{ color: '#22d3ee' }}>
                Category Management
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControl sx={{ minWidth: 130 }}>
                <InputLabel>View</InputLabel>
                <Select
                  value={categoryViewMode}
                  onChange={(e) => setCategoryViewMode(e.target.value)}
                  label="View"
                >
                  <MenuItem value="grid">Grid</MenuItem>
                  <MenuItem value="list">List</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 170 }}>
                <InputLabel>Organize</InputLabel>
                <Select
                  value={categoryOrganizeMode}
                  onChange={(e) => setCategoryOrganizeMode(e.target.value)}
                  label="Organize"
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  <MenuItem value="by_type">By Category Type</MenuItem>
                </Select>
              </FormControl>
              {filteredCategories.length > 8 && (
                <FormControl sx={{ minWidth: 160 }}>
                  <InputLabel>Filter by Type</InputLabel>
                  <Select
                    value={categoryTypeFilter}
                    onChange={(e) => setCategoryTypeFilter(e.target.value)}
                    label="Filter by Type"
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="music">Music</MenuItem>
                    <MenuItem value="station_id">Station / ID</MenuItem>
                    <MenuItem value="music_bed">Music Bed</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              )}
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenCategoryDialog()}
                sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
              >
                New Category
              </Button>
            </Box>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Fixed Categories:</strong> "Voicetrack", "Commercial Spots" and "Music Beds" are fixed categories that cannot be deleted. 
              Music Beds supports sub-categories for organising different types of music beds. Assets assigned to Music Beds sub-categories can be automatically mixed under voicetracks and AI content.
            </Typography>
          </Alert>

          {categoriesWithTypeFilter.length === 0 ? (
            <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
              <CardContent>
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  {categoryTypeFilter ? 'No categories match the selected type.' : 'No categories created. Create your first category to organize your assets.'}
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {categoryOrganizeMode === 'by_type'
                ? orderedFilteredCategoryGroups.map((group) => {
                    const meta = getCategoryTypeMeta(group.type);
                    return (
                      <Box key={group.type}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Chip icon={meta.icon} label={meta.label} size="small" sx={{ bgcolor: `${meta.titleColor}20`, color: meta.titleColor, fontSize: '0.7rem' }} />
                          <Typography variant="subtitle2" sx={{ color: meta.titleColor }}>
                            {group.categories.length} {group.categories.length === 1 ? 'category' : 'categories'}
                          </Typography>
                        </Box>
                        {categoryViewMode === 'list' ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {group.categories.map((category) => renderClocksCategoryCard(category, true))}
                          </Box>
                        ) : (
                          <Grid container spacing={2}>
                            {group.categories.map((category) => (
                              <Grid item xs={12} sm={6} md={4} lg={3} key={category.id}>
                                {renderClocksCategoryCard(category, false)}
                              </Grid>
                            ))}
                          </Grid>
                        )}
                      </Box>
                    );
                  })
                : categoryViewMode === 'list' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {categoriesWithTypeFilter.map((category) => renderClocksCategoryCard(category, true))}
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      {categoriesWithTypeFilter.map((category) => {
                        const isMusicBeds = category.category_type === 'music_bed' || category.name === 'Music Beds';
                        return (
                          <Grid item xs={isMusicBeds ? 12 : 12} sm={isMusicBeds ? 12 : 6} md={isMusicBeds ? 6 : 4} lg={isMusicBeds ? 6 : 3} key={category.id}>
                            {renderClocksCategoryCard(category, false)}
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
            </Box>
          )}
        </Box>

          {/* Category Dialog */}
          <Dialog open={categoryDialogOpen} onClose={handleCloseCategoryDialog} maxWidth="sm" fullWidth>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LabelIcon sx={{ color: '#22d3ee' }} />
                <Typography variant="h6">
                  {editingCategory ? 'Edit Category' : 'New Category'}
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              {categoryError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {categoryError}
                </Alert>
              )}

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Category Name *"
                    value={categoryFormData.name}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                    required
                    disabled={editingCategory && (editingCategory.name === 'Voicetrack' || editingCategory.name === 'Commercial Spots' || editingCategory.name === 'Music Beds')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={2}
                    value={categoryFormData.description}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Color"
                    type="color"
                    value={categoryFormData.color}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, color: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                {stations.length > 1 && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Stations</InputLabel>
                      <Select
                        multiple
                        value={categoryFormData.station_ids || []}
                        onChange={(e) => {
                          const value = e.target.value;
                          const selectedIds = (Array.isArray(value) ? value : String(value).split(','))
                            .map((id) => String(id))
                            .filter((id) => id !== '');
                          setCategoryFormData({
                            ...categoryFormData,
                            station_ids: selectedIds,
                            station_id: selectedIds.length === 1 ? Number(selectedIds[0]) : null,
                          });
                        }}
                        label="Stations"
                        renderValue={(selected) => {
                          const ids = Array.isArray(selected) ? selected.map((id) => String(id)) : [];
                          if (ids.length === 0) return 'All Stations';
                          return stations
                            .filter((station) => ids.includes(String(station.id)))
                            .map((station) => station.name)
                            .join(', ');
                        }}
                      >
                        {stations.map((station) => (
                          <MenuItem key={station.id} value={String(station.id)}>
                            {(categoryFormData.station_ids || []).includes(String(station.id)) ? '✓ ' : ''}{station.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <FormControl component="fieldset" sx={{ mt: 1 }}>
                    <FormLabel component="legend" sx={{ color: '#22d3ee', '&.Mui-focused': { color: '#22d3ee' } }}>
                      Category Type
                    </FormLabel>
                    <RadioGroup
                      value={categoryFormData.category_type || 'other'}
                      onChange={(e) => setCategoryFormData({ ...categoryFormData, category_type: e.target.value })}
                    >
                      <FormControlLabel
                        value="music"
                        control={<Radio sx={{ color: '#22c55e', '&.Mui-checked': { color: '#22c55e' } }} />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <MusicNoteIcon sx={{ fontSize: '1rem', color: '#22c55e' }} />
                            Music
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="station_id"
                        control={<Radio sx={{ color: '#f59e0b', '&.Mui-checked': { color: '#f59e0b' } }} />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <RadioIcon sx={{ fontSize: '1rem', color: '#f59e0b' }} />
                            Station / ID
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="music_bed"
                        control={<Radio sx={{ color: '#ec4899', '&.Mui-checked': { color: '#ec4899' } }} />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <HeadphonesIcon sx={{ fontSize: '1rem', color: '#ec4899' }} />
                            Music Bed
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="other"
                        control={<Radio sx={{ color: '#9e9e9e', '&.Mui-checked': { color: '#9e9e9e' } }} />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <MoreHorizIcon sx={{ fontSize: '1rem', color: '#9e9e9e' }} />
                            Other
                          </Box>
                        }
                      />
                    </RadioGroup>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      Music categories support autofade early-fade. Station/ID categories are protected from back-to-back scheduling before ad breaks. Music Bed categories hold audio for mixing under voicetracks and AI content.
                    </Typography>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    disabled={!hasSystemFeature('ai_insights') && !categoryFormData.ai_insights_enabled}
                    control={
                      <Switch
                        checked={!!categoryFormData.ai_insights_enabled}
                        onChange={(e) => {
                          if (e.target.checked && !hasSystemFeature('ai_insights')) return;
                          setCategoryFormData({ ...categoryFormData, ai_insights_enabled: e.target.checked });
                        }}
                        disabled={!hasSystemFeature('ai_insights') && !categoryFormData.ai_insights_enabled}
                      />
                    }
                    label="Enable AI Insights"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={categoryFormData.metadata_enabled}
                        onChange={(e) => setCategoryFormData({ ...categoryFormData, metadata_enabled: e.target.checked })}
                      />
                    }
                    label="Enable Metadata Passing"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={categoryFormData.artwork_enabled}
                        onChange={(e) => setCategoryFormData({ ...categoryFormData, artwork_enabled: e.target.checked })}
                      />
                    }
                    label="Enable Artwork Display"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 4 }}>
                    Show artwork images in the dashboard when assets in this category are playing
                  </Typography>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseCategoryDialog}>Cancel</Button>
              <Button onClick={handleSaveCategory} variant="contained" sx={{ bgcolor: '#22d3ee' }}>
                {editingCategory ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Sub-Category Dialog (for Music Beds) */}
          <Dialog open={subCategoryDialogOpen} onClose={handleCloseSubCategoryDialog} maxWidth="xs" fullWidth>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HeadphonesIcon sx={{ color: '#ec4899' }} />
                <Typography variant="h6">
                  {editingSubCategory ? 'Edit Sub-Category' : 'New Music Bed Sub-Category'}
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Sub-Category Name *"
                    value={subCategoryFormData.name}
                    onChange={(e) => setSubCategoryFormData({ ...subCategoryFormData, name: e.target.value })}
                    required
                    placeholder="e.g. Upbeat, Mellow, News Beds"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={2}
                    value={subCategoryFormData.description}
                    onChange={(e) => setSubCategoryFormData({ ...subCategoryFormData, description: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Color"
                    type="color"
                    value={subCategoryFormData.color}
                    onChange={(e) => setSubCategoryFormData({ ...subCategoryFormData, color: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseSubCategoryDialog}>Cancel</Button>
              <Button onClick={handleSaveSubCategory} variant="contained" sx={{ bgcolor: '#ec4899' }}>
                {editingSubCategory ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {/* Scheduling Settings Tab */}
      {tabValue === schedulingTab && (
        <Box>
          <AutoGenerationSchedules
            jobType="playlist"
            stations={stations}
            canEdit={canEditLogs}
            onSchedulesChange={handleAutoPlaylistSchedulesChange}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Alert severity="info" sx={{ flex: 1, mr: 2 }}>
              <Typography variant="body2">
                <strong>Scheduling Rules:</strong> Rules control how content is selected and scheduled. Rules apply globally but can be overridden when a specific item is selected in a clock (specific items bypass rules).
              </Typography>
            </Alert>
          </Box>

          {/* Power Intros Feature - Per Station */}
          <Card sx={{ bgcolor: 'var(--rs-paper)', border: '2px solid #22d3ee', mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Station</InputLabel>
                  <Select
                    value={selectedStation || (stations.length === 1 ? stations[0].id : '')}
                    onChange={e => setSelectedStation(e.target.value)}
                    label="Station"
                  >
                    {stations.map(station => (
                      <MenuItem key={station.id} value={station.id}>{station.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={stationPowerIntroSettings?.[selectedStation]?.power_intro_enabled || false}
                      onChange={e => setStationPowerIntroSettings({
                        ...stationPowerIntroSettings,
                        [selectedStation]: {
                          ...stationPowerIntroSettings[selectedStation],
                          power_intro_enabled: e.target.checked
                        }
                      })}
                    />
                  }
                  label={
                    <Tooltip title="Enable per-track power intro uploads and scheduling for this station.">
                      <span>Enable Power Intros</span>
                    </Tooltip>
                  }
                />
              </Box>
              {stationPowerIntroSettings?.[selectedStation]?.power_intro_enabled && (
                <Box sx={{ mb: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Suppress Categories Before Power Intro</InputLabel>
                    <Select
                      multiple
                      value={stationPowerIntroSettings?.[selectedStation]?.power_intro_suppression_categories || []}
                      onChange={e => setStationPowerIntroSettings({
                        ...stationPowerIntroSettings,
                        [selectedStation]: {
                          ...stationPowerIntroSettings[selectedStation],
                          power_intro_suppression_categories: e.target.value
                        }
                      })}
                      label="Suppress Categories Before Power Intro"
                      renderValue={selected => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map(id => {
                            const cat = categories.find(c => String(c.id) === String(id));
                            return cat ? (
                              <Chip key={id} label={cat.name} size="small" />
                            ) : null;
                          })}
                        </Box>
                      )}
                    >
                      {categories.map(cat => (
                        <MenuItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    If a power intro is scheduled and the previous item is in one of these categories, the previous item will be removed from the log.
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSavePowerIntroSettings}
                  sx={{ bgcolor: '#22d3ee' }}
                >
                  Save Power Intro Settings
                </Button>
              </Box>
            </CardContent>
          </Card>

          <ExternalSchedulerSettings
            stationId={selectedStation || (stations.length === 1 ? stations[0]?.id : '')}
            stations={stations}
            categories={categories}
            canManage={canManageExternalScheduler}
            onStationChange={setSelectedStation}
          />

          {/* Default Rule Set Configuration */}
          <Card sx={{ bgcolor: 'var(--rs-paper)', border: '2px solid #22d3ee', mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ color: '#22d3ee' }}>
                  Default Rule Set
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                All clocks will automatically use this rule set by default. Individual clocks can opt out and use a custom rule set.
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Default Rule Set</InputLabel>
                <Select
                  value={schedulingSettings.default_rule_set_id || ''}
                  onChange={(e) => setSchedulingSettings({ ...schedulingSettings, default_rule_set_id: e.target.value || null })}
                  label="Default Rule Set"
                >
                  <MenuItem value="">None (No default)</MenuItem>
                  {ruleSets.map((rs) => (
                    <MenuItem key={rs.id} value={rs.id}>
                      {rs.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveSchedulingSettings}
                  sx={{ bgcolor: '#22d3ee' }}
                >
                  Save Default Rule Set
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Grid container spacing={3}>
            {/* Rule Sets List */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ color: '#22d3ee' }}>
                  Rule Sets
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => handleOpenRuleSetDialog()}
                  sx={{ bgcolor: '#22d3ee' }}
                >
                  New
                </Button>
              </Box>

              {ruleSets.length === 0 ? (
                <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
                  <CardContent>
                    <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
                      No rule sets. Create one to start.
                    </Typography>
                  </CardContent>
                </Card>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {ruleSets.map((ruleSet) => (
                    <Card
                      key={ruleSet.id}
                      sx={{
                        bgcolor: selectedRuleSet?.id === ruleSet.id ? '#22d3ee20' : 'var(--rs-paper)',
                        border: selectedRuleSet?.id === ruleSet.id ? '2px solid #22d3ee' : '1px solid var(--rs-border)',
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: '#22d3ee',
                        },
                      }}
                      onClick={() => {
                        setSelectedRuleSet(ruleSet);
                        fetchSchedulingRules(ruleSet.id);
                      }}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ color: '#22d3ee', fontWeight: 'bold', mb: 0.5 }}>
                              {ruleSet.name}
                            </Typography>
                            {ruleSet.description && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                {ruleSet.description}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', color: selectedRuleSet?.id === ruleSet.id ? '#22d3ee' : '#666' }}>
                              {selectedRuleSet?.id === ruleSet.id ? '✓ Selected - Rules shown on right' : 'Click to manage rules →'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenRuleSetDialog(ruleSet);
                              }}
                              sx={{ color: '#22d3ee' }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm('Delete this rule set?')) {
                                  axios.delete(`${API_URL}/api/rule-sets/${ruleSet.id}`, { withCredentials: true })
                                    .then(() => {
                                      fetchRuleSets();
                                      if (selectedRuleSet?.id === ruleSet.id) {
                                        setSelectedRuleSet(null);
                                        setSchedulingRules([]);
                                      }
                                    })
                                    .catch(err => alert('Failed to delete: ' + err.message));
                                }
                              }}
                              sx={{ color: '#ff6b6b' }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Grid>

            {/* Rules List */}
            <Grid item xs={12} md={8}>
              {!selectedRuleSet ? (
                <Card sx={{ bgcolor: 'var(--rs-paper)', border: '2px dashed #22d3ee', minHeight: '400px' }}>
                  <CardContent>
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" sx={{ color: '#22d3ee', mb: 2 }}>
                        Select a Rule Set
                      </Typography>
                      <Typography color="text.secondary" sx={{ mb: 3 }}>
                        Click on a rule set from the left panel to view and manage its rules
                      </Typography>
                      <Alert severity="info" sx={{ maxWidth: 500, mx: 'auto' }}>
                        <Typography variant="body2">
                          <strong>Tip:</strong> Click on any rule set card (left side) to select it and see its rules here.
                        </Typography>
                      </Alert>
                    </Box>
                  </CardContent>
                </Card>
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{ color: '#22d3ee' }}>
                      Rules for: {selectedRuleSet.name}
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Add />}
                      onClick={() => handleOpenRuleDialog(null, selectedRuleSet.id)}
                      sx={{ bgcolor: '#22d3ee' }}
                    >
                      Add Rule
                    </Button>
                  </Box>

                  {schedulingRules.length === 0 ? (
                    <Card sx={{ bgcolor: 'var(--rs-paper)', border: '2px dashed #22d3ee' }}>
                      <CardContent>
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="h6" sx={{ color: '#22d3ee', mb: 2 }}>
                            No Rules Yet
                          </Typography>
                          <Typography color="text.secondary" sx={{ mb: 3 }}>
                            This rule set doesn't have any rules yet.
                          </Typography>
                          <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => handleOpenRuleDialog(null, selectedRuleSet.id)}
                            sx={{ bgcolor: '#22d3ee' }}
                          >
                            Add First Rule
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {schedulingRules.map((rule) => (
                        <Paper
                          key={rule.id}
                          sx={{
                            p: 2,
                            bgcolor: 'var(--rs-bg)',
                            border: '1px solid var(--rs-border)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                {rule.rule_name}
                              </Typography>
                              {!rule.enabled && (
                                <Chip label="Disabled" size="small" sx={{ bgcolor: '#666', fontSize: '0.65rem' }} />
                              )}
                              {rule.breakable && (
                                <Chip label="Breakable" size="small" sx={{ bgcolor: '#22d3ee20', color: '#22d3ee', fontSize: '0.65rem' }} />
                              )}
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              Type: {ruleTypes.find(rt => rt.value === rule.rule_type)?.label || rule.rule_type} | 
                              Scope: {rule.scope} {rule.scope_value ? `(${rule.scope_value})` : ''}
                            </Typography>
                            {rule.settings && (() => {
                              try {
                                const settings = parseJsonObject(rule.settings);
                                const configuredParams = Object.entries(settings)
                                  .filter(([k, v]) => v !== null && v !== '' && v !== 0 && v !== false && k !== 'use_exact_time')
                                  .map(([k, v]) => {
                                    if (k === 'days' || k === 'hours' || k === 'minutes') {
                                      return `${k}: ${v}`;
                                    }
                                    return `${k}: ${v}`;
                                  });
                                if (configuredParams.length > 0) {
                                  return (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', color: '#22d3ee' }}>
                                      Parameters: {configuredParams.join(', ')}
                                    </Typography>
                                  );
                                }
                              } catch (e) {}
                              return null;
                            })()}
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenRuleDialog(rule)}
                              sx={{ color: '#22d3ee' }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteRule(rule.id)}
                              sx={{ color: '#ff6b6b' }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Grid>
          </Grid>

          {/* Rule Dialog */}
          <Dialog 
            open={ruleDialogOpen} 
            onClose={handleCloseRuleDialog} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
              sx: {
                maxHeight: 'calc(var(--rs-vh) * 0.9)',
              }
            }}
          >
            <DialogTitle>
              <Typography variant="h6">
                {editingRule ? 'Edit Rule' : 'New Rule'}
              </Typography>
            </DialogTitle>
            <DialogContent dividers sx={{ maxHeight: 'calc(var(--rs-vh) * 0.7)', overflowY: 'auto' }}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Rule Type *</InputLabel>
                    <Select
                      value={ruleFormData.rule_type}
                      onChange={(e) => setRuleFormData({ ...ruleFormData, rule_type: e.target.value, rule_name: ruleTypes.find(rt => rt.value === e.target.value)?.label || '' })}
                      label="Rule Type *"
                    >
                      <MenuItem value="">Select Rule Type</MenuItem>
                      {ruleTypes.map((rt) => (
                        <MenuItem key={rt.value} value={rt.value}>
                          {rt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Rule Name *"
                    value={ruleFormData.rule_name}
                    onChange={(e) => setRuleFormData({ ...ruleFormData, rule_name: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Scope</InputLabel>
                    <Select
                      value={ruleFormData.scope}
                      onChange={(e) => setRuleFormData({ ...ruleFormData, scope: e.target.value, scope_value: null })}
                      label="Scope"
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="music">Music</MenuItem>
                      <MenuItem value="non_music">Non-Music</MenuItem>
                      <MenuItem value="category">Category</MenuItem>
                      <MenuItem value="artist">Artist</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {(ruleFormData.scope === 'category' || ruleFormData.scope === 'artist') && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>{ruleFormData.scope === 'category' ? 'Category' : 'Artist(s)'}</InputLabel>
                      {ruleFormData.scope === 'category' ? (
                        <Select
                          value={ruleFormData.scope_value || ''}
                          onChange={(e) => setRuleFormData({ ...ruleFormData, scope_value: e.target.value || null })}
                          label="Category"
                        >
                          <MenuItem value="">Select Category</MenuItem>
                          {categories.map((cat) => (
                            <MenuItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <TextField
                          fullWidth
                          label="Artist(s) Name"
                          helperText="Separate multiple artists with: Feat, Featuring, &, X, or comma"
                          value={ruleFormData.scope_value || ''}
                          onChange={(e) => setRuleFormData({ ...ruleFormData, scope_value: e.target.value || null })}
                        />
                      )}
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={ruleFormData.enabled}
                        onChange={(e) => setRuleFormData({ ...ruleFormData, enabled: e.target.checked })}
                      />
                    }
                    label="Enabled"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={ruleFormData.breakable}
                        onChange={(e) => setRuleFormData({ ...ruleFormData, breakable: e.target.checked })}
                      />
                    }
                    label="Breakable"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                    When on, this rule can be skipped if no other track is eligible. When off, the clock slot is left empty rather than breaking the rule.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 2, color: '#22d3ee' }}>
                    Rule Parameters
                  </Typography>
                </Grid>

                {/* Separation Rules (Artist, Related Artist, Title, Audio Item) */}
                {['artist_separation', 'related_artist_separation', 'audio_item_separation', 'title_separation'].includes(ruleFormData.rule_type) && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="body2" sx={{ mb: 1, color: '#22d3ee' }}>
                        Minimum Time Between Plays:
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Days"
                        type="number"
                        inputProps={{ min: 0 }}
                        value={ruleFormData.settings.days || 0}
                        onChange={(e) => setRuleFormData({
                          ...ruleFormData,
                          settings: { ...ruleFormData.settings, days: parseInt(e.target.value) || 0 }
                        })}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Hours"
                        type="number"
                        inputProps={{ min: 0, max: 23 }}
                        value={ruleFormData.settings.hours || 0}
                        onChange={(e) => setRuleFormData({
                          ...ruleFormData,
                          settings: { ...ruleFormData.settings, hours: parseInt(e.target.value) || 0 }
                        })}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Minutes"
                        type="number"
                        inputProps={{ min: 0, max: 59 }}
                        value={ruleFormData.settings.minutes || 0}
                        onChange={(e) => setRuleFormData({
                          ...ruleFormData,
                          settings: { ...ruleFormData.settings, minutes: parseInt(e.target.value) || 0 }
                        })}
                      />
                    </Grid>
                  </>
                )}

                {/* Hour Rotation */}
                {ruleFormData.rule_type === 'hour_rotation' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Unique Hours Required"
                      type="number"
                      inputProps={{ min: 1 }}
                      value={ruleFormData.settings.unique_hours || 0}
                      onChange={(e) => setRuleFormData({
                        ...ruleFormData,
                        settings: { ...ruleFormData.settings, unique_hours: parseInt(e.target.value) || 0 }
                      })}
                      helperText="Number of unique hours an item must be scheduled in before it can play in the same hour again"
                    />
                  </Grid>
                )}

                {/* Daypart Rotation */}
                {ruleFormData.rule_type === 'daypart_rotation' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Unique Dayparts Required"
                      type="number"
                      inputProps={{ min: 1 }}
                      value={ruleFormData.settings.unique_dayparts || 0}
                      onChange={(e) => setRuleFormData({
                        ...ruleFormData,
                        settings: { ...ruleFormData.settings, unique_dayparts: parseInt(e.target.value) || 0 }
                      })}
                      helperText="Number of unique dayparts an item must be scheduled in before it can play in the same daypart again"
                    />
                  </Grid>
                )}

                {/* Yesterday Same Hour */}
                {ruleFormData.rule_type === 'yesterday_same_hour' && (
                  <>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={ruleFormData.settings.use_exact_time || false}
                            onChange={(e) => setRuleFormData({
                              ...ruleFormData,
                              settings: { ...ruleFormData.settings, use_exact_time: e.target.checked }
                            })}
                          />
                        }
                        label="Use Exact Time (vs Same Log Hour)"
                      />
                    </Grid>
                    {ruleFormData.settings.use_exact_time && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="body2" sx={{ mb: 1, color: '#22d3ee' }}>
                            Time Threshold (±):
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label="Hours"
                            type="number"
                            inputProps={{ min: 0 }}
                            value={ruleFormData.settings.threshold_hours || 0}
                            onChange={(e) => setRuleFormData({
                              ...ruleFormData,
                              settings: { ...ruleFormData.settings, threshold_hours: parseInt(e.target.value) || 0 }
                            })}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label="Minutes"
                            type="number"
                            inputProps={{ min: 0, max: 59 }}
                            value={ruleFormData.settings.threshold_minutes || 0}
                            onChange={(e) => setRuleFormData({
                              ...ruleFormData,
                              settings: { ...ruleFormData.settings, threshold_minutes: parseInt(e.target.value) || 0 }
                            })}
                          />
                        </Grid>
                      </>
                    )}
                  </>
                )}

                {/* BPM Time Restriction */}
                {ruleFormData.rule_type === 'bpm_time_restriction' && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Min BPM"
                        type="number"
                        inputProps={{ min: 0, step: 0.1 }}
                        value={ruleFormData.settings.min_bpm}
                        onChange={(e) => setRuleFormData({
                          ...ruleFormData,
                          settings: { ...ruleFormData.settings, min_bpm: e.target.value }
                        })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Max BPM"
                        type="number"
                        inputProps={{ min: 0, step: 0.1 }}
                        value={ruleFormData.settings.max_bpm}
                        onChange={(e) => setRuleFormData({
                          ...ruleFormData,
                          settings: { ...ruleFormData.settings, max_bpm: e.target.value }
                        })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Start Time"
                        type="time"
                        value={ruleFormData.settings.start_time || ''}
                        onChange={(e) => setRuleFormData({
                          ...ruleFormData,
                          settings: { ...ruleFormData.settings, start_time: e.target.value }
                        })}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="End Time"
                        type="time"
                        value={ruleFormData.settings.end_time || ''}
                        onChange={(e) => setRuleFormData({
                          ...ruleFormData,
                          settings: { ...ruleFormData.settings, end_time: e.target.value }
                        })}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Days (leave empty = all days)</InputLabel>
                        <Select
                          multiple
                          value={ruleFormData.settings.days_of_week || []}
                          label="Days (leave empty = all days)"
                          onChange={(e) => setRuleFormData({
                            ...ruleFormData,
                            settings: { ...ruleFormData.settings, days_of_week: e.target.value }
                          })}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((dayValue) => {
                                const dayOption = WEEKDAY_OPTIONS.find((d) => d.value === Number(dayValue));
                                return (
                                  <Chip key={dayValue} size="small" label={dayOption?.label || dayValue} />
                                );
                              })}
                            </Box>
                          )}
                        >
                          {WEEKDAY_OPTIONS.map((dayOption) => (
                            <MenuItem key={dayOption.value} value={dayOption.value}>
                              {dayOption.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={ruleFormData.settings.require_detected_bpm !== false}
                            onChange={(e) => setRuleFormData({
                              ...ruleFormData,
                              settings: { ...ruleFormData.settings, require_detected_bpm: e.target.checked }
                            })}
                          />
                        }
                        label="Require detected BPM (exclude tracks without BPM)"
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Note:</strong> Rules are automatically overridden when a specific item is selected in a clock (not random selection).
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseRuleDialog}>Cancel</Button>
              <Button onClick={handleSaveRule} variant="contained" sx={{ bgcolor: '#22d3ee' }}>
                {editingRule ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Rule Set Dialog */}
          <Dialog open={ruleSetDialogOpen} onClose={handleCloseRuleSetDialog} maxWidth="sm" fullWidth>
            <DialogTitle>
              <Typography variant="h6">
                {editingRuleSet ? 'Edit Rule Set' : 'New Rule Set'}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Rule Set Name *"
                    value={ruleSetFormData.name}
                    onChange={(e) => setRuleSetFormData({ ...ruleSetFormData, name: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={3}
                    value={ruleSetFormData.description}
                    onChange={(e) => setRuleSetFormData({ ...ruleSetFormData, description: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Station</InputLabel>
                    <Select
                      value={ruleSetFormData.station_id || ''}
                      onChange={(e) => setRuleSetFormData({ ...ruleSetFormData, station_id: e.target.value || null })}
                      label="Station"
                    >
                      <MenuItem value="">All Stations</MenuItem>
                      {stations.map((station) => (
                        <MenuItem key={station.id} value={station.id}>
                          {station.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseRuleSetDialog}>Cancel</Button>
              <Button onClick={handleSaveRuleSet} variant="contained" sx={{ bgcolor: '#22d3ee' }}>
                {editingRuleSet ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Scheduling Settings Dialog */}
          <Dialog open={schedulingSettingsOpen} onClose={() => setSchedulingSettingsOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon sx={{ color: '#22d3ee' }} />
                <Typography variant="h6">Scheduling Settings</Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      Configure default settings for scheduling. New clocks will automatically use the default rule set.
                    </Typography>
                  </Alert>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Default Rule Set</InputLabel>
                    <Select
                      value={schedulingSettings.default_rule_set_id || ''}
                      onChange={(e) => setSchedulingSettings({ ...schedulingSettings, default_rule_set_id: e.target.value || null })}
                      label="Default Rule Set"
                    >
                      <MenuItem value="">None (No default)</MenuItem>
                      {ruleSets.map((rs) => (
                        <MenuItem key={rs.id} value={rs.id}>
                          {rs.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    All clocks will automatically use this rule set by default. Individual clocks can opt out and use a custom rule set.
                  </Typography>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSchedulingSettingsOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveSchedulingSettings} variant="contained" sx={{ bgcolor: '#22d3ee' }}>
                Save Settings
              </Button>
            </DialogActions>
          </Dialog>

        </Box>
      )}

      {/* Generate Playlist Dialog */}
      <Dialog open={playlistDialogOpen} onClose={() => setPlaylistDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6">
            Generate Playlist from Clocks
          </Typography>
        </DialogTitle>
        <DialogContent>
          {autoScheduleEnabled && (
            <Alert severity="warning" icon={<WarningAmber />} sx={{ mt: 1, mb: 1 }}>
              Automatic station log generation is enabled
              {nextAutoScheduleRun ? ` (next run ${new Date(nextAutoScheduleRun).toLocaleString()})` : ''}.
              Playlists you generate now may be overwritten by that scheduled run.
            </Alert>
          )}
          {(() => {
            const sid = playlistFormData.station_id || (stations.length === 1 ? stations[0]?.id : null);
            const station = stations.find((s) => String(s.id) === String(sid));
            const mode = parseJsonObject(station?.settings).scheduler_mode;
            if (mode !== 'external') return null;
            return (
              <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                {station?.name || 'This station'} uses an external scheduler. Import hour logs from Scheduling Settings instead of generating from clocks.
              </Alert>
            );
          })()}
          <Typography variant="body2" color="text.secondary" sx={{ mt: autoScheduleEnabled ? 0 : 1 }}>
            This still generates immediately. To run at a set time (for example 23:00 for tomorrow, or Friday 22:00 for next week) without logging in, use Automatic station logs on the Scheduling Settings tab.
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Only show station selector when multiple stations (not in single-station mode) */}
            {stations.length > 1 && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Station *</InputLabel>
                  <Select
                    value={playlistFormData.station_id || ''}
                    onChange={(e) => setPlaylistFormData({ ...playlistFormData, station_id: e.target.value })}
                    label="Station *"
                    required
                  >
                    <MenuItem value="">Select Station</MenuItem>
                    {stations.map((station) => (
                      <MenuItem key={station.id} value={station.id}>
                        {station.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={playlistFormData.generate_range}
                    onChange={(e) => setPlaylistFormData({ ...playlistFormData, generate_range: e.target.checked })}
                  />
                }
                label="Generate for Date Range"
              />
            </Grid>
            {!playlistFormData.generate_range ? (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Date *"
                    type="date"
                    value={playlistFormData.date}
                    onChange={(e) => setPlaylistFormData({ ...playlistFormData, date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Start Hour"
                    type="number"
                    value={playlistFormData.start_hour || 0}
                    onChange={(e) => setPlaylistFormData({ ...playlistFormData, start_hour: parseInt(e.target.value) || 0 })}
                    InputProps={{ inputProps: { min: 0, max: 23 } }}
                    InputLabelProps={{ shrink: true }}
                    helperText="0-23 (default: 0)"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="End Hour"
                    type="number"
                    value={playlistFormData.end_hour !== undefined ? playlistFormData.end_hour : 23}
                    onChange={(e) => setPlaylistFormData({ ...playlistFormData, end_hour: parseInt(e.target.value) || 23 })}
                    InputProps={{ inputProps: { min: 0, max: 23 } }}
                    InputLabelProps={{ shrink: true }}
                    helperText="0-23 (default: 23)"
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Start Date *"
                    type="date"
                    value={playlistFormData.date}
                    onChange={(e) => setPlaylistFormData({ ...playlistFormData, date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="End Date *"
                    type="date"
                    value={playlistFormData.end_date}
                    onChange={(e) => setPlaylistFormData({ ...playlistFormData, end_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Start Hour"
                    type="number"
                    value={playlistFormData.start_hour || 0}
                    onChange={(e) => setPlaylistFormData({ ...playlistFormData, start_hour: parseInt(e.target.value) || 0 })}
                    InputProps={{ inputProps: { min: 0, max: 23 } }}
                    InputLabelProps={{ shrink: true }}
                    helperText="0-23 (default: 0)"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="End Hour"
                    type="number"
                    value={playlistFormData.end_hour !== undefined ? playlistFormData.end_hour : 23}
                    onChange={(e) => setPlaylistFormData({ ...playlistFormData, end_hour: parseInt(e.target.value) || 23 })}
                    InputProps={{ inputProps: { min: 0, max: 23 } }}
                    InputLabelProps={{ shrink: true }}
                    helperText="0-23 (default: 23)"
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        {playlistGenerating && (
          <Box sx={{ px: 3, pb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" sx={{ color: 'var(--rs-muted)' }}>
                {generationProgress.message || 'Generating...'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#22d3ee', fontWeight: 600 }}>
                {generationProgress.percent}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={generationProgress.percent}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: 'var(--rs-border)',
                '& .MuiLinearProgress-bar': { bgcolor: '#22d3ee', borderRadius: 4 },
              }}
            />
          </Box>
        )}
        <DialogActions>
          <Button onClick={() => setPlaylistDialogOpen(false)} disabled={playlistGenerating}>
            Cancel
          </Button>
          <Button
            onClick={() => handleGeneratePlaylist(false)}
            variant="contained"
            sx={{ bgcolor: '#22d3ee' }}
            disabled={
              playlistGenerating
              || !playlistFormData.station_id
              || !playlistFormData.date
              || parseJsonObject(
                stations.find((station) => String(station.id) === String(playlistFormData.station_id))?.settings
              ).scheduler_mode === 'external'
            }
          >
            {playlistGenerating ? `Generating... ${generationProgress.percent}%` : 'Generate Playlist'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={autoScheduleWarningOpen} onClose={() => setAutoScheduleWarningOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Automatic schedule is enabled</Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" icon={<WarningAmber />} sx={{ mb: 2 }}>
            Station logs are already generated automatically. Generating now will overwrite existing playlists in the date and hour range you choose, and a later scheduled run may overwrite this generation.
          </Alert>
          {nextAutoScheduleRun && (
            <Typography variant="body2" color="text.secondary">
              Next automatic run: {new Date(nextAutoScheduleRun).toLocaleString()}
            </Typography>
          )}
          {enabledAutoPlaylistSchedules.some((schedule) => schedule.summary) && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {enabledAutoPlaylistSchedules.map((schedule) => schedule.summary).filter(Boolean).join(' · ')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAutoScheduleWarningOpen(false)}>Cancel</Button>
          <Button
            onClick={openPlaylistDialog}
            variant="contained"
            color="warning"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={playlistOverwriteWarningOpen} onClose={() => setPlaylistOverwriteWarningOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Recorded Voicetracks Found</Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {playlistOverwriteWarning?.message || 'Recorded voicetracks were found in the selected range.'}
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Proceeding will overwrite existing playlists in the selected date/hour range.
          </Typography>
          {playlistOverwriteWarning?.recorded_voicetracks?.sample?.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              Examples: {playlistOverwriteWarning.recorded_voicetracks.sample.slice(0, 5).map(vt => `${vt.playlist_date} ${String(vt.playlist_hour).padStart(2, '0')}:00`).join(', ')}
              {playlistOverwriteWarning?.recorded_voicetracks?.truncated ? ' …' : ''}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlaylistOverwriteWarningOpen(false)} disabled={playlistGenerating}>Cancel</Button>
          <Button
            onClick={async () => {
              setPlaylistOverwriteWarningOpen(false);
              await handleGeneratePlaylist(true);
            }}
            variant="contained"
            color="warning"
            disabled={playlistGenerating}
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================== MIXBLOCKS TAB ========================== */}
      {beatmixEnabled && tabValue === mixblocksTab && (
        <Box>
          {stations.length > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Station</InputLabel>
                <Select
                  value={selectedStation}
                  onChange={(e) => {
                    setSelectedStation(e.target.value);
                    const station = stations.find(s => String(s.id) === String(e.target.value));
                    setBeatmixEnabled(station?.beatmix_enabled || false);
                  }}
                  label="Station"
                >
                  {stations.filter(s => s.beatmix_enabled).map((station) => (
                    <MenuItem key={station.id} value={station.id}>
                      {station.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          {selectedStation ? (
            <MixblockBuilder stationId={selectedStation} />
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              Please select a station to manage mixblocks.
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
}
